import { createRef, PureComponent } from "react";
import { refreshAccessToken } from "../../../../../auth/token-refresh";
import { AiChatProviderName, AiChatService } from "../../../../../services/ai-chat-service";
import { AiPaymentService } from "../../../../../services/ai-payment-service";
import BranchingService from "../../../../../services/branching-service";
import { ScreenService } from "../../../../../services/screen-service";
import {
  MCP_TOOL_ACTIVITY_EVENT,
  MCP_ZONE_ACTIVATE,
  MCP_ZONE_RESET,
  McpToolActivityEvent,
  getMcpBridgeInstance,
  McpToolBridge,
  ChatEventData,
  type AuxCallContext,
  type ChatAttachClaim,
  type ChatAttachOutcome,
  type InterruptDecision,
  type ValidationFixItemPayload
} from "../../../../../shared/utility/mcp-tool-bridge";

/** Wire shape of `done.quotaWarning` (mirrors mcp-chat-server's QuotaWarningInfo). */
interface QuotaWarningPayload {
  kind?: string;
  window?: string;
  limitCredits?: number;
  consumedCredits?: number;
}

interface KuikaRuntime {
  activeProjectId?: string;
  activeProjectName?: string;
  activeProjectType?: string | number;
  _mcpDeferRender?: boolean;
}

interface UserInfoRuntime {
  id?: string;
  username?: string;
  isDomainOwner?: boolean;
}

interface DashboardStateRuntime {
  dashboardState?: number;
}

interface DesignerScreenSummary {
  id?: string | number;
  name?: string;
}

interface ScreenDesignEntry {
  friendlyName?: string;
  item?: {
    name?: string;
  };
}

interface ScreenProviderRuntime {
  state?: {
    dashboardState?: number;
    screenDesignDictionary?: Record<number, ScreenDesignEntry>;
    screenList?: DesignerScreenSummary[];
  };
  getUndoStackSize?: () => number;
}

import type { ScreenDesignInstance } from "../../../../../shared/utility/designer-context";

type DesignerRuntime = ScreenDesignInstance;

interface KuikaBrowserWindow extends Window {
  kuika?: KuikaRuntime;
  currentDomain?: string;
  userInfo?: UserInfoRuntime;
  getSdInstance?: () => ScreenDesignInstance | undefined;
}

interface NamedChatTab {
  name: string;
}

interface ChatRequestImagePayload {
  base64: string;
  mediaType: string;
}

interface ChatRequestFilePayload {
  base64: string;
  fileName: string;
  mediaType: string;
  fileSize: number;
}

interface ChatRequestHistoryMessage {
  id: string;
  role: McpChatMessage["role"];
  content: string;
  timestamp: number;
  completedToolActivities?: CompletedToolActivity[];
  responseDurationMs?: number;
  mentions?: MentionReference[];
  chatRequestId?: string;
  userRating?: number;
}

interface ChatRequestBody {
  session_id: string;
  tab_id: string;
  /** Chat-tab id (panel `tab.id`) — gates server plan hydration to the owner (KOA-17797). */
  chat_tab_id?: string;
  user_id?: string;
  user_name?: string;
  message: string;
  project_id?: string;
  project_name?: string;
  project_type?: string | number;
  domain_name?: string;
  auth_token?: string;
  screen_context?: unknown;
  images?: ChatRequestImagePayload[];
  files?: ChatRequestFilePayload[];
  ask_mode?: boolean;
  builder_mode?: boolean;
  requested_mode: ChatTab["chatMode"];
  plan_mode?: boolean;
  platform_mode?: boolean;
  /** Business Analyst spec approval (KOA-17628) — the `message` is the BA's
   *  `## Spec:` markdown sent as the user's prompt; flips the server gate to approved. */
  spec_approved?: boolean;
  /** KOA-18304 — the user answered the Designer capability question by choosing to
   *  stay in Designer. Tells the server's triage not to ask again about the
   *  resent request, which would otherwise loop forever. */
  designer_ui_only_confirmed?: boolean;
  model_overrides?: ModelOverrides;
  /** User-facing effort tier (Lite/Economy/Power) — server folds it into model overrides. */
  model_tier?: EffortTier;
  history?: ChatRequestHistoryMessage[];
  plan_json?: string;
  intent?: "fix_validations" | "analyze_project" | "apply_change_delta" | "discard_change_delta";
  change_delta_id?: string;
  validation_fix_items?: ValidationFixItemPayload[];
}

/** Options for `handleSend`. KOA-17628 — Business Analyst spec approval sends the
 *  spec markdown as the driving prompt with no visible user bubble. Also carries
 *  the structured (non-NL) AI validation-fix task fields (KOA-17717). */
interface HandleSendOptions {
  /** Use this text as the message sent to the server instead of the input/param. */
  driveMessageOverride?: string;
  /** Skip pushing a visible user-message bubble (still enters the thinking state). */
  suppressUserBubble?: boolean;
  /** Set `spec_approved` on the request so the server flips the BA gate to approved. */
  specApproved?: boolean;
  /** KOA-18304 — set `designer_ui_only_confirmed` so the server's triage skips the
   *  Designer capability question for this (resent) request. */
  designerUiOnlyConfirmed?: boolean;
  /** Structured (non-NL) task marker — `fix_validations` (KOA-17717), the
   *  project-analysis action, or a decision on a change plan (KOA-18422).
   *  Routed by the server before any classification. */
  intent?: "fix_validations" | "analyze_project" | "apply_change_delta" | "discard_change_delta";
  /** Items for the AI validation-fix task (KOA-17717). */
  validationFixItems?: ValidationFixItemPayload[];
  /** KOA-18422 — the recorded delta a change plan's Approve/Revise decides on. */
  changeDeltaId?: string;
}

interface PendingTabPatch {
  tabId: string;
  patch: Partial<ChatTab> | ((tab: ChatTab) => Partial<ChatTab>);
}

interface StreamToolActivityData {
  toolName?: string;
  status?: unknown;
  result?: unknown;
  parameters?: ToolParameterObject;
  toolCallId?: string;
  duration?: number;
  /**
   * KOA-17717 — backend tool activity emitted to drive the toolbar / datasource
   * panel reveal animations. Tagged so it never becomes a transcript pill; it
   * now feeds the waiting indicator's header line instead of being dropped.
   */
  _notificationOnly?: boolean;
}

interface StreamSpecialistActivityData {
  agent?: string;
  activityKey?: string;
  status?: unknown;
  turn?: number;
  accumulated?: unknown;
}

interface PlanProgressStep {
  index: number;
  description: string;
}

interface ContextDatasource {
  name?: string;
}

interface ValidationToolIssue {
  category: string;
  severity: "error" | "warning" | "info";
  issue: string;
  components?: string[];
  suggestion?: string;
}

interface ValidationToolResult {
  score: number;
  componentCount: number;
  summary: string;
  issues: ValidationToolIssue[];
}

function getKuikaWindow(): KuikaBrowserWindow {
  return window as KuikaBrowserWindow;
}

function getActiveProjectId(): string | undefined {
  return getKuikaWindow().kuika?.activeProjectId || undefined;
}

function getActiveProjectName(): string | undefined {
  return getKuikaWindow().kuika?.activeProjectName || undefined;
}

function getActiveProjectType(): string | number | undefined {
  return getKuikaWindow().kuika?.activeProjectType ?? undefined;
}

function getCurrentDomain(): string | undefined {
  return getKuikaWindow().currentDomain || undefined;
}

function getCurrentUser(): UserInfoRuntime | undefined {
  return getKuikaWindow().userInfo;
}

function getDesignerRuntime(): DesignerRuntime | undefined {
  return getKuikaWindow().getSdInstance?.();
}

/**
 * Builds the user + project context attached to standalone helper calls
 * (title generation, prompt improvement) so the server can attribute the
 * LLM cost to the user's balance. Mirrors the fields chat messages
 * already carry — auth token, active project, current domain.
 */
function buildAuxCallContext(sessionId: string | null | undefined): AuxCallContext {
  return {
    session_id: sessionId || undefined,
    project_id: getActiveProjectId(),
    project_name: getActiveProjectName(),
    domain_name: getCurrentDomain(),
    auth_token: localStorage.getItem("tokenString") || undefined
  };
}
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { McpToolExecutor } from "../../../../../shared/utility/mcp-tools";
import { readRightPanelWidth, RIGHT_MIN_WIDTH } from "../../../../../shared/utility/panel-dimensions";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import McpChatBalancePanel, { type BalanceInfo } from "../balance/mcp-chat-balance-panel";
import { buildMcpChatBalancePanelProps } from "../balance/mcp-chat-balance-panel-props";
import McpChatBuyCreditsModal from "../balance/mcp-chat-buy-credits-modal";
import { gatherScreenContext } from "../context-pills/mcp-chat-context";
import McpChatHistoryModal from "../history/mcp-chat-history-modal";
import {
  buildCheckpointList,
  computeRewindSummary,
  type CheckpointDescriptor,
  type RewindSummary
} from "../history/mcp-chat-rewind";
import McpChatRewindDialog from "../history/mcp-chat-rewind-dialog";
import McpChatInputArea from "../input/mcp-chat-input-area";
import { buildMcpChatInputAreaProps } from "../input/mcp-chat-input-area-props";
import { expandMentionContext } from "../input/mcp-chat-mention-data";
import { SLASH_COMMANDS, COMMAND_CATEGORY_META } from "../input/mcp-chat-slash-commands";
import McpChatSuggestions from "../input/mcp-chat-suggestions";
import McpChatMessageComponent from "../message/mcp-chat-message";
import McpChatThinkingIndicator from "../message/mcp-chat-thinking";
import {
  buildCompletedToolActivitiesFromActivities,
  getToolActivityIdentity,
  mergeCompletedToolActivities,
  mergeToolActivityCollections
} from "../message/mcp-chat-tool-activity-state";
import { createToolActivity, type ToolActivity } from "../message/mcp-tool-activity";
import {
  getInterruptDescription,
  getInterruptDescriptionParts,
  isReadOnlyTool
} from "../message/mcp-tool-descriptions";
import { getToolDescription } from "../message/mcp-tool-descriptions";
import { getToolGroupLabel } from "../message/mcp-tool-group-labels";
import McpChatModelSelector, {
  getStoredModelOverrides,
  type ModelOverrides
} from "../model-selector/mcp-chat-model-selector";
import { captureSnapshot, buildStructuralDiff, type DictSnapshot } from "../persistence/mcp-chat-diff";
import {
  PROJECT_LOADED_EVENT,
  CHAT_SAVE_FAILED_EVENT,
  CHAT_SAVE_RECOVERED_EVENT
} from "../persistence/mcp-chat-events";
import {
  archiveTab,
  checkForNewerState,
  loadArchivedChatTabs,
  loadChatState,
  migrateFromLocalStorage,
  resetSyncCursor,
  restoreTabs,
  saveBeacon,
  saveChatState
} from "../persistence/mcp-chat-persistence";
import { mergeRestoredTabsWithLiveTurns } from "../persistence/merge-live-turns";
import { searchMessages } from "../search/mcp-chat-search";
import McpChatSearchBar from "../search/mcp-chat-search-bar";
import McpChatSearchResults from "../search/mcp-chat-search-results";
import {
  type AssistantMessagePatch,
  type UpsertAssistantMessageResult,
  upsertAssistantMessage
} from "../shared/assistant-message-upsert";
import {
  MAX_TABS,
  MESSAGES_PAGE_SIZE,
  MAX_ATTACHED_IMAGES,
  MAX_ATTACHED_FILES,
  ALLOWED_FILE_EXTENSIONS,
  PANEL_SIZE_STORAGE_KEY,
  PANEL_MIN_WIDTH,
  PANEL_MIN_HEIGHT,
  PANEL_COMPACT_THRESHOLD,
  getPanelMaxWidth,
  getPanelMaxHeight,
  PANEL_HORIZONTAL_MIN_WIDTH,
  PANEL_HORIZONTAL_MIN_HEIGHT,
  getPanelHorizontalMaxWidth,
  getPanelHorizontalMaxHeight,
  PANEL_HORIZONTAL_DEFAULT_HEIGHT,
  PANEL_HORIZONTAL_SIZE_STORAGE_KEY,
  ANCHOR_SNAP_DISTANCE,
  PANEL_ANCHOR_STORAGE_KEY,
  isPlatformMode
} from "../shared/mcp-chat-config";
import { getStoredEffort, setStoredEffort } from "../shared/mcp-chat-effort";
import { phaseLabelFromEvent } from "../shared/mcp-chat-phase-title";
import type {
  AttachedFile,
  AttachedImage,
  CapabilityCategory,
  ChatAnchor,
  ChatOption,
  ChatTab,
  EffortTier,
  CompletedToolActivity,
  ConfirmationInterruptData,
  DdlOp,
  InterruptData,
  McpChatMessage,
  QuestionInterruptData,
  MentionReference,
  ParsedQuestionBlock,
  ParsedQuestionnaire,
  QueuedMessage,
  QuestionnaireProgress,
  SearchState,
  SelectedElementAttachment,
  SelectedElementSegment,
  SlashCommandDefinition,
  ToolParameterObject,
  InspectorDirectSendPayload,
  InspectorPourPayload
} from "../shared/mcp-chat-types";
import {
  buildChangesSummary,
  collapseExactDuplicateAssistantResponse,
  hasSendablePayload,
  isChangeDecisionIntent,
  isOtherSelection,
  DESIGNER_CHOICE_CONTINUE,
  DESIGNER_CHOICE_SWITCH,
  PLAN_APPROVED_DIRECTIVE
} from "../shared/mcp-chat-utils";
import { planBelongsToTab, stampPlanOwner } from "../shared/plan-ownership";
import {
  clearSkippedMark,
  isQuestionAnswered,
  isQuestionDelegated,
  isQuestionResolved
} from "../shared/questionnaire-answered";
import {
  cleanQuestionPrompt,
  formatAnsweredQuestionLines,
  formatQuestionnaireAnswers
} from "../shared/questionnaire-answers";
import {
  createEmptyQuestionnaireProgress,
  mergeStreamingAssistantMessage,
  questionnaireProgressForPendingQuestion,
  shouldForkStreamingMessageForNonInterruptContent,
  shouldForkStreamingMessageForQuestionInterrupt
} from "../shared/streaming-assistant-merge";
import { publishMcpChatBusy } from "./mcp-chat-busy";
import McpChatEmptyState from "./mcp-chat-empty-state";
import McpChatErrorBoundary from "./mcp-chat-error-boundary";
import McpChatHeader from "./mcp-chat-header";
import { setMcpChatPanelInstance } from "./mcp-chat-panel-ref";
import McpChatTabBar from "./mcp-chat-tab-bar";
import "./mcp-chat-panel.scss";

interface McpChatPanelProps {
  isMcpChatPanelVisible: boolean;
  setIsMcpChatPanelVisible: (visible: boolean) => void;
  onDockToPanel?: (anchor: ChatAnchor) => void;
  /** When true, the floating FAB and panel are hidden (e.g. report designer mode). */
  /** When false, the "right-panel" anchor zone is excluded from drag targets. */
  canDockToRightPanel?: boolean;
}

interface PanelSize {
  width: number;
  height: number;
}

interface AnchorZoneRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface SnapTarget {
  left: number;
  top: number;
  width?: number;
  height?: number;
}

interface McpChatPanelState {
  tabs: ChatTab[];
  activeTabId: string;
  isWsConnected: boolean;
  capabilities: CapabilityCategory[];
  showDisconnectedBanner: boolean;
  showSaveFailureBanner: boolean;
  panelSize: PanelSize | null;
  horizontalPanelSize: PanelSize | null;
  isResizing: boolean;
  showScrollToBottom: boolean;
  anchor: ChatAnchor;
  isDragging: boolean;
  dragPosition: { x: number; y: number } | null;
  dragOffset: { x: number; y: number };
  nearestAnchor: ChatAnchor | null;
  isSnapping: boolean;
  isFileDragOver: boolean;
  isInspectorActive: boolean;
  /** One-time "Builder mode is live" announcement above the FAB (dismissed on first open). */
  search: SearchState;
  isCollapsedForInspector: boolean;
  /** Floating panel maximized to fill the viewport (width/height only). */
  isFullScreen: boolean;
  /** Buy-credits modal (embeds the settings credits panel) is open. */
  isBuyCreditsOpen: boolean;
  isModelSelectorOpen: boolean;
  modelOverrides: ModelOverrides;
  serverProvider: AiChatProviderName | null;
  /** Server offers the project-analysis walk. Defaults false so the action stays
   *  hidden until the server has actually said it is available. */
  projectAnalysisAvailable: boolean;
  remainingBalance: number | null | undefined;
  isBalancePanelOpen: boolean;
  balanceInfo: BalanceInfo | null;
  /**
   * Whether backend-driven rewind is available for the current project.
   * True only when staging is enabled AND the project is not fully
   * provisioned with branching (in full-branching projects, saves go to
   * KBranchStagedChange so the draft-discard path doesn't apply).
   * Null while unknown (on first mount, before the status check completes).
   */
  rewindEnabled: boolean | null;
  /**
   * Active rewind confirmation dialog. Present only while the user is
   * deciding whether to confirm the rewind scope the dialog is showing.
   */
  rewindDialog: RewindDialogState | null;
  /** Revert-history browse modal state. */
  historyModal: HistoryModalState | null;
}

interface BalanceCursor {
  domainName: string;
  version: number;
  projectionId?: string;
  balanceRevision?: string | null;
}

function isFresherBalanceSnapshot(prev: BalanceCursor | null, next: BalanceCursor): boolean {
  if (!prev) return true;
  if (next.domainName !== prev.domainName) return true;
  if (next.projectionId && prev.projectionId && next.projectionId !== prev.projectionId) return true;
  if (next.balanceRevision && prev.balanceRevision && next.balanceRevision !== prev.balanceRevision) return true;
  return next.version > prev.version;
}

interface HistoryModalState {
  tabId: string;
  checkpoints: CheckpointDescriptor[];
  isLoading: boolean;
  errorText: string | null;
}

interface RewindDialogState {
  /** Tab the dialog was opened from — guards against cross-tab operation
   *  if the user switches tabs with the dialog still open. */
  tabId: string;
  userMsgId: string;
  summary: RewindSummary;
  errorText: string | null;
  isInFlight: boolean;
  isLoadingSummary: boolean;
}

interface DdlToolInput {
  operation?: string;
  table?: DdlTableRef;
  tableName?: string;
  newName?: string;
  oldName?: string;
  columnName?: string;
}

interface DdlTableRef {
  name?: string;
}

interface DdlToolResult {
  error?: unknown;
}

interface TreeNodeLike {
  children?: TreeNodeLike[];
}

/** How a turn ended. Only a completed turn earns the success flash. */
type ChatTurnOutcome = "completed" | "cancelled" | "disconnected";

/** A line appended after the turn's own message — a cancellation or disconnect notice. */
interface FinalizeTurnNotice {
  content: string;
  isError?: boolean;
}

interface FinalizeTurnOptions {
  /** How the turn ended. */
  outcome: ChatTurnOutcome;
  /** Request whose listener is torn down and whose id is stamped on the message. */
  chatRequestId?: string;
  /** Notice appended after the turn's own message. */
  notice?: FinalizeTurnNotice;
  /** Drop the tab's queued messages instead of sending the next one. Stop means stop everything. */
  dropQueue?: boolean;
  /** Apply by mutating state instead of setState — for teardown paths that run
   *  after `isMounted_` is false, where React will not process an update. */
  sync?: boolean;
  /** Runs in the setState callback, after the fold has been applied. */
  onFinalized?: () => void;
}

let messageIdCounter = 0;
function generateMessageId(): string {
  return `msg_${Date.now()}_${++messageIdCounter}`;
}

/**
 * Hand a markdown file to the browser. One implementation for every download
 * this panel does — conversation export and the project-discovery report —
 * so the blob/anchor/revoke dance exists once.
 */
function downloadMarkdownFile(fileName: string, markdown: string): void {
  const blob = new Blob([markdown], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * A download name that cannot escape the download itself. The server already
 * slugifies the report name, but this value arrives over the wire, so path
 * separators, traversal and control characters are stripped here too rather
 * than trusted — and an empty result still yields a usable name.
 */
function sanitizeDownloadFileName(candidate: string, fallback: string): string {
  const cleaned = candidate
    .replace(/[\u0000-\u001f]/g, "")
    .replace(/[/\\]/g, "-")
    .replace(/\.{2,}/g, ".")
    .replace(/[^a-zA-Z0-9._-]/g, "-")
    .replace(/^[.-]+/, "")
    .slice(0, 120);
  return cleaned.length > 0 ? cleaned : fallback;
}

let tabIdCounter = 0;
function generateTabId(): string {
  return `tab_${Date.now()}_${++tabIdCounter}`;
}

function getNextChatNumber(existingTabs: NamedChatTab[]): number {
  let maxNum = 0;
  for (const tab of existingTabs) {
    const match = tab.name.match(/^Chat\s+(\d+)$/);
    if (match) maxNum = Math.max(maxNum, parseInt(match[1], 10));
  }
  return maxNum + 1;
}

const MCP_INSPECTOR_REQUEST_EVENT = "kuika-mcp-inspector-request";
const MCP_INSPECTOR_SELECTED_EVENT = "kuika-mcp-inspector-selected";
const MCP_INSPECTOR_CANCEL_EVENT = "kuika-mcp-inspector-cancel";
const MCP_INSPECTOR_DIRECT_SEND_EVENT = "kuika-mcp-inspector-direct-send";
const MCP_INSPECTOR_POUR_EVENT = "kuika-mcp-inspector-pour";

// Suppress the disconnected banner during transient WS drops that auto-recover
// (server hot-reload, network blip, tab suspension). RECONNECT_DELAY in the
// bridge is 3000ms + token fetch; 5000ms covers the normal recovery window
// while still surfacing genuinely persistent outages.
const DISCONNECTED_BANNER_DEBOUNCE_MS = 5000;

interface McpChatDisconnectedBannerProps {
  isWsConnected: boolean;
  showDisconnectedBanner: boolean;
  onRetry: () => void;
  onDismiss: () => void;
}

function McpChatDisconnectedBanner({
  isWsConnected,
  showDisconnectedBanner,
  onRetry,
  onDismiss
}: McpChatDisconnectedBannerProps) {
  if (isWsConnected || !showDisconnectedBanner) return null;

  return (
    <div className="mcp-chat__disconnected-banner">
      <span className="mcp-chat__disconnected-text">Not connected to AI server</span>
      <div className="mcp-chat__disconnected-actions">
        <button className="mcp-chat__disconnected-retry" onClick={onRetry}>
          Retry
        </button>
        <button className="mcp-chat__disconnected-dismiss" onClick={onDismiss} aria-label="Dismiss">
          ×
        </button>
      </div>
    </div>
  );
}

interface McpChatSaveFailureBannerProps {
  showSaveFailureBanner: boolean;
  onDismiss: () => void;
}

function McpChatSaveFailureBanner({ showSaveFailureBanner, onDismiss }: McpChatSaveFailureBannerProps) {
  if (!showSaveFailureBanner) return null;

  return (
    <div className="mcp-chat__save-failure-banner">
      <span className="mcp-chat__save-failure-text">Chat history may not be saved</span>
      <button className="mcp-chat__save-failure-dismiss" onClick={onDismiss} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
}

interface McpChatAnchorZonesProps {
  zones: Record<ChatAnchor, AnchorZoneRect> | null;
  nearestAnchor: ChatAnchor | null;
}

function McpChatAnchorZones({ zones, nearestAnchor }: McpChatAnchorZonesProps) {
  if (!zones) return null;

  const previewZone = nearestAnchor ? zones[nearestAnchor] : null;

  return (
    <>
      {(Object.entries(zones) as [ChatAnchor, AnchorZoneRect][]).map(([key, zone]) => (
        <div
          key={key}
          className={[
            "mcp-chat__anchor-zone",
            "mcp-chat__anchor-zone--visible",
            nearestAnchor === key && "mcp-chat__anchor-zone--active"
          ]
            .filter(Boolean)
            .join(" ")}
          style={{
            left: zone.x,
            top: zone.y,
            width: zone.width,
            height: zone.height
          }}
        />
      ))}
      {previewZone && (
        <div
          className="mcp-chat__anchor-preview"
          style={{
            left: previewZone.x,
            top: previewZone.y,
            width: previewZone.width,
            height: previewZone.height
          }}
        >
          <div className="mcp-chat__anchor-preview-header">
            <div className="mcp-chat__anchor-preview-header-icon" />
            <span className="mcp-chat__anchor-preview-header-title">Kuika AI</span>
          </div>
          <div className="mcp-chat__anchor-preview-body" />
          <div className="mcp-chat__anchor-preview-input" />
        </div>
      )}
    </>
  );
}

// Bound the in-memory message history per tab so a multi-day session with heavy
// AI usage (base64 images, DDL ops, tool results, diffs) can't grow the heap
// without limit. Persistence already keeps only the last 50 messages on reload;
// keeping 200 in memory preserves scrollback/rewind while capping worst-case
// retention. Applied at every tab-message write path (updateTab, batched
// patches, and the direct streaming setState).
const MAX_IN_MEMORY_MESSAGES = 200;

function withCappedMessages(tab: ChatTab): ChatTab {
  if (tab.messages.length <= MAX_IN_MEMORY_MESSAGES) return tab;
  return { ...tab, messages: tab.messages.slice(-MAX_IN_MEMORY_MESSAGES) };
}

class McpChatPanel extends PureComponent<McpChatPanelProps, McpChatPanelState> {
  messagesEndRef = createRef<HTMLDivElement>();
  messagesContainerRef = createRef<HTMLDivElement>();
  /** Wraps all scroll children; its height changes are what the stick-to-bottom
   *  ResizeObserver watches (the container's own box never changes). */
  messagesContentRef = createRef<HTMLDivElement>();
  inputAreaRef = createRef<McpChatInputArea>();
  tabBarRef = createRef<McpChatTabBar>();
  panelRef = createRef<HTMLDivElement>();
  /** Maps tabId → active chat requestId for cancellation */
  activeChatRequestIds = new Map<string, string>();
  /** Maps tabId → monotonic start time (performance.now()) for response duration */
  requestStartTimes = new Map<string, number>();
  preRequestUndoStackSize = new Map<string, number>();
  /** Durable per-turn activity archive so terminal tool states survive `done` timing races. */
  private archivedToolActivities = new Map<string, ToolActivity[]>();
  /** The assistant message that owns the most recently finalized tool transcript for each tab. */
  private finalizedAssistantMessageIds = new Map<string, string>();
  /** Tracks the message ID of the in-flight streaming assistant message per tab. */
  streamingMessageIds = new Map<string, string>();
  /** Buffer for streaming content — flushed once per rAF frame instead of per token. */
  private streamingBuffer = new Map<
    string,
    {
      content: string;
      interruptData?: InterruptData;
      isStreaming?: boolean;
      serverMessageId?: string;
      explorationContent?: string;
      /** Agent attribution for the streaming bubble (Designer/Wirer/Binder/etc.). */
      streamingAgent?: string;
    }
  >();
  /** Maps tabId → dictionary snapshot captured when request starts */
  private beforeSnapshots = new Map<string, DictSnapshot>();
  /** Maps tabId → LLM-generated suggested next prompts, pending until finalize */
  private pendingSuggestions = new Map<string, string[]>();
  private streamingRafId: number | null = null;
  /** Tracks tabs where the canvas zone has been activated (activated on first canvas tool, deactivated on finalize) */
  private canvasZoneActiveTabs = new Set<string>();

  /**
   * Batched tab updates — collects updateTab patches from plan_progress and
   * tool_activity events and flushes them once per animation frame. This
   * prevents multiple setState calls in the same frame when events arrive
   * in quick succession.
   */
  private pendingTabPatches: PendingTabPatch[] = [];
  private patchRafId: number | null = null;

  saveTimer: ReturnType<typeof setTimeout> | null = null;
  SAVE_DEBOUNCE_MS = 1000;

  /**
   * Tab ids that should be archived on the server with the next save.
   * handleCloseTab / handleClearChat push onto this set; `flushSave` reads
   * and clears it when dispatching the save. The atomic-archive path in
   * `saveTransaction` on the server prevents the close-then-beacon race
   * (archive POST losing to an in-flight delete-and-reinsert PUT).
   */
  private pendingArchiveTabIds: Set<string> = new Set();
  private panelDragEnterCounter = 0;
  /** Watches the content wrapper's height. On every growth (streaming delta,
   *  new tool row, 320ms expand transition frame) we pin to the bottom IF the
   *  user is stuck there — continuous instant pinning, never a chase-the-final-
   *  height animation. This is the ChatGPT-style stick-to-bottom core. */
  private contentResizeObserver: ResizeObserver | null = null;
  /** The element the observer is currently attached to — guards setupContentObserver
   *  against re-attaching on every componentDidUpdate. */
  private observedContentEl: HTMLElement | null = null;

  /** Current input value — stored outside React state to avoid re-renders on every keystroke.
   *  Synced back to tab state on: send, tab switch, clear, unmount. */
  private currentInputValue = "";

  /** Message history browsing: -1 = not browsing, 0+ = index into getUserMessages() (0 = most recent). */
  private historyIndex = -1;
  /** Saves the in-progress draft when entering history mode. */
  private historyDraft = "";

  private isMounted_ = false;

  /** Tracks which projectId's chat history is currently loaded — used to detect project switches. */
  private loadedProjectId: string | null = null;

  // Scroll-position tracking (plain property, not state, to avoid re-renders on every scroll).
  // `isUserNearBottom` is the stick-to-bottom flag: true → the observer pins on
  // every content growth; false → the user has scrolled up to read/inspect and
  // is left alone until they return to the bottom.
  private isUserNearBottom = true;
  private static SCROLL_THRESHOLD = 150; // px from bottom to count as "near bottom"
  /** Re-attach (resume sticking) only when the user is genuinely back at the
   *  very bottom — NOT merely within SCROLL_THRESHOLD. This hysteresis is what
   *  lets a user scroll a little way up to read a running tool without the next
   *  pin snapping them back down. */
  private static STICK_REATTACH_GAP = 4;
  /** Last observed scrollTop / scrollHeight — used to tell a user's upward
   *  scroll (scrollTop drops) from our own pins (which only ever increase it),
   *  while ignoring the browser's scrollTop clamp when content shrinks. */
  private lastScrollTop = 0;
  private lastScrollHeight = 0;

  // ── Render-path memoization caches ──
  private _cachedVisibleMsgs: McpChatMessage[] | null = null;
  private _cachedVisibleMsgsInput: { messages: McpChatMessage[]; count: number } | null = null;
  private _cachedHiddenCount = 0;

  private _cachedUndoableId: string | null = null;
  private _cachedUndoableInput: McpChatMessage[] | null = null;
  private _cachedUndoableRewindEnabled: boolean | null = null;

  private _cachedCanUseInspector = false;
  private _cachedCanUseInspectorTs = 0;
  private static CAN_USE_INSPECTOR_TTL = 1000; // 1 second cache

  private _cachedAnchorZones: Record<ChatAnchor, { x: number; y: number; width: number; height: number }> | null = null;
  private _anchorZonesDirty = true;

  // Resize state (kept outside React state for mouse-move perf)
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartW = 0;
  private resizeStartH = 0;
  private resizeStartTop = 0;

  constructor(props: McpChatPanelProps) {
    super(props);
    const initialTab = this.createNewTab();
    this.state = {
      tabs: [initialTab],
      activeTabId: initialTab.id,
      isWsConnected: false,
      capabilities: [],
      showDisconnectedBanner: false,
      showSaveFailureBanner: false,
      panelSize: this.loadPersistedSize(),
      horizontalPanelSize: this.loadPersistedHorizontalSize(),
      isResizing: false,
      showScrollToBottom: false,
      anchor: this.loadPersistedAnchor(),
      isDragging: false,
      dragPosition: null,
      dragOffset: { x: 0, y: 0 },
      nearestAnchor: null,
      isSnapping: false,
      isFileDragOver: false,
      isInspectorActive: false,
      isCollapsedForInspector: false,
      isFullScreen: false,
      isBuyCreditsOpen: false,
      search: { isOpen: false, query: "", matches: [], currentMatchIndex: -1, crossTab: false },
      isModelSelectorOpen: false,
      modelOverrides: getStoredModelOverrides(),
      serverProvider: null,
      projectAnalysisAvailable: false,
      remainingBalance: undefined,
      isBalancePanelOpen: false,
      balanceInfo: null,
      rewindEnabled: null,
      rewindDialog: null,
      historyModal: null
    };
  }

  loadPersistedSize(): PanelSize | null {
    try {
      const raw = localStorage.getItem(PANEL_SIZE_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed.width === "number" && typeof parsed.height === "number") {
        return {
          width: Math.max(PANEL_MIN_WIDTH, Math.min(parsed.width, getPanelMaxWidth())),
          height: Math.max(PANEL_MIN_HEIGHT, Math.min(parsed.height, getPanelMaxHeight()))
        };
      }
    } catch {
      // ignore
    }
    return null;
  }

  savePersistedSize(size: PanelSize): void {
    try {
      localStorage.setItem(PANEL_SIZE_STORAGE_KEY, JSON.stringify(size));
    } catch {
      // ignore
    }
  }

  loadPersistedHorizontalSize(): PanelSize | null {
    try {
      const raw = localStorage.getItem(PANEL_HORIZONTAL_SIZE_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed.width === "number" && typeof parsed.height === "number") {
        return {
          width: Math.max(PANEL_HORIZONTAL_MIN_WIDTH, Math.min(parsed.width, getPanelHorizontalMaxWidth())),
          height: Math.max(PANEL_HORIZONTAL_MIN_HEIGHT, Math.min(parsed.height, getPanelHorizontalMaxHeight()))
        };
      }
    } catch {
      // ignore
    }
    return null;
  }

  savePersistedHorizontalSize(size: PanelSize): void {
    try {
      localStorage.setItem(PANEL_HORIZONTAL_SIZE_STORAGE_KEY, JSON.stringify(size));
    } catch {
      // ignore
    }
  }

  isHorizontalAnchor(anchor?: ChatAnchor): boolean {
    const a = anchor ?? this.state.anchor;
    return a === "top" || a === "bottom";
  }

  getResizeCursor(anchor?: ChatAnchor): string {
    const a = anchor ?? this.state.anchor;
    if (a === "bottom-left") return "nesw-resize";
    if (a === "top" || a === "bottom") return "ns-resize";
    return "nwse-resize";
  }

  computeDefaultHorizontalSize(): PanelSize {
    const leftMenuWidth = 48;
    const margin = 24;
    const leftPanelEl = document.querySelector(".kuika-left-panel") as HTMLElement | null;
    const rightPanelEl = document.querySelector(".kuika_rightPanel") as HTMLElement | null;
    const leftPanelW = leftPanelEl ? leftPanelEl.getBoundingClientRect().width : 0;
    const rightPanelW = rightPanelEl ? rightPanelEl.getBoundingClientRect().width : readRightPanelWidth();
    const availableWidth = window.innerWidth - leftMenuWidth - leftPanelW - rightPanelW - margin * 2;
    return {
      width: Math.max(PANEL_HORIZONTAL_MIN_WIDTH, Math.min(availableWidth, getPanelHorizontalMaxWidth())),
      height: PANEL_HORIZONTAL_DEFAULT_HEIGHT
    };
  }

  /** True when the designer's right (properties/styling) panel is mounted/open. */
  isRightPanelOpen(): boolean {
    return document.querySelector(".kuika_rightPanel") != null;
  }

  getEffectivePanelSize(): PanelSize {
    // While the element inspector is active, shrink the floating panel to a tall
    // narrow strip (the right-panel width) so it stays visible yet leaves the
    // canvas clickable for picking — instead of hiding entirely. Transient: the
    // stored panelSize is untouched, so exiting the inspector restores it.
    if (this.state.isCollapsedForInspector) {
      const stripWidth: number = this.isRightPanelOpen() ? readRightPanelWidth() : RIGHT_MIN_WIDTH;
      return { width: stripWidth, height: window.innerHeight - 80 };
    }
    // Full-screen overrides size ONLY (no position change) — the panel stays
    // anchored and grows to fill the viewport minus small margins so it fits
    // on screen. The stored panelSize is untouched, so toggling off restores
    // the exact prior dimensions. Floating-only (docked renders elsewhere).
    if (this.state.isFullScreen) {
      return { width: window.innerWidth - 48, height: window.innerHeight - 80 };
    }
    if (this.isHorizontalAnchor()) {
      return this.state.horizontalPanelSize ?? this.computeDefaultHorizontalSize();
    }
    return this.state.panelSize ?? { width: 400, height: 560 };
  }

  handleFullScreenToggle = (): void => {
    this.setState((prev: McpChatPanelState) => ({ isFullScreen: !prev.isFullScreen }));
  };

  canUseInspector = (): boolean => {
    const now = performance.now();
    if (now - this._cachedCanUseInspectorTs < McpChatPanel.CAN_USE_INSPECTOR_TTL) {
      return this._cachedCanUseInspector;
    }
    try {
      const sd = getDesignerRuntime();
      const dashboardState = sd?.designSituationProvider?.state?.dashboardState ?? sd?.state?.dashboardState;
      this._cachedCanUseInspector = dashboardState === 1 || dashboardState === 17;
    } catch {
      this._cachedCanUseInspector = false;
    }
    this._cachedCanUseInspectorTs = now;
    return this._cachedCanUseInspector;
  };

  dispatchInspectorRequest = (active: boolean) => {
    window.dispatchEvent(new CustomEvent(MCP_INSPECTOR_REQUEST_EVENT, { detail: { active } }));
  };

  /**
   * Attach the component currently selected on the canvas as a
   * selected-element pill — the same result as picking it with the inspector,
   * but from the current selection (bound to the Tab shortcut). Returns true
   * when an element was added (so the caller can swallow the key), false when
   * there is no selection or it's already attached.
   */
  addCurrentSelection = (): boolean => {
    let attachment: SelectedElementAttachment | null = null;
    try {
      const sd = getDesignerRuntime();
      const selectedDi = sd?.displayItemProvider?.getSelectedDisplayItem?.();
      if (selectedDi?.id != null) {
        const kuikaId: string = selectedDi.id.toString();
        const componentName: string = selectedDi.item?.name || "Component";
        const displayName: string = selectedDi.friendlyName || selectedDi.item?.name || componentName;
        attachment = { kuikaId, componentName, displayName };
      }
    } catch {
      return false;
    }
    if (!attachment) return false;

    const activeTab = this.getActiveTab();
    if (activeTab.selectedElementAttachments.some((a) => a.kuikaId === attachment!.kuikaId)) {
      return false;
    }
    this.updateActiveTab((tab) => ({
      selectedElementAttachments: [...tab.selectedElementAttachments, attachment!]
    }));
    return true;
  };

  deactivateInspector = (clearAttachment = false, restoreFocus = true) => {
    if (this.state.isInspectorActive) {
      this.dispatchInspectorRequest(false);
    }

    if (clearAttachment) {
      this.updateActiveTab(
        () => ({ selectedElementAttachments: [] }),
        () => {
          this.setState({ isInspectorActive: false, isCollapsedForInspector: false }, () => {
            if (restoreFocus) this.inputAreaRef.current?.focus();
          });
        }
      );
      return;
    }

    this.setState({ isInspectorActive: false, isCollapsedForInspector: false }, () => {
      if (restoreFocus) this.inputAreaRef.current?.focus();
    });
  };

  handleToggleInspector = () => {
    if (!this.canUseInspector()) return;

    const nextActive = !this.state.isInspectorActive;
    this.setState({ isInspectorActive: nextActive, isCollapsedForInspector: nextActive }, () => {
      this.dispatchInspectorRequest(nextActive);
      if (!nextActive) {
        this.inputAreaRef.current?.focus();
      }
    });
  };

  handleInspectorSelected = (event: Event) => {
    const detail = (event as CustomEvent<SelectedElementAttachment>).detail;
    if (!detail?.kuikaId || !detail?.displayName) return;

    this.updateActiveTab(
      (tab) => {
        if (tab.selectedElementAttachments.some((att) => att.kuikaId === detail.kuikaId)) {
          return {};
        }
        return { selectedElementAttachments: [...tab.selectedElementAttachments, detail] };
      },
      () => {
        this.setState({ isInspectorActive: false, isCollapsedForInspector: false }, () => {
          this.dispatchInspectorRequest(false);
          this.inputAreaRef.current?.focus();
        });
      }
    );
  };

  handleInspectorCancelled = () => {
    this.setState({ isInspectorActive: false, isCollapsedForInspector: false }, () => {
      this.dispatchInspectorRequest(false);
      this.inputAreaRef.current?.focus();
    });
  };

  handleInspectorDirectSend = (event: Event) => {
    const detail = (event as CustomEvent<InspectorDirectSendPayload>).detail;
    if (!detail?.entries?.length) return;

    // Build per-entry segments so each badge sits above its own prompt inside
    // a single user message bubble — instead of stacking all badges at the top.
    const segments: SelectedElementSegment[] = detail.entries.map((entry) => ({
      attachment: {
        kuikaId: entry.kuikaId,
        componentName: entry.componentName,
        displayName: entry.displayName
      },
      promptText: entry.promptText?.trim() ?? ""
    }));

    const seenIds = new Set<string>();
    const attachments: SelectedElementAttachment[] = [];
    for (const seg of segments) {
      if (seenIds.has(seg.attachment.kuikaId)) continue;
      seenIds.add(seg.attachment.kuikaId);
      attachments.push(seg.attachment);
    }

    this.setState({ isInspectorActive: false, isCollapsedForInspector: false }, () => {
      this.dispatchInspectorRequest(false);
      this.handleSend("", attachments, segments);
    });
  };

  handleInspectorPour = (event: Event) => {
    const detail = (event as CustomEvent<InspectorPourPayload>).detail;
    if (!detail?.entries?.length) {
      this.handleInspectorCancelled();
      return;
    }

    this.setState({ isInspectorActive: false, isCollapsedForInspector: false }, () => {
      this.dispatchInspectorRequest(false);

      const contentEditable = this.inputAreaRef.current?.contentEditableRef.current;
      if (!contentEditable) return;

      for (let i = 0; i < detail.entries.length; i++) {
        const entry = detail.entries[i];
        contentEditable.insertElementPill({
          kuikaId: entry.kuikaId,
          componentName: entry.componentName,
          displayName: entry.displayName
        });
        if (entry.promptText) {
          contentEditable.insertTextAtCursor(` ${entry.promptText}`);
        }
        if (i < detail.entries.length - 1) {
          contentEditable.insertTextAtCursor("\n");
        }
      }

      this.inputAreaRef.current?.focus();
    });
  };

  buildOutgoingMessage = (
    message: string,
    selectedElementAttachments?: SelectedElementAttachment[],
    selectedElementSegments?: SelectedElementSegment[]
  ): string => {
    const trimmedMessage = message.trim();

    if (selectedElementSegments && selectedElementSegments.length > 0) {
      // Each segment becomes "selected element: <kuikaId>\n<promptText>". Display
      // names are intentionally omitted from the LLM payload — instance suffixes
      // like "Col1_1" / "Header1" trip the classifier's expectedAdditions
      // extractor into reading them as "1 col" / "1 header" add requests. The
      // visual chat-bubble badge still shows the friendly name; this only
      // affects what the model sees.
      const segmentBlocks = selectedElementSegments
        .map((seg) => {
          const head = `selected element: ${seg.attachment.kuikaId}`;
          const body = seg.promptText.trim();
          return body ? `${head}\n${body}` : head;
        })
        .join("\n\n");
      return trimmedMessage ? `${segmentBlocks}\n\n${trimmedMessage}` : segmentBlocks;
    }

    const selectionPrefix =
      selectedElementAttachments && selectedElementAttachments.length > 0
        ? selectedElementAttachments.map((att) => `selected element: ${att.kuikaId}`).join("\n")
        : "";

    if (selectionPrefix && trimmedMessage) {
      return `${selectionPrefix}\n\n${trimmedMessage}`;
    }

    return selectionPrefix || trimmedMessage;
  };

  componentDidMount(): void {
    this.isMounted_ = true;
    setMcpChatPanelInstance(this);
    this.setupBridgeConnectionListener();
    this.subscribeBalanceState();
    this.fetchCapabilities();
    this.fetchServerConfig();
    // Eager cold-start fetch regardless of panel visibility — when the
    // user opens the panel from a FAB or anchor, the balance pill should
    // render instantly, not wait for an HTTP round-trip. The fetch is a
    // single GET; the 5-min throttle in fetchBalance prevents repeats
    // when the panel is opened/closed quickly. When we're returning from a
    // Stripe top-up (?session_id=...) the dedicated handler credits the
    // ledger and force-refreshes instead — see handleStripeReturnIfPresent.
    const isStripeReturn: boolean = new URLSearchParams(window.location.search).has("session_id");
    if (isStripeReturn) {
      void this.handleStripeReturnIfPresent();
    } else {
      this.fetchBalance();
    }
    if (this.props.isMcpChatPanelVisible) {
      window.addEventListener("keydown", this.handleGlobalKeyDown, true);
      window.addEventListener("keydown", this.handleFocusTrap);
    }
    window.addEventListener(MCP_TOOL_ACTIVITY_EVENT, this.handleToolActivity as EventListener);
    window.addEventListener(MCP_INSPECTOR_SELECTED_EVENT, this.handleInspectorSelected as EventListener);
    window.addEventListener(MCP_INSPECTOR_CANCEL_EVENT, this.handleInspectorCancelled as EventListener);
    window.addEventListener(MCP_INSPECTOR_DIRECT_SEND_EVENT, this.handleInspectorDirectSend as EventListener);
    window.addEventListener(MCP_INSPECTOR_POUR_EVENT, this.handleInspectorPour as EventListener);
    window.addEventListener(CHAT_SAVE_FAILED_EVENT, this.handleSaveFailed);
    window.addEventListener(CHAT_SAVE_RECOVERED_EVENT, this.handleSaveRecovered);
    window.addEventListener("resize", this.handleWindowResize);
    window.addEventListener("popstate", this.handleNavigationAway);
    window.addEventListener("beforeunload", this.handleNavigationAway);

    this.setupContentObserver();

    // Listen for project loads/switches throughout the component's lifetime.
    // If projectId is already available, restore immediately as well.
    window.addEventListener(PROJECT_LOADED_EVENT, this.handleProjectLoaded);
    const projectId = getActiveProjectId();
    if (projectId) {
      this.ensureRewindStaging();
      this.tryRestorePersistedState();
    }
  }

  handleProjectLoaded = () => {
    const newProjectId = getActiveProjectId();

    // Same project reloaded (e.g. screen switch) — nothing to do; the
    // debounced save timer (if any) will fire under the same project.
    if (newProjectId && newProjectId === this.loadedProjectId) {
      return;
    }

    // Different project — save current chat under its OWN projectId, cancel
    // active work, reset state, load new project's chat.
    if (this.loadedProjectId) {
      // Drop any pending debounced save so the timer doesn't fire later
      // under the new project and save stale A-tabs under B's id.
      if (this.saveTimer) {
        clearTimeout(this.saveTimer);
        this.saveTimer = null;
      }
      // Flush the in-memory tabs to the OLD project's row on the server.
      // flushSave reads `this.loadedProjectId` (still the old one) — safe
      // regardless of `window.kuika.activeProjectId` already being flipped
      // to the new project by ProjectProvider.
      this.flushSave();
      this.cancelAllActiveRequests();
    }

    // Reset the savedAt sync cursor so the new project's first
    // checkForNewerState doesn't compare against the previous project's
    // timestamp.
    resetSyncCursor();

    // Clear the panel's record of what project the in-memory tabs belong
    // to — the new project's id is set later in tryRestorePersistedState
    // once it actually loads that project's history.
    this.loadedProjectId = null;

    // Reset rewindEnabled — will be re-resolved for the new project.
    this.setState({ rewindEnabled: null });
    this.rewindStagingInFlightProjectId = null;
    // Fire-and-forget: ensure draft-scope staging is active for this project
    // so every save records into KProjectStagedChange. Guards against projects
    // with full branching provisioned — in that case undo is disabled.
    this.ensureRewindStaging();

    // Reset to a clean tab state before loading new project's history
    const freshTab = this.createNewTab();
    this.setState({ tabs: [freshTab], activeTabId: freshTab.id }, () => {
      this.tryRestorePersistedState();
    });
  };

  /**
   * Tracks the project id the in-flight (or last completed) `EnableStagingOnly`
   * call targeted. Guards against double-firing on initial mount — the panel
   * calls `ensureRewindStaging()` from `componentDidMount` when projectId is
   * already present, and `PROJECT_LOADED_EVENT` may fire concurrently for the
   * same project.
   */
  private rewindStagingInFlightProjectId: string | null = null;

  /**
   * Flip IsBranchingActive=true (without provisioning) on the current project
   * so BranchingSaveInterceptor starts routing patches into KProjectStagedChange.
   * Idempotent: no-op when already enabled, disabled (rewindEnabled=false) when
   * the project has full branching provisioned since that path writes to
   * KBranchStagedChange instead and our draft-discard flow doesn't apply there.
   */
  ensureRewindStaging = async (): Promise<void> => {
    const projectId: string | null = getActiveProjectId() || null;
    if (!projectId) return;
    if (this.rewindStagingInFlightProjectId === projectId) return;
    this.rewindStagingInFlightProjectId = projectId;

    try {
      const res = await BranchingService.EnableStagingOnly();
      if (!this.isMounted_) return;
      const status = res.data;
      const enabled = status.stagingEnabled && !status.fullyProvisioned;
      this.setState({ rewindEnabled: enabled });
    } catch (err) {
      if (this.isMounted_) this.setState({ rewindEnabled: false });
    }
  };

  /**
   * Capture the current set of staged-op IDs for the project. Used as the
   * baseline snapshot stored on user messages: at restore time, the diff
   * (current staged ops) \ (baseline) is the set of ops the message owns.
   * Returns null on failure — caller should treat the message as non-rewindable.
   */
  /**
   * Resolve a component kuikaId to a human-friendly label by looking it up
   * in the designer's live screenDesignDictionary. Falls back to null so
   * callers can use a generic "Component #N" placeholder. Used by the
   * revert confirmation dialog to name components instead of exposing
   * raw kuikaIds.
   */
  private resolveComponentLabel = (kuikaId: number): string | null => {
    try {
      const sd = getDesignerRuntime();
      const dict = sd?.screenProvider?.state?.screenDesignDictionary;
      if (!dict) return null;
      const entry = dict[kuikaId];
      if (!entry) return null;
      const friendly = typeof entry.friendlyName === "string" ? entry.friendlyName.trim() : "";
      const typeName = typeof entry?.item?.name === "string" ? entry.item.name : "";
      if (friendly && typeName) return `${friendly} (${typeName})`;
      if (friendly) return friendly;
      if (typeName) return `${typeName} #${kuikaId}`;
      return null;
    } catch {
      return null;
    }
  };

  /**
   * OpIds present before this chat message (rewind checkpoint).
   * Prefer light stagedDraftOpIds; if that fails (old CoreApi without the
   * route, timeout, etc.) fall back to stagedDraftEntities and extract opIds
   * so chat still works and rewind is preserved. null only when both fail.
   */
  captureStagedOpIdsBaseline = async (): Promise<string[] | null> => {
    const projectId = getActiveProjectId();
    if (!projectId) return null;

    const dedupeIds = (raw: string[]): string[] => {
      const ids: string[] = [];
      const seen = new Set<string>();
      for (const id of raw) {
        if (typeof id !== "string" || id.length === 0) continue;
        if (seen.has(id)) continue;
        seen.add(id);
        ids.push(id);
      }
      return ids;
    };

    try {
      const res = await BranchingService.GetStagedDraftOpIds(projectId);
      if (Array.isArray(res.data)) {
        return dedupeIds(res.data);
      }
    } catch (err) {
      console.warn("[rewind] light stagedDraftOpIds failed — falling back to stagedDraftEntities", err);
    }

    // Fallback: full entities (slow) but same opId set — keeps rewind when
    // CoreApi has not been restarted with the new endpoint yet.
    try {
      const res = await BranchingService.GetStagedDraftEntities(projectId);
      const entities = res.data || [];
      const raw: string[] = [];
      for (const entity of entities) {
        if (!entity.patch) continue;
        for (const op of entity.patch) {
          if (op.opId) raw.push(op.opId);
        }
      }
      return dedupeIds(raw);
    } catch (err) {
      console.warn("[rewind] baseline capture failed (both light and full)", err);
      return null;
    }
  };

  tryRestorePersistedState = async () => {
    const projectId = getActiveProjectId();
    if (!projectId) return;

    this.loadedProjectId = projectId;

    let persisted = await loadChatState(projectId);
    if (!this.isMounted_) return;
    // Guard against a rapid A→B switch: if the active project changed
    // while we were awaiting A's fetch, drop the response — applying it
    // would clobber B's restored state with A's messages.
    if (getActiveProjectId() !== projectId) return;

    // If server has no data, try one-time migration from localStorage
    if (!persisted) {
      persisted = await migrateFromLocalStorage(projectId);
      if (!this.isMounted_) return;
      if (getActiveProjectId() !== projectId) return;
    }

    // Start listening for cross-tab sync (even if no data yet)
    document.addEventListener("visibilitychange", this.handleVisibilitySync);

    if (!persisted) return;

    this.applyPersistedState(persisted);
  };

  handleVisibilitySync = async () => {
    if (document.visibilityState !== "visible") return;

    const projectId = this.loadedProjectId;
    const newerState = await checkForNewerState(projectId);
    if (!this.isMounted_ || !newerState) return;
    // Drop stale responses if the user switched projects mid-fetch.
    if (this.loadedProjectId !== projectId) return;

    this.applyPersistedState(newerState);
  };

  /**
   * Attach (or re-attach) the stick-to-bottom ResizeObserver to the content
   * wrapper. Idempotent: if already observing the current element, it's a
   * no-op, so calling it from componentDidUpdate every render is cheap.
   */
  private setupContentObserver = () => {
    const contentEl = this.messagesContentRef.current;
    if (contentEl === this.observedContentEl) return;

    if (this.contentResizeObserver) {
      this.contentResizeObserver.disconnect();
      this.contentResizeObserver = null;
    }
    this.observedContentEl = contentEl;
    if (!contentEl) return;

    this.contentResizeObserver = new ResizeObserver(() => {
      // Fires when the content height changes (streaming text, a tool row
      // added, an expand transition frame) OR when the container's own box
      // changes (panel resized shorter/taller). Either way, keep the bottom
      // glued if the user is stuck there, and reflect button visibility.
      this.pinIfStuck();
      this.syncScrollButtonVisibility();
    });
    this.contentResizeObserver.observe(contentEl);
    // Also watch the scroll container itself: a vertical panel resize changes
    // its clientHeight (not the content's height), which would otherwise leave
    // the view parked off-bottom until the next content change.
    const container = this.messagesContainerRef.current;
    if (container) this.contentResizeObserver.observe(container);
  };

  /**
   * Instant pin to the bottom — the heart of the stick-to-bottom behavior.
   * No-op when the user has scrolled up (isUserNearBottom=false) so they're
   * never yanked down while inspecting. The `> 1` gap guard avoids redundant
   * scrollTop writes (and any ResizeObserver feedback loop). Direct scrollTop
   * assignment is genuinely instant now that the container has no
   * `scroll-behavior: smooth`.
   */
  private pinIfStuck = () => {
    if (!this.isUserNearBottom) return;
    const container = this.messagesContainerRef.current;
    if (!container) return;
    const gap = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (gap > 1) container.scrollTop = container.scrollHeight;
  };

  /** Show the "scroll to bottom" button whenever the view is parked away from
   *  the bottom. Cheap: only setState when the boolean actually flips. */
  private syncScrollButtonVisibility = () => {
    const container = this.messagesContainerRef.current;
    if (!container) return;
    const nearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < McpChatPanel.SCROLL_THRESHOLD;
    if (this.state.showScrollToBottom === !nearBottom) return;
    this.setState({ showScrollToBottom: !nearBottom });
  };

  private applyPersistedState(persisted: import("../persistence/mcp-chat-persistence").PersistedChatState): void {
    const { tabs: restored, activeTabId } = restoreTabs(persisted);

    // KOA-18210 — never overwrite a tab whose turn is still running. This path
    // also runs on reconnect (see `setupBridgeConnectionListener`), where the
    // snapshot has no record of the in-flight turn.
    const liveTabs: ChatTab[] = this.state.tabs.filter((tab: ChatTab): boolean =>
      this.activeChatRequestIds.has(tab.id)
    );
    const tabs: ChatTab[] = mergeRestoredTabsWithLiveTurns(restored, liveTabs);

    let maxMsgIdNum = 0;
    for (const tab of tabs) {
      for (const msg of tab.messages) {
        const msgMatch = msg.id.match(/_(\d+)$/);
        if (msgMatch) maxMsgIdNum = Math.max(maxMsgIdNum, parseInt(msgMatch[1], 10));
      }
    }
    if (maxMsgIdNum > messageIdCounter) messageIdCounter = maxMsgIdNum;

    this.setState({ tabs, activeTabId }, () => {
      this.loadInputFromTab(activeTabId);
      this.forceScrollToBottom();
      // Claim these conversations' event channels: the restored sessionIds are
      // the address the server delivers turn events to (KOA-18210).
      this.sendChatAttachForTabs();
    });
  }

  /**
   * Tell the server which conversations this browser is listening for, so an
   * in-flight turn keeps narrating into the CURRENT socket (KOA-18210).
   *
   * A turn's events are addressed by `sessionId` — durable, restored with each
   * chat tab — instead of the socket that opened it, because that socket dies on
   * every blip. Claiming per chat tab is also what keeps one tab's build out of
   * another: the server has no "latest socket" fallback.
   */
  private sendChatAttachForTabs = (): void => {
    const bridge = getMcpBridgeInstance();
    if (!bridge?.isConnected) return;
    const claims: ChatAttachClaim[] = this.state.tabs
      .filter((tab: ChatTab): boolean => !!tab.sessionId)
      .map((tab: ChatTab): ChatAttachClaim => ({ sessionId: tab.sessionId as string, chatTabId: tab.id }));
    bridge.sendChatAttach(claims);
  };

  /**
   * Reconcile the panel's spinners with what the server actually still has
   * running, using the `chat_attach` answer (KOA-18210).
   *
   * A turn can end while we are disconnected — its `done` was emitted into the
   * dead socket, or it was cancelled outright by the close handler. The tab would
   * otherwise spin forever, because the client never gives up on a requestId.
   * Nothing here fakes a result: the turn's output is genuinely lost, so say so.
   */
  private handleChatAttachResult = (outcomes: ChatAttachOutcome[]): void => {
    if (!this.isMounted_) return;
    for (const outcome of outcomes) {
      const tab: ChatTab | undefined = this.state.tabs.find(
        (candidate: ChatTab): boolean => candidate.id === outcome.chatTabId
      );
      if (!tab) continue;
      const trackedRequestId: string | undefined = this.activeChatRequestIds.get(tab.id);
      if (!trackedRequestId) continue;
      if (outcome.ok && outcome.requestIds.includes(trackedRequestId)) continue; // still running — events resume

      // Close it the same way a normal finish does. The turn's OUTPUT is lost,
      // but the tool calls that already succeeded are not — they are in the
      // activity archive, and wiping `toolActivities` here (as this used to do)
      // threw them away along with the answer (KOA-17572).
      this.finalizeTurn(tab.id, {
        outcome: "disconnected",
        chatRequestId: trackedRequestId,
        notice: {
          content:
            "Bağlantı kesildiği için bu isteğin sonucu alınamadı. Yapılan değişiklikler ekranda olabilir — kontrol edip gerekirse tekrar deneyin.",
          isError: true
        }
      });
    }
  };

  /**
   * Cancel all in-flight requests: chat requests, prompt improve, streaming buffer.
   * Reusable from componentWillUnmount, handleStop, navigation-away handlers,
   * and parent components (design-left-menu).
   */
  cancelAllActiveRequests = () => {
    const bridge = getMcpBridgeInstance();

    // Cancel any in-flight prompt improve
    for (const tab of this.state.tabs) {
      if (tab.isImprovingPrompt && tab.improveRequestId) {
        if (bridge) {
          bridge.sendPromptImproveCancel(tab.improveRequestId);
          bridge.offChatEvent(tab.improveRequestId);
        }
      }
    }

    // Cancel all active chat requests via WS — force hard abort since the
    // frontend is tearing down listeners and won't be able to escalate.
    //
    // Fold each turn before dropping it. This runs from unmount and
    // beforeunload, i.e. after `isMounted_` is false, so `finalizeTurn` is
    // asked to apply synchronously — React would silently discard a setState
    // here, and the save that follows has to see the folded tab. Without the
    // fold the tab was left with `isLoading: true` and a live transcript that
    // could never progress (KOA-17572).
    //
    // NOTE: closing the chat PANEL does not reach this path — `McpChatPanel` is
    // rendered unconditionally and visibility is only a prop, so a turn keeps
    // running in the background. Only leaving the designer, switching project
    // or navigating away cancels.
    const cancelledEntries: [string, string][] = Array.from(this.activeChatRequestIds.entries());
    for (const [, reqId] of cancelledEntries) {
      if (bridge) bridge.sendChatCancel(reqId, true);
      bridge?.offChatEvent(reqId);
    }
    for (const [cancelledTabId, reqId] of cancelledEntries) {
      this.finalizeTurn(cancelledTabId, {
        outcome: "cancelled",
        chatRequestId: reqId,
        dropQueue: true,
        sync: true
      });
    }
    this.activeChatRequestIds.clear();
    this.requestStartTimes.clear();
    this.streamingMessageIds.clear();
    this.beforeSnapshots.clear();

    // Flush and clear streaming buffer/rAF. The flush must happen BEFORE the
    // clear — dropping the buffer outright discarded the last un-flushed frame
    // of streamed text on every teardown path.
    if (this.streamingBuffer.size > 0) {
      this.flushStreamingBufferSync();
    }
    if (this.streamingRafId != null) {
      cancelAnimationFrame(this.streamingRafId);
      this.streamingRafId = null;
    }
    this.streamingBuffer.clear();
    if (this.patchRafId != null) {
      cancelAnimationFrame(this.patchRafId);
      this.patchRafId = null;
    }
    this.pendingTabPatches.length = 0;

    if (this.canvasZoneActiveTabs.size > 0) {
      this.canvasZoneActiveTabs.clear();
      window.dispatchEvent(new CustomEvent(MCP_ZONE_RESET, { detail: { zone: "canvas" } }));
    }
  };

  /** Browser popstate / beforeunload handler — save state and cancel active work on navigation away. */
  private handleNavigationAway = () => {
    // Flush any streaming content into state synchronously (React won't process setState during beforeunload)
    this.flushStreamingBufferSync();

    // Save via sendBeacon — works reliably during beforeunload. Include
    // the pending archive set atomically so a just-closed tab doesn't
    // lose its baseline-opId metadata to a race between archive POST and
    // the beacon PUT.
    // Cancel BEFORE the beacon: cancelAllActiveRequests folds each in-flight
    // turn into its tab synchronously, and the beacon has to carry that folded
    // state — otherwise the tool cards the turn earned are lost on refresh.
    this.cancelAllActiveRequests();

    const archivedIds = this.pendingArchiveTabIds.size > 0 ? Array.from(this.pendingArchiveTabIds) : undefined;
    this.pendingArchiveTabIds.clear();
    saveBeacon(this.loadedProjectId, this.state.tabs, this.state.activeTabId, archivedIds);
  };

  componentWillUnmount(): void {
    this.isMounted_ = false;
    setMcpChatPanelInstance(null);
    publishMcpChatBusy(false);

    // Cancel all active requests (chat, prompt improve, streaming)
    this.cancelAllActiveRequests();

    // Sync input value to tab state before final save
    const activeTabForInput = this.getActiveTab();
    if (activeTabForInput && activeTabForInput.inputValue !== this.currentInputValue) {
      activeTabForInput.inputValue = this.currentInputValue;
    }
    document.removeEventListener("visibilitychange", this.handleVisibilitySync);
    window.removeEventListener(PROJECT_LOADED_EVENT, this.handleProjectLoaded);
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      // Fire-and-forget final save — includes any pending archive ids so
      // componentWillUnmount (e.g. route change) doesn't strand them.
      this.flushSave();
    }
    window.removeEventListener("keydown", this.handleGlobalKeyDown, true);
    window.removeEventListener("keydown", this.handleFocusTrap);
    this.balanceStateUnsub?.();
    this.balanceStateUnsub = null;
    window.removeEventListener(MCP_TOOL_ACTIVITY_EVENT, this.handleToolActivity as EventListener);
    window.removeEventListener(MCP_INSPECTOR_SELECTED_EVENT, this.handleInspectorSelected as EventListener);
    window.removeEventListener(MCP_INSPECTOR_CANCEL_EVENT, this.handleInspectorCancelled as EventListener);
    window.removeEventListener(MCP_INSPECTOR_DIRECT_SEND_EVENT, this.handleInspectorDirectSend as EventListener);
    window.removeEventListener(MCP_INSPECTOR_POUR_EVENT, this.handleInspectorPour as EventListener);
    window.removeEventListener(CHAT_SAVE_FAILED_EVENT, this.handleSaveFailed);
    window.removeEventListener(CHAT_SAVE_RECOVERED_EVENT, this.handleSaveRecovered);
    window.removeEventListener("resize", this.handleWindowResize);
    window.removeEventListener("popstate", this.handleNavigationAway);
    window.removeEventListener("beforeunload", this.handleNavigationAway);
    if (this.disconnectedBannerTimer) {
      clearTimeout(this.disconnectedBannerTimer);
      this.disconnectedBannerTimer = null;
    }
    document.removeEventListener("mousemove", this.handleResizeMouseMove);
    document.removeEventListener("mouseup", this.handleResizeMouseUp);
    document.removeEventListener("mousemove", this.handleDragMove);
    document.removeEventListener("mouseup", this.handleDragEnd);
    if (this.contentResizeObserver) {
      this.contentResizeObserver.disconnect();
      this.contentResizeObserver = null;
      this.observedContentEl = null;
    }
    this.dispatchInspectorRequest(false);
  }

  componentDidUpdate(prevProps: McpChatPanelProps, prevState: McpChatPanelState): void {
    // Any tab with a turn running — the header's AI button spins on it (mcp-chat-busy.ts).
    publishMcpChatBusy(this.state.tabs.some((tab: ChatTab): boolean => tab.isLoading));
    if (this.props.isMcpChatPanelVisible && !prevProps.isMcpChatPanelVisible) {
      window.addEventListener("keydown", this.handleGlobalKeyDown, true);
      window.addEventListener("keydown", this.handleFocusTrap);
      setTimeout(() => this.inputAreaRef.current?.focus(), 300);
    } else if (!this.props.isMcpChatPanelVisible && prevProps.isMcpChatPanelVisible) {
      window.removeEventListener("keydown", this.handleGlobalKeyDown, true);
      window.removeEventListener("keydown", this.handleFocusTrap);
      this.deactivateInspector(true, false);
    }

    const wasDisplayed = prevProps.isMcpChatPanelVisible || prevState.anchor === "right-panel";
    const isDisplayed = this.isPanelDisplayed();
    if (isDisplayed && !wasDisplayed) {
      // Refresh the lifetime breakdown when the panel becomes visible —
      // throttled to 5 min so rapid open/close doesn't hammer Backend.
      // Live credits are pushed via balance_state WS events.
      this.fetchBalance();
    }

    // Invalidate anchor zone cache when dock eligibility changes
    if (this.props.canDockToRightPanel !== prevProps.canDockToRightPanel) {
      this._anchorZonesDirty = true;
    }

    // Idempotent — only re-attaches if the content wrapper element changed.
    this.setupContentObserver();
  }

  private isPanelDisplayed(): boolean {
    return this.props.isMcpChatPanelVisible || this.state.anchor === "right-panel";
  }

  // ── WS connection state (replaces HTTP health polling) ──

  private balanceStateUnsub: (() => void) | null = null;
  private latestBalanceCursor: BalanceCursor | null = null;

  /**
   * Subscribe to authoritative `balance_state` snapshots from the server's
   * BalanceManager. The bridge already drops out-of-order pushes; the panel
   * re-applies the same freshness check to guard against re-delivery during
   * mount/unmount cycles where the bridge replays its cached snapshot.
   */
  subscribeBalanceState = (): void => {
    const bridge = getMcpBridgeInstance();
    if (!bridge) {
      setTimeout(() => {
        if (this.isMounted_) this.subscribeBalanceState();
      }, 100);
      return;
    }
    this.balanceStateUnsub?.();
    this.balanceStateUnsub = bridge.onBalanceState((snapshot) => {
      if (!this.isMounted_) return;
      if (!isFresherBalanceSnapshot(this.latestBalanceCursor, snapshot)) return;
      this.latestBalanceCursor = snapshot;
      this.setState((prev): Pick<McpChatPanelState, "remainingBalance" | "balanceInfo"> | null => {
        if (prev.remainingBalance === snapshot.credits) {
          return null;
        }
        // Derive `creditsConsumed` from live remaining so the breakdown stays
        // self-consistent — otherwise WS ticks `remaining` down each step but
        // `consumed` stays frozen at the last HTTP /balance fetch.
        const totalPool =
          (prev.balanceInfo?.totalPurchasedCents ?? 0) +
          (prev.balanceInfo?.totalGrantedCredits ?? prev.balanceInfo?.starterCreditsIncluded ?? 0);
        const derivedConsumed = Math.max(0, totalPool - snapshot.credits);
        const nextBalanceInfo: BalanceInfo = prev.balanceInfo
          ? {
              ...prev.balanceInfo,
              creditsRemaining: snapshot.credits,
              creditsConsumed: derivedConsumed
            }
          : {
              creditsRemaining: snapshot.credits,
              creditsConsumed: 0,
              totalPurchasedCents: 0,
              totalGrantedCredits: 0,
              totalSpentUsd: 0,
              starterCreditsIncluded: 0
            };
        return {
          remainingBalance: snapshot.credits,
          balanceInfo: nextBalanceInfo
        };
      });
    });
  };

  setupBridgeConnectionListener = () => {
    const bridge = getMcpBridgeInstance();
    if (bridge) {
      this.setState({ isWsConnected: bridge.isConnected });
      bridge.setOnChatAttachResult(this.handleChatAttachResult);
      bridge.setOnConnectionChange((connected: boolean) => {
        if (!this.isMounted_) return;
        const wasDisconnected: boolean = !this.state.isWsConnected;
        this.setState({ isWsConnected: connected });
        if (!connected) {
          // Defer the banner: most disconnects (server hot-reload, tab
          // suspension recovery, network blip) reconnect within a few seconds.
          // If we still aren't connected when the timer fires, surface it then.
          if (!this.disconnectedBannerTimer) {
            this.disconnectedBannerTimer = setTimeout(() => {
              this.disconnectedBannerTimer = null;
              if (this.isMounted_ && !this.state.isWsConnected) {
                this.setState({ showDisconnectedBanner: true });
              }
            }, DISCONNECTED_BANNER_DEBOUNCE_MS);
          }
          this.latestBalanceCursor = null;
          return;
        }
        if (this.disconnectedBannerTimer) {
          clearTimeout(this.disconnectedBannerTimer);
          this.disconnectedBannerTimer = null;
        }
        // After server restart the in-memory ConversationStore is gone but
        // SQLite chat history persists.  Reload persisted state on reconnect
        // so the user's history reappears.
        if (wasDisconnected) {
          // Re-claim the conversation event channels FIRST, before any state
          // reload: a turn that survived the drop is still running and its
          // events have nowhere to go until this lands (KOA-18210). The claim
          // reads the live tabs, so it does not wait on the restore fetch.
          this.sendChatAttachForTabs();
          this.tryRestorePersistedState();
          // Force-refresh past the throttle on reconnect — the lifetime
          // breakdown is rarely moving but a top-up could have landed
          // during the outage, and a forced fetch is cheap.
          this.lastBalanceFetchAt = 0;
          void this.fetchBalance();
          // Re-fetch /config unconditionally on every reconnect. The
          // one-shot call in componentDidMount can land during a cold
          // start and silently fail; reconnect is the natural cue to
          // retry. We also refetch when serverProvider is already set
          // — a restart could have flipped LLM_PROVIDER (e.g. operator
          // switched from openrouter to openai-compatible for testing),
          // and stale provider state would leave the OpenRouter model-
          // override UI showing/hidden incorrectly until full reload.
          void this.fetchServerConfig();
        }
      });
    } else {
      this.setState({ isWsConnected: false });
      // Bridge not initialized yet (parent componentDidMount hasn't fired).
      // Retry shortly so we catch it once it's created.
      setTimeout(() => {
        if (this.isMounted_) this.setupBridgeConnectionListener();
      }, 100);
    }
  };

  fetchCapabilities = async () => {
    try {
      const res = await AiChatService.GetCapabilities();
      if (!this.isMounted_) return;
      if (res.data?.categories) this.setState({ capabilities: res.data.categories as unknown as CapabilityCategory[] });
    } catch {
      // Server not available yet
    }
  };

  fetchServerConfig = async () => {
    try {
      const res = await AiChatService.GetConfig();
      if (!this.isMounted_) return;
      // Provider is a numeric enum (0 = Anthropic, etc.) — guard
      // against falsy `0` skipping the setState. The reconnect-retry
      // hook below also uses `=== null` instead of `!serverProvider`
      // for the same reason.
      if (res.data?.provider !== undefined) this.setState({ serverProvider: res.data.provider });
      // Unconditional, unlike the line above: an older server omits the field
      // entirely, and that must read as "off". Leaving the previous value would
      // keep offering a control the server cannot serve.
      this.setState({ projectAnalysisAvailable: res.data?.projectAnalysis === true });
    } catch {
      // Server not available yet — model override UI stays hidden until provider is known
    }
  };

  /**
   * Cold-start /balance fetch — reads the lifetime breakdown the popover
   * needs (totalPurchasedCents, creditsConsumed, totalSpentUsd) and seeds
   * a credits value for the brief window before the first `balance_state`
   * push arrives. The server's `/balance` route reads from the same
   * BalanceManager projection that drives the WS push, so the two values
   * agree by construction. We do NOT poll on a timer — live credit
   * tracking is the WS stream's job.
   */
  /**
   * Soft min-gap between cold-start fetches. Lifetime fields
   * (totalPurchasedCents, creditsConsumed, totalSpentUsd) only move when a
   * payment lands or a turn finishes; toggling the panel rapidly should
   * not hammer Backend. Live credits are owned by the WS push.
   */
  private static readonly BALANCE_FETCH_MIN_GAP_MS = 5 * 60_000;
  private lastBalanceFetchAt = 0;
  fetchBalance = async (options?: { force?: boolean }): Promise<void> => {
    const force: boolean = options?.force === true;
    const user = getCurrentUser();
    const userId = user?.id;
    const userName = user?.username;
    if (!userId) return;
    if (!force && Date.now() - this.lastBalanceFetchAt < McpChatPanel.BALANCE_FETCH_MIN_GAP_MS) return;
    this.lastBalanceFetchAt = Date.now();
    try {
      // A forced fetch asks the server to forceSync (Backend re-read) rather
      // than serve its warm cache — required after a top-up so the credited
      // balance shows immediately instead of after the 60s server timer.
      const res = await AiChatService.GetBalance(userId, userName, force);
      if (!this.isMounted_) return;
      const data = res.data;
      if (!data) return;
      const hadBalanceCursor = this.latestBalanceCursor !== null;
      const currentDomain = getCurrentDomain();
      if (!hadBalanceCursor && typeof data.version === "number" && currentDomain) {
        this.latestBalanceCursor = {
          domainName: currentDomain,
          version: data.version,
          projectionId: data.projectionId,
          balanceRevision: data.balanceRevision
        };
      }
      this.setState((prev): Pick<McpChatPanelState, "remainingBalance" | "balanceInfo"> | null => {
        const freshBalance = data.creditsRemaining ?? 0;
        // HTTP /balance has no version — once the WS stream has delivered any
        // snapshot, prefer its value for `creditsRemaining` and only refresh
        // the lifetime breakdown from the HTTP response. A forced refresh (e.g.
        // right after a top-up) is the exception: the WS stream only pushes
        // after a billed turn, which the zero-balance send gate would otherwise
        // block — a deadlock — so a force must adopt the fresh durable value.
        const credits = hadBalanceCursor && !force ? (prev.remainingBalance ?? freshBalance) : freshBalance;
        return {
          remainingBalance: credits,
          balanceInfo: {
            creditsRemaining: credits,
            creditsConsumed: data.creditsConsumed ?? 0,
            totalPurchasedCents: data.totalPurchasedCents ?? 0,
            totalGrantedCredits: data.totalGrantedCredits ?? data.starterCreditsIncluded ?? 0,
            totalSpentUsd: data.totalSpentUsd ?? 0,
            starterCreditsIncluded: data.starterCreditsIncluded ?? 0
          }
        };
      });
    } catch {
      // Server not available yet
    }
  };

  // "Buy more credits" opens the credits UI in a centered modal. The chat panel
  // is hidden while the modal is up so it presents as a proper dialog (not a
  // drawer squeezed beside the open panel); it reopens when the modal closes.
  handleGoToTopUp = () => {
    this.setState({ isBuyCreditsOpen: true, isBalancePanelOpen: false });
    this.props.setIsMcpChatPanelVisible(false);
  };

  handleBuyCreditsClose = () => {
    this.setState({ isBuyCreditsOpen: false });
    this.props.setIsMcpChatPanelVisible(true);
    // A purchase may have just landed. The WS balance_state stream only pushes
    // after a billed turn — which the zero-balance send gate blocks — so a
    // top-up would otherwise stay invisible to the send gate (retry keeps
    // failing at the stale ≤0 value). Force a durable balance refresh on close
    // to release the gate. Harmless when nothing was purchased (plain re-read).
    void this.fetchBalance({ force: true });
  };

  /**
   * Handle a return from the Stripe top-up checkout (URL carries
   * `?session_id=cs_...`). In the in-app buy-credits flow Stripe redirects
   * back to the designer route where the credits panel is NOT mounted, so the
   * chat panel is the only component that can process the return — without
   * this the purchase isn't credited until the user manually reopens the
   * credits panel. Steps: scrub the param, POST verify (credits the ledger on
   * the Backend; idempotent per session_id, with a short retry for the async
   * "unpaid" window), then force a server-side balance resync so the pill
   * reflects the top-up immediately. Best-effort; failures leave the balance
   * to self-heal via the normal sync.
   */
  private handleStripeReturnIfPresent = async (): Promise<void> => {
    const params = new URLSearchParams(window.location.search);
    const sessionId: string | null = params.get("session_id");
    if (!sessionId) return;

    // Scrub immediately so a reload doesn't re-trigger (verify is idempotent
    // on session_id, but a clean URL is better UX).
    const url = new URL(window.location.href);
    url.searchParams.delete("session_id");
    window.history.replaceState({}, "", url.toString());

    const MAX_ATTEMPTS = 3;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        await AiPaymentService.VerifyPayment(sessionId);
        break;
      } catch (err: unknown) {
        const status: number | undefined = (err as { response?: { status?: number } })?.response?.status;
        const body = (err as { response?: { data?: { paymentStatus?: string } } })?.response?.data;
        // Async payment methods (3-D Secure, bank redirects) can report
        // "unpaid" right after redirect; retry with backoff. Any other error
        // (already-verified idempotent 200 won't throw; auth/ownership) stops.
        const isTransient: boolean = status === 400 && !!body?.paymentStatus && body.paymentStatus !== "paid";
        if (!isTransient || attempt === MAX_ATTEMPTS) break;
        await new Promise((resolve) => setTimeout(resolve, 1500 * attempt));
      }
    }

    if (this.isMounted_) await this.fetchBalance({ force: true });
  };

  /** Legacy full-page redirect to the settings credits route (modal fallback). */
  redirectToTopUp = () => {
    const domain = getCurrentDomain();
    if (domain) {
      try {
        sessionStorage.setItem("kuika.aiChatbotReturnUrl", window.location.pathname + window.location.search);
      } catch {
        // sessionStorage may be unavailable (e.g. private mode) — fall through; user just doesn't auto-return.
      }
      window.location.href = `/${domain}/settings/ai-chatbot-credits`;
    }
  };

  // ── Tab helpers ──

  createNewTab(): ChatTab {
    return {
      id: generateTabId(),
      name: `Chat ${getNextChatNumber(this.state?.tabs ?? [])}`,
      sessionId: null,
      isCustomName: false,
      messages: [],
      isLoading: false,
      isTextStreaming: false,
      inputValue: "",
      toolActivities: [],
      expandedCategory: null,
      attachedImages: [],
      attachedFiles: [],
      questionnaires: {},
      visibleMessageCount: MESSAGES_PAGE_SIZE,
      chatMode: "builder",
      effort: getStoredEffort(),
      planMode: false,
      selectedElementAttachments: [],
      queuedMessages: [],
      mentions: [],
      mentionCounter: 0,
      isImprovingPrompt: false,
      improveRequestId: null,
      improveOriginalPrompt: null,
      planJson: null,
      effectiveCapabilityProfile: null,
      capabilityReason: null,
      capabilityPromotedGroups: null,
      capabilityDenialMessage: null,
      phaseTitle: null,
      toolTitle: null
    };
  }

  getActiveTab(): ChatTab {
    return this.state.tabs.find((t) => t.id === this.state.activeTabId)!;
  }

  updateActiveTab(updater: (tab: ChatTab) => Partial<ChatTab>, callback?: () => void) {
    this.updateTab(this.state.activeTabId, updater, callback);
  }

  /**
   * Name the build stage from a `routing` event.
   *
   * Only events carrying a known stage CODE render. The same event name also
   * carries long retrospective narration — much of it reporting failures the
   * build is already recovering from — which has no `phase` and is ignored, the
   * way it was before this feature existed.
   *
   * Setting a stage clears the tool line: the tool that was running belonged to
   * the stage that just ended.
   */
  notePhase(tabId: string, data: unknown): void {
    const label: string | null = phaseLabelFromEvent(data);
    if (label === null) return;
    this.updateTab(tabId, () => ({ phaseTitle: label, toolTitle: null }));
  }

  /**
   * Name the backend tool running right now.
   *
   * Backend tools are hidden from the transcript (KOA-17717 — a pill per tool
   * would be noise), so they have no group to label the line with. Their phrase
   * goes to `toolTitle`, which the transcript renders as its own label without
   * creating an entry. Browser tools return undefined here: their own group
   * already labels that line, and two sources must not fight over it.
   */
  noteBackendToolTitle(tabId: string, data: StreamToolActivityData): void {
    const label: string | undefined = this.backendToolPhaseTitle(data);
    if (label === undefined) return;
    this.updateTab(tabId, () => ({ toolTitle: label }));
  }

  /**
   * Clear the phase title as a turn begins, so a finished turn's last phase can
   * never be shown as the next turn's first.
   *
   * Done here rather than at each of the dozen turn-end sites: the indicator
   * renders only while `isLoading`, so a lingering title is invisible until the
   * next turn starts — which is exactly when this runs.
   */
  beginTurnProgress(tabId: string): void {
    this.updateTab(tabId, () => ({ phaseTitle: null, toolTitle: null }));
  }

  /**
   * Header text for a backend tool the transcript deliberately does not show.
   *
   * Backend tools carry `_notificationOnly` (KOA-17717) so they never become
   * transcript pills, which left the Backend Architect's longest work with no
   * visible signal at all. They have nowhere else to speak, so their label goes
   * to the header; the next turn-start restores the wave phase over it.
   *
   * Browser-executed UI tools return undefined here on purpose — their label
   * already shows on the transcript line below, and overwriting the header with
   * it would throw away the phase ("Designing ProductList (2 of 5)"). They still
   * refresh the activity clock, since undefined means "clock only".
   *
   * The phrase comes from the panel's existing tool-label table; no second
   * mapping is introduced.
   */
  backendToolPhaseTitle(data: StreamToolActivityData): string | undefined {
    if (data?._notificationOnly !== true) return undefined;
    if (typeof data.toolName !== "string" || data.toolName.length === 0) return undefined;
    // Same chain the transcript uses for browser tools, so both sources of this
    // line read alike. `getToolDescription` is the richer of the two — it reads
    // the tool's own arguments ("Creating table 'Sikayetler'", "Saving 7 SQL
    // actions") and ends in a humanising fallback, so an unmapped tool still
    // never renders a raw name.
    return getToolGroupLabel(data.toolName, data.parameters) ?? getToolDescription(data.toolName, data.parameters);
  }

  updateTab(tabId: string, updater: (tab: ChatTab) => Partial<ChatTab>, callback?: () => void) {
    if (!this.isMounted_) return;
    this.setState(
      (prev) => ({
        tabs: prev.tabs.map((t) => (t.id === tabId ? withCappedMessages({ ...t, ...updater(t) }) : t))
      }),
      () => {
        callback?.();
        this.scheduleSave();
      }
    );
  }

  scheduleSave = () => {
    // Skip save while streaming — finalize() will trigger a save when done
    if (this.activeChatRequestIds.size > 0) return;

    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this.flushSave();
    }, this.SAVE_DEBOUNCE_MS);
  };

  /** Dispatch a save and hand the pending archive set off atomically.
   *  Saves under `this.loadedProjectId` — the id of the project whose tabs
   *  are currently in memory — NOT `window.kuika.activeProjectId`. The two
   *  diverge during a project switch: ProjectProvider flips the global
   *  before dispatching `kuika-project-loaded`, so reading the global here
   *  would write the old project's in-memory tabs under the new project's
   *  row and corrupt its history. */
  private flushSave = (): void => {
    if (!this.loadedProjectId) return;
    const archivedIds = this.pendingArchiveTabIds.size > 0 ? Array.from(this.pendingArchiveTabIds) : undefined;
    this.pendingArchiveTabIds.clear();
    saveChatState(this.loadedProjectId, this.state.tabs, this.state.activeTabId, archivedIds);
  };

  // ── Tab management ──

  handleAddTab = () => {
    if (this.state.tabs.length >= MAX_TABS) return;
    const newTab = this.createNewTab();
    this.setState(
      (prev) => ({ tabs: [...prev.tabs, newTab], activeTabId: newTab.id }),
      () => {
        this.inputAreaRef.current?.focus();
        this.scheduleSave();
      }
    );
  };

  /**
   * Open a NEW chat tab and run a structured AI validation-fix task in it
   * (footer "Fix with AI" / "Fix all with AI"). Reuses the full handleSend
   * streaming/finalize path; forces designer mode so the server routes to the
   * ValidationFixCoordinator (its branch is after the ASK/PLAN short-circuits).
   */
  sendValidationFixRequest = (items: ValidationFixItemPayload[]): void => {
    if (items.length === 0) return;
    const message: string =
      items.length === 1 ? `Fix validation: ${items[0].message}` : `Fix ${items.length} validation issues`;
    const send = (): void => {
      void this.handleSend(message, undefined, undefined, { intent: "fix_validations", validationFixItems: items });
    };

    // At the tab cap, run the fix in the current tab (the forced-designer override
    // in handleSend still protects mode regardless of the active tab's chatMode).
    if (this.state.tabs.length >= MAX_TABS) {
      send();
      return;
    }

    // Open a fresh tab and send from the setState callback — this guarantees the
    // new tab is the active one when handleSend reads getActiveTab(), eliminating
    // the flush-timing guess + tab-switch race a bare setTimeout would leave.
    // Fix-with-AI runs in BUILDER mode (full capability) — the request still
    // hits the fix_validations bypass branch (intent-based, before mode routing),
    // but the tab + turn show builder, not the designer default.
    const newTab: ChatTab = { ...this.createNewTab(), chatMode: "builder" };
    this.setState(
      (prev) => ({ tabs: [...prev.tabs, newTab], activeTabId: newTab.id }),
      () => {
        this.inputAreaRef.current?.focus();
        this.scheduleSave();
        send();
      }
    );
  };

  /**
   * Run the project-analysis (discovery) walk — the composer's "Analyze project"
   * button and the `/analyze` command.
   *
   * The message text is a label for the transcript, not an instruction: the
   * server routes on `intent`, before anything reads the words. It is therefore
   * safe (and deliberate) that this string is never classified.
   *
   * The walk is billed per screen, so nothing is spent here — the server quotes
   * it first and asks for confirmation with the real numbers before running.
   * Reuses the full handleSend streaming/finalize path.
   */
  sendProjectAnalysisRequest = (): void => {
    const message: string = "Analyze this project";
    const send = (): void => {
      void this.handleSend(message, undefined, undefined, { intent: "analyze_project" });
    };

    // At the tab cap, run it in the current tab. Otherwise open a fresh one —
    // the report is long, and burying it under an in-progress conversation is
    // how a paid artifact gets lost. Sent from the setState callback so the new
    // tab is definitely active when handleSend reads getActiveTab().
    if (this.state.tabs.length >= MAX_TABS) {
      send();
      return;
    }
    const newTab: ChatTab = this.createNewTab();
    this.setState(
      (prev) => ({ tabs: [...prev.tabs, newTab], activeTabId: newTab.id }),
      () => {
        this.inputAreaRef.current?.focus();
        this.scheduleSave();
        send();
      }
    );
  };

  handleCloseTab = (tabId: string) => {
    const { tabs, activeTabId } = this.state;
    if (tabs.length <= 1) return;
    if (this.state.isInspectorActive && activeTabId === tabId) {
      this.deactivateInspector(true, false);
    }

    const closingTab = tabs.find((t) => t.id === tabId);
    if (closingTab) {
      this.clearSession(closingTab.sessionId);
      // Fire the archive POST immediately so a quick history-modal open
      // right after close can already see the row. The save path also
      // includes this id via `pendingArchiveTabIds` as a race-free fallback
      // in case the POST hasn't committed by the time a beacon fires.
      this.pendingArchiveTabIds.add(tabId);
      archiveTab(this.loadedProjectId, tabId);
    }

    const idx = tabs.findIndex((t) => t.id === tabId);
    const newTabs = tabs.filter((t) => t.id !== tabId);
    let newActiveId = activeTabId;

    if (activeTabId === tabId) {
      const newIdx = Math.min(idx, newTabs.length - 1);
      newActiveId = newTabs[newIdx].id;
    }

    this.setState({ tabs: newTabs, activeTabId: newActiveId }, () => {
      this.inputAreaRef.current?.focus();
      this.forceScrollToBottom();
      this.scheduleSave();
    });
  };

  handleSwitchTab = (tabId: string) => {
    if (tabId === this.state.activeTabId) return;
    if (this.state.isInspectorActive) {
      this.deactivateInspector(true, false);
    }
    // Save current input to outgoing tab
    const outgoingTabId = this.state.activeTabId;
    const outgoingInputValue = this.currentInputValue;
    // Reset history browsing
    this.historyIndex = -1;
    this.historyDraft = "";
    // Load input from incoming tab
    this.loadInputFromTab(tabId);
    this.setState(
      (prev) => ({
        activeTabId: tabId,
        tabs: prev.tabs.map((t) => (t.id === outgoingTabId ? { ...t, inputValue: outgoingInputValue } : t))
      }),
      () => {
        this.updateTab(tabId, () => ({ visibleMessageCount: MESSAGES_PAGE_SIZE }));
        this.inputAreaRef.current?.focus();
        this.forceScrollToBottom();
      }
    );
  };

  handleRenameTab = (tabId: string, newName: string) => {
    this.updateTab(tabId, () => ({ name: newName, isCustomName: true }));
  };

  generateTabTitle = (tabId: string, message: string, sessionIdOverride?: string) => {
    const bridge = getMcpBridgeInstance();
    if (!bridge || !bridge.isConnected) return;

    const requestId = `title_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    bridge.onChatEvent(requestId, (evt: ChatEventData) => {
      bridge.offChatEvent(requestId);
      if (!this.isMounted_) return;

      const title = evt.data?.title;
      if (!title) return;

      const tab = this.state.tabs.find((t) => t.id === tabId);
      if (!tab || tab.isCustomName) return;

      const sanitized = title.length > 50 ? title.slice(0, 50) + "..." : title;
      this.updateTab(tabId, () => ({ name: sanitized }));
    });

    // Prefer the caller-supplied sessionId (handleSend mints one before the
    // tab's setState commits) so the title call attribution lines up with
    // the first chat request's session. Falling back to the tab's sessionId
    // keeps older callers working, but without a sessionId the server aux
    // fallback collapses every title call into a single "aux-title" session
    // row — avoid it by always passing the minted id from handleSend.
    const tab = this.state.tabs.find((t) => t.id === tabId);
    const sessionId = sessionIdOverride || tab?.sessionId;
    bridge.sendGenerateTitle(requestId, message, tab?.chatMode, buildAuxCallContext(sessionId));
  };

  handleCategoryClick = (categoryId: string) => {
    this.updateActiveTab((tab) => ({
      expandedCategory: tab.expandedCategory === categoryId ? null : categoryId
    }));
  };

  handleSuggestionClick = (suggestion: string) => {
    this.updateActiveTab(() => ({ expandedCategory: null }));
    this.handleSend(suggestion);
  };

  handleRateResponse = async (chatRequestId: string, rating: number, reason?: string) => {
    const activeTab = this.getActiveTab();

    // Update message state with userRating (pins the footer's rated state,
    // persists across reload)
    this.updateTab(activeTab.id, (tab) => {
      const msgs = [...tab.messages];
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].chatRequestId === chatRequestId) {
          msgs[i] = { ...msgs[i], userRating: rating };
          break;
        }
      }
      return { messages: msgs };
    });

    try {
      await AiChatService.SubmitRating({
        request_id: chatRequestId,
        session_id: activeTab.sessionId || "",
        tab_id: activeTab.id,
        user_id: getCurrentUser()?.id || "",
        project_id: getActiveProjectId() || "",
        rating,
        ...(reason ? { reason } : {})
      });
    } catch (err) {
      console.warn("[MCP Chat] Rating submission failed:", err);
    }
  };

  // Canvas-zone tool prefixes — activate canvas glow on first match per session
  private static CANVAS_TOOL_PREFIXES = ["ui_component", "ui_style", "ui_history", "ui_render"];

  /**
   * DDL-producing operations on the `database_schema` backend tool.
   * A successful call with one of these operations modifies the live DB
   * schema — a side effect the draft-discard flow CAN'T revert (schema
   * lives outside KProjectStagedChange). Tracked so the restore dialog
   * can warn the user that these changes will persist after rewind.
   */
  private static DDL_SCHEMA_OPERATIONS = new Set([
    "create_table",
    "drop_table",
    "rename_table",
    "add_column",
    "drop_column",
    "rename_column",
    "alter_column"
  ]);

  private getToolActivityKey = (activity: Pick<ToolActivity, "requestId" | "toolCallId">): string =>
    getToolActivityIdentity(activity);

  private resetToolActivityState = (tabId: string): void => {
    this.archivedToolActivities.delete(tabId);
    this.finalizedAssistantMessageIds.delete(tabId);
  };

  private recordToolActivity = (tabId: string, activity: ToolActivity): void => {
    const nextActivities = mergeToolActivityCollections(this.archivedToolActivities.get(tabId) || [], [activity]);
    this.archivedToolActivities.set(tabId, nextActivities);
  };

  private getArchivedToolActivities = (tabId: string): ToolActivity[] => this.archivedToolActivities.get(tabId) || [];

  private getFinalToolActivities = (tabId: string, liveActivities: ToolActivity[]): ToolActivity[] =>
    mergeToolActivityCollections(this.getArchivedToolActivities(tabId), liveActivities);

  private normalizeToolActivityStatus = (status: unknown): "running" | "completed" | "error" => {
    if (status === "running" || status === "error") return status;
    return "completed";
  };

  private stringifyToolActivityError = (value: unknown): string | undefined => {
    if (typeof value === "string") return value;
    if (value && typeof value === "object" && "error" in value && (value as { error?: unknown }).error != null) {
      return String((value as { error?: unknown }).error);
    }
    if (value == null) return undefined;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  private mergeToolActivitiesIntoAssistantMessage = (
    messages: McpChatMessage[],
    activities: CompletedToolActivity[],
    targetMessageId?: string
  ): { messages: McpChatMessage[]; messageId?: string } => {
    if (activities.length === 0) {
      return { messages, messageId: targetMessageId };
    }

    const nextMessages = [...messages];
    let targetIndex = -1;

    if (targetMessageId) {
      targetIndex = nextMessages.findIndex(
        (message) => message.id === targetMessageId && message.role === "assistant" && !message.isError
      );
    }

    if (targetIndex < 0) {
      let lastUserIndex = -1;
      for (let i = nextMessages.length - 1; i >= 0; i--) {
        if (nextMessages[i].role === "user") {
          lastUserIndex = i;
          break;
        }
      }

      for (let i = nextMessages.length - 1; i > lastUserIndex; i--) {
        if (nextMessages[i].role === "assistant" && !nextMessages[i].isError) {
          targetIndex = i;
          break;
        }
      }
    }

    if (targetIndex >= 0) {
      const existing = nextMessages[targetIndex];
      const mergedActivities = mergeCompletedToolActivities(existing.completedToolActivities, activities);
      nextMessages[targetIndex] = {
        ...existing,
        completedToolActivities: mergedActivities
      };
      return { messages: nextMessages, messageId: existing.id };
    }

    const messageId = generateMessageId();
    nextMessages.push({
      id: messageId,
      role: "assistant",
      content: "",
      timestamp: Date.now(),
      completedToolActivities: activities
    });

    return { messages: nextMessages, messageId };
  };

  private attachLateToolActivities = (tabId: string, activities: ToolActivity[]): void => {
    const completedToolActivities = buildCompletedToolActivitiesFromActivities(activities);
    if (!completedToolActivities?.length) return;

    let mergedMessageId: string | undefined;
    this.updateTab(
      tabId,
      (tab) => {
        const merged = this.mergeToolActivitiesIntoAssistantMessage(
          tab.messages,
          completedToolActivities,
          this.finalizedAssistantMessageIds.get(tabId)
        );
        mergedMessageId = merged.messageId;
        return merged.messages === tab.messages ? {} : { messages: merged.messages };
      },
      () => {
        if (mergedMessageId) {
          this.finalizedAssistantMessageIds.set(tabId, mergedMessageId);
        }
      }
    );
  };

  private getToolActivityTargetTab = (detail: McpToolActivityEvent): ChatTab | undefined => {
    if (detail.tabId) {
      const tab = this.state.tabs.find((t) => t.id === detail.tabId);
      if (tab) return tab;
    }
    const activeTab = this.getActiveTab();
    return activeTab.isLoading ? activeTab : this.state.tabs.find((t) => t.isLoading);
  };

  private dispatchStreamToolActivity = (tabId: string, data: StreamToolActivityData): void => {
    if (!data?.toolName) return;

    // KOA-17717 kept these off the transcript because a pill per backend tool
    // would be noise the user never had before. That still holds: they feed the
    // header line only (see the tool_activity branches), never a transcript row.
    // Guarding in the shared dispatcher rather than at each branch means a new
    // call site cannot reintroduce the pills by forgetting the check.
    if (data._notificationOnly === true) return;

    // Phase-blocked tools (e.g. generate_guids during backend_setup) are
    // expected behavior, not failures — hide them from the UI entirely.
    // On localhost we keep them visible so developers can debug routing.
    const isLocalhost: boolean = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    if (!isLocalhost) {
      if (data.status === "blocked") return;
      if (data.status === "error" && typeof data.result === "string" && data.result.includes("[BLOCKED]")) return;
      // Failed tool calls are internal mechanics — render retries, additive-guard
      // rejections, specialist re-emits, etc. The build still completes via the
      // remaining steps and the final assistant message conveys the outcome, so
      // these ✗ entries are noise to end users on deployed environments. Devs on
      // localhost keep them visible for debugging.
      if (data.status === "error") return;
    }

    const input = data.parameters && typeof data.parameters === "object" ? data.parameters : {};
    const status = this.normalizeToolActivityStatus(data.status);
    const toolCallId = typeof data.toolCallId === "string" && data.toolCallId ? data.toolCallId : undefined;

    const detail: McpToolActivityEvent = {
      requestId: toolCallId || `${tabId}:${data.toolName}:${String(input.operation || Date.now())}`,
      toolCallId,
      tabId,
      toolName: data.toolName,
      input,
      status,
      isMutating: !isReadOnlyTool(data.toolName, input),
      timestamp: Date.now(),
      duration: typeof data.duration === "number" ? data.duration : undefined,
      result: data.result,
      error: status === "error" ? this.stringifyToolActivityError(data.result) : undefined
    };

    window.dispatchEvent(new CustomEvent(MCP_TOOL_ACTIVITY_EVENT, { detail }));
  };

  /**
   * Synthesize a specialist-run `McpToolActivityEvent` from a server
   * `specialist_activity` payload. This routes Wirer/Binder/Designer/
   * Backend-Architect/Render-Fixer runs through the existing
   * `tab.toolActivities` pipeline so they appear as entries inside the
   * live tool transcript (sparkle icon, "Specialist" eyebrow, group-by-
   * agent with a run count) instead of spawning standalone assistant
   * bubbles.
   *
   * `activityKey` comes from the server (stable per (agent,turn,requestId))
   * and doubles as the `toolCallId` so running → completed transitions
   * reconcile onto the same entry. Same-agent different-turn runs get
   * distinct keys; the group key drops the turn so all turns of one
   * agent collapse into a single group with a run count.
   */
  private dispatchStreamSpecialistActivity = (tabId: string, data: StreamSpecialistActivityData): void => {
    if (!data?.agent || !data?.activityKey) return;

    // Mirror dispatchStreamToolActivity: a failed specialist run (Designer/
    // Wirer/Binder/… retry, bounce-back, additive-guard rejection) is internal
    // mechanics — the build completes via the remaining turns and the final
    // assistant message conveys the outcome. Hide these ✗ specialist entries
    // from end users on deployed environments; keep them on localhost so
    // developers can debug the pipeline.
    const isLocalhostSpecialist: boolean =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    if (!isLocalhostSpecialist && data.status === "error") return;

    const status: "running" | "completed" | "error" =
      data.status === "running" || data.status === "completed" || data.status === "error" ? data.status : "running";

    const detail: McpToolActivityEvent = {
      requestId: data.activityKey,
      toolCallId: data.activityKey,
      tabId,
      toolName: `specialist:${data.agent}`,
      input: { operation: "run", turn: typeof data.turn === "number" ? data.turn : 0 },
      status,
      isMutating: false,
      timestamp: Date.now(),
      result: status === "completed" || status === "error" ? data.accumulated : undefined,
      error: status === "error" && typeof data.accumulated === "string" ? data.accumulated : undefined,
      specialistAccumulated: typeof data.accumulated === "string" ? data.accumulated : undefined
    };

    window.dispatchEvent(new CustomEvent(MCP_TOOL_ACTIVITY_EVENT, { detail }));
  };

  handleToolActivity = (event: CustomEvent<McpToolActivityEvent>) => {
    const detail = event.detail;
    const targetTab = this.getToolActivityTargetTab(detail);
    if (!targetTab) return;

    const activityKey = detail.toolCallId || detail.requestId;
    const status = this.normalizeToolActivityStatus(detail.status);
    let resolvedActivity: ToolActivity | undefined;
    let isNewActivity = false;

    // Activate canvas zone on first canvas-zone tool in this session
    if (
      status === "running" &&
      !this.canvasZoneActiveTabs.has(targetTab.id) &&
      McpChatPanel.CANVAS_TOOL_PREFIXES.some((p) => detail.toolName.startsWith(p))
    ) {
      this.canvasZoneActiveTabs.add(targetTab.id);
      window.dispatchEvent(new CustomEvent(MCP_ZONE_ACTIVATE, { detail: { zone: "canvas" } }));
    }

    this.updateTab(
      targetTab.id,
      (tab) => {
        const now = performance.now();
        const nextActivities = [...tab.toolActivities];
        const existingIndex = nextActivities.findIndex((activity) => this.getToolActivityKey(activity) === activityKey);
        const existing = existingIndex >= 0 ? nextActivities[existingIndex] : undefined;
        let newActivityStartTime: number | undefined;
        if (status === "running") {
          newActivityStartTime = now;
        } else if (typeof detail.duration === "number") {
          newActivityStartTime = now - detail.duration;
        }

        const nextActivity: ToolActivity = existing
          ? {
              ...existing,
              requestId: existing.requestId || detail.requestId,
              toolCallId: detail.toolCallId || existing.toolCallId,
              toolName: detail.toolName,
              input: detail.input || existing.input,
              status,
              isMutating: detail.isMutating,
              description: createToolActivity({
                requestId: existing.requestId || detail.requestId,
                toolCallId: detail.toolCallId || existing.toolCallId,
                toolName: detail.toolName,
                input: detail.input || existing.input,
                status,
                isMutating: detail.isMutating,
                result: detail.result ?? existing.result,
                error: detail.error ?? existing.error,
                specialistAccumulated: detail.specialistAccumulated ?? existing.specialistAccumulated
              }).description,
              result: detail.result ?? existing.result,
              error: detail.error ?? existing.error,
              specialistAccumulated: detail.specialistAccumulated ?? existing.specialistAccumulated,
              startTime: existing.startTime ?? (status === "running" ? now : undefined),
              duration:
                status === "running"
                  ? existing.duration
                  : (detail.duration ?? (existing.startTime ? now - existing.startTime : existing.duration))
            }
          : {
              ...createToolActivity({
                requestId: detail.requestId,
                toolCallId: detail.toolCallId,
                toolName: detail.toolName,
                input: detail.input,
                status,
                isMutating: detail.isMutating,
                result: detail.result,
                error: detail.error,
                specialistAccumulated: detail.specialistAccumulated
              }),
              startTime: newActivityStartTime,
              duration: status === "running" ? undefined : detail.duration
            };

        resolvedActivity = nextActivity;

        if (existingIndex >= 0) {
          nextActivities[existingIndex] = nextActivity;
        } else {
          nextActivities.push(nextActivity);
          isNewActivity = true;
        }

        // Ensure loading indicator stays visible while tools are running.
        // Browser-dispatched events (e.g. ui_render sub-calls via WsBridge)
        // bypass the server event handler that normally sets isLoading: true.
        return status === "running"
          ? { toolActivities: nextActivities, isLoading: true }
          : { toolActivities: nextActivities };
      },
      () => {
        if (resolvedActivity) {
          this.recordToolActivity(targetTab.id, resolvedActivity);
          const updatedTab = this.state.tabs.find((tab) => tab.id === targetTab.id);
          if (status !== "running" && updatedTab && !updatedTab.isLoading) {
            this.attachLateToolActivities(targetTab.id, [resolvedActivity]);
          }
        }

        // Snap to the bottom only when a genuinely NEW tool activity appears.
        // Progressive status updates (running → completed, heartbeat `running`
        // re-emits, duration ticks) don't need an explicit pin — the row's
        // height change is caught by the content ResizeObserver, which keeps
        // the bottom glued throughout the expand. `scrollToBottom` (→ pinIfStuck)
        // is a no-op when the user has scrolled up.
        if (status === "running" && isNewActivity) {
          this.scrollToBottom();
        }
      }
    );

    if (status === "completed" && detail.toolName === "ui_project") {
      const roleMutations = ["create_role", "update_role", "delete_role"];
      if (roleMutations.includes(detail.input?.operation)) {
        McpToolExecutor.execute("ui_project", { operation: "reload_project" });
      }
    }

    // Track DDL operations against the current user message so the restore
    // dialog can warn that schema changes are NOT covered by the draft-discard
    // revert. Only records on success — failed DDL didn't change anything.
    if (status === "completed" && this.state.rewindEnabled === true) {
      this.recordDdlIfApplicable(targetTab.id, detail);
    }
  };

  private recordDdlIfApplicable = (tabId: string, detail: McpToolActivityEvent): void => {
    if (detail.toolName !== "database_schema") return;
    const operation = typeof detail.input?.operation === "string" ? detail.input.operation : undefined;
    if (!operation || !McpChatPanel.DDL_SCHEMA_OPERATIONS.has(operation)) return;

    // Result of a failed op is returned as `{ error: true, ... }` — skip those.
    const resultObj = detail.result as DdlToolResult | undefined;
    if (resultObj && resultObj.error) return;

    const input = detail.input as DdlToolInput;
    const tableName = input.table?.name || input.tableName;
    const target =
      operation === "rename_table" && input.newName
        ? `${tableName || ""} → ${input.newName}`
        : operation === "rename_column"
          ? `${tableName || ""}.${input.oldName || ""} → ${input.newName || ""}`
          : operation === "drop_column" || operation === "add_column" || operation === "alter_column"
            ? `${tableName || ""}.${input.columnName || ""}`.replace(/\.$/, "")
            : tableName;

    const ddlOp: DdlOp = {
      toolName: "database_schema",
      operation,
      target: target || undefined
    };

    this.updateTab(tabId, (tab) => {
      let lastUserIdx = -1;
      for (let i = tab.messages.length - 1; i >= 0; i--) {
        if (tab.messages[i].role === "user") {
          lastUserIdx = i;
          break;
        }
      }
      if (lastUserIdx < 0) return {};
      const userMsg = tab.messages[lastUserIdx];
      const nextDdl = [...(userMsg.ddlOps || []), ddlOp];
      const nextMessages = [...tab.messages];
      nextMessages[lastUserIdx] = { ...userMsg, ddlOps: nextDdl };
      return { messages: nextMessages };
    });
  };

  handleMessagesScroll = () => {
    const container = this.messagesContainerRef.current;
    if (!container) return;
    const scrollTop: number = container.scrollTop;
    const scrollHeight: number = container.scrollHeight;
    const gap: number = scrollHeight - scrollTop - container.clientHeight;

    // A drop in scrollTop can only come from the user — our pins only ever push
    // it toward the bottom. So an upward move detaches the stick flag, which
    // stops the next content-growth tick from yanking the user back down while
    // they read a running tool. Ignore the browser's automatic scrollTop clamp
    // when content shrinks (a collapse), which would otherwise read as a fake
    // upward scroll.
    const shrank: boolean = scrollHeight < this.lastScrollHeight;
    if (!shrank && scrollTop < this.lastScrollTop - 1) {
      this.isUserNearBottom = false;
    }
    this.lastScrollTop = scrollTop;
    this.lastScrollHeight = scrollHeight;

    // Re-attach only once they're genuinely back at the very bottom (hysteresis
    // vs SCROLL_THRESHOLD) so a small read-up doesn't immediately re-stick.
    if (gap <= McpChatPanel.STICK_REATTACH_GAP) {
      this.isUserNearBottom = true;
    }

    const shouldShow: boolean = gap > McpChatPanel.SCROLL_THRESHOLD;
    if (this.state.showScrollToBottom !== shouldShow) {
      this.setState({ showScrollToBottom: shouldShow });
    }
  };

  /** Wheel-up is an unambiguous "let me read up" signal. Detach synchronously —
   *  the wheel event fires before the resulting scroll event, so the next
   *  ResizeObserver pin is suppressed immediately, with no one-frame yank. */
  handleMessagesWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (e.deltaY < 0) this.isUserNearBottom = false;
  };

  /** Queue a tab update to be flushed once per rAF instead of immediately. */
  private batchTabUpdate = (tabId: string, patch: Partial<ChatTab> | ((t: ChatTab) => Partial<ChatTab>)) => {
    this.pendingTabPatches.push({ tabId, patch });
    if (this.patchRafId == null) {
      this.patchRafId = requestAnimationFrame(this.flushPendingTabPatches);
    }
  };

  private flushPendingTabPatches = () => {
    this.patchRafId = null;
    if (!this.isMounted_ || this.pendingTabPatches.length === 0) return;

    const patches = this.pendingTabPatches.slice();
    this.pendingTabPatches.length = 0;

    this.setState((prev) => {
      let tabs = prev.tabs;
      for (const { tabId, patch } of patches) {
        tabs = tabs.map((t) => {
          if (t.id !== tabId) return t;
          const resolved = typeof patch === "function" ? patch(t) : patch;
          return Object.keys(resolved).length > 0 ? withCappedMessages({ ...t, ...resolved }) : t;
        });
      }
      return { tabs };
    });
  };

  /** Debounce window for surfacing the disconnected banner — cleared on reconnect
   *  so transient drops never reach the user. */
  private disconnectedBannerTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * One-shot pin request from event handlers (tool_activity, model_delta,
   * plan_progress, message append). Continuous growth — including the whole
   * 320ms expand transition — is already kept glued by the content
   * ResizeObserver; this just snaps to the bottom at the instant an event lands
   * instead of waiting for the next resize tick. No-op when the user scrolled up.
   */
  scrollToBottom = () => {
    this.pinIfStuck();
  };

  /**
   * Explicit re-stick to the bottom: the scroll-to-bottom button and the start
   * of every user-initiated turn (send / interrupt-approve / questionnaire-submit).
   * INSTANT, not smooth. A smooth animation targets scrollHeight-at-call-time,
   * but the assistant starts streaming immediately so the true bottom moves past
   * that target: a concurrent pin jumps to the real bottom and the smooth
   * animation then drags scrollTop back toward its stale, smaller target — which
   * reads as a user scroll-up and falsely detaches, stranding the panel mid-list.
   * An instant jump keeps scrollTop monotonic toward the bottom, which is exactly
   * what the detach heuristic in handleMessagesScroll relies on.
   */
  forceScrollToBottom = () => {
    this.isUserNearBottom = true;
    this.setState({ showScrollToBottom: false });
    const container = this.messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      this.lastScrollTop = container.scrollTop;
      this.lastScrollHeight = container.scrollHeight;
    }
  };

  getVisibleMessages(): { visibleMessages: McpChatMessage[]; hiddenCount: number } {
    const tab = this.getActiveTab();
    const messages = tab.messages;
    const count = tab.visibleMessageCount;

    // Return cached result if inputs haven't changed (reference equality)
    if (
      this._cachedVisibleMsgsInput &&
      this._cachedVisibleMsgsInput.messages === messages &&
      this._cachedVisibleMsgsInput.count === count &&
      this._cachedVisibleMsgs
    ) {
      return { visibleMessages: this._cachedVisibleMsgs, hiddenCount: this._cachedHiddenCount };
    }

    const total = messages.length;
    const visible = Math.min(count, total);
    const visibleMessages = messages.slice(total - visible);
    const hiddenCount = total - visible;

    this._cachedVisibleMsgsInput = { messages, count };
    this._cachedVisibleMsgs = visibleMessages;
    this._cachedHiddenCount = hiddenCount;

    return { visibleMessages, hiddenCount };
  }

  handleLoadMore = () => {
    const container = this.messagesContainerRef.current;
    if (!container) return;
    const prevScrollHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;
    this.updateActiveTab(
      (tab) => ({ visibleMessageCount: tab.visibleMessageCount + MESSAGES_PAGE_SIZE }),
      () => {
        const heightDelta = container.scrollHeight - prevScrollHeight;
        container.scrollTop = prevScrollTop + heightDelta;
      }
    );
  };

  renderLoadMoreButton = (hiddenCount: number) => {
    const nextBatch = Math.min(hiddenCount, MESSAGES_PAGE_SIZE);
    return (
      <button className="mcp-chat__load-more" onClick={this.handleLoadMore}>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="18 15 12 9 6 15" />
        </svg>
        Load {nextBatch} earlier messages
        <span className="mcp-chat__load-more-badge">{hiddenCount}</span>
      </button>
    );
  };

  // ── Input area callbacks ──

  /** Persist the current input value into the active tab's state (for persistence/restore). */
  private syncInputToTab = () => {
    const activeTab = this.getActiveTab();
    if (activeTab && activeTab.inputValue !== this.currentInputValue) {
      this.updateActiveTab(() => ({ inputValue: this.currentInputValue }));
    }
  };

  /** Load the input value from a tab into the instance variable. */
  private loadInputFromTab = (tabId: string) => {
    const tab = this.state.tabs.find((t) => t.id === tabId);
    this.currentInputValue = tab?.inputValue ?? "";
  };

  handleInputChange = (value: string) => {
    this.currentInputValue = value;
    if (this.historyIndex !== -1) {
      this.historyIndex = -1;
      this.historyDraft = "";
    }
  };

  /** Programmatically set the input value from external callers (e.g. "Fix with AI"). */
  setInputValue = (value: string) => {
    this.currentInputValue = value;
    this.updateActiveTab(
      () => ({ inputValue: value }),
      () => {
        this.inputAreaRef.current?.setContent(value);
        this.inputAreaRef.current?.focus();
      }
    );
  };

  handleMentionsChange = (mentions: MentionReference[], counter: number) => {
    this.updateActiveTab(() => ({ mentions, mentionCounter: counter }));
  };

  handleAttachImages = (newImages: AttachedImage[]) => {
    this.updateActiveTab((tab) => ({
      attachedImages: [...tab.attachedImages, ...newImages].slice(0, MAX_ATTACHED_IMAGES)
    }));
  };

  handleRemoveImage = (imageId: string) => {
    this.updateActiveTab((tab) => ({
      attachedImages: tab.attachedImages.filter((img) => img.id !== imageId)
    }));
  };

  handleAttachFiles = (newFiles: AttachedFile[]) => {
    this.updateActiveTab((tab) => ({
      attachedFiles: [...tab.attachedFiles, ...newFiles].slice(0, MAX_ATTACHED_FILES)
    }));
  };

  handleRemoveFile = (fileId: string) => {
    this.updateActiveTab((tab) => ({
      attachedFiles: tab.attachedFiles.filter((f) => f.id !== fileId)
    }));
  };

  normalizeQuestionPrompt = (prompt: string, index: number): string => {
    const compact = prompt
      .replace(/\r?\n+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    const normalized = compact.replace(/[:?]\s*$/, "").trim();
    return normalized || `Question ${index + 1}`;
  };

  handleQuestionOptionSelect = (
    tabId: string,
    messageId: string,
    questionIndex: number,
    value: string,
    questionCount: number,
    question?: ParsedQuestionBlock
  ) => {
    // Instant-submit escape: an option flagged `submitImmediately` (e.g. the
    // Business Analyst's build-as-is choice) ends the Q&A in one click — record
    // the pick and send just this answer, skipping every remaining question and
    // the Submit button. Only for a live `ui_common_ask_user_question` interrupt
    // (the only producer of the flag); anything else falls through to the normal
    // advance-to-next-question behavior.
    const picked: ChatOption | undefined = question?.options.find((o: ChatOption): boolean => o.value === value);
    if (picked?.submitImmediately === true) {
      const tab: ChatTab | undefined = this.state.tabs.find((t: ChatTab): boolean => t.id === tabId);
      const msg: McpChatMessage | undefined = tab?.messages.find((m: McpChatMessage): boolean => m.id === messageId);
      const isPendingQuestionInterrupt: boolean =
        msg?.interruptData?.type === "question" && msg.interruptData.status === "pending";
      if (tab && !tab.isLoading && isPendingQuestionInterrupt) {
        this.updateTab(tabId, (t) => {
          const existing = t.questionnaires[messageId] || createEmptyQuestionnaireProgress();
          return {
            questionnaires: {
              ...t.questionnaires,
              [messageId]: {
                ...existing,
                selectedValues: { ...existing.selectedValues, [questionIndex]: value },
                delegatedIndices: clearSkippedMark(existing, questionIndex)
              }
            }
          };
        });
        const formatted: string = `${cleanQuestionPrompt(question?.prompt ?? "", questionIndex)}: ${value}`;
        this.dispatchInterruptAnswer(messageId, formatted);
        return;
      }
    }

    this.updateTab(tabId, (tab) => {
      const existing = tab.questionnaires[messageId] || createEmptyQuestionnaireProgress();
      const nextIndex =
        !isOtherSelection(question, value) && questionIndex < questionCount - 1 ? questionIndex + 1 : questionIndex;
      return {
        questionnaires: {
          ...tab.questionnaires,
          [messageId]: {
            ...existing,
            currentQuestionIndex: nextIndex,
            selectedValues: {
              ...existing.selectedValues,
              [questionIndex]: value
            },
            delegatedIndices: clearSkippedMark(existing, questionIndex)
          }
        }
      };
    });
  };

  handleQuestionMultiToggle = (tabId: string, messageId: string, questionIndex: number, value: string) => {
    this.updateTab(tabId, (tab) => {
      const existing = tab.questionnaires[messageId] || createEmptyQuestionnaireProgress();
      const prev = existing.multiSelectedValues?.[questionIndex] || [];
      const next = prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
      return {
        questionnaires: {
          ...tab.questionnaires,
          [messageId]: {
            ...existing,
            multiSelectedValues: {
              ...existing.multiSelectedValues,
              [questionIndex]: next
            },
            delegatedIndices: next.length > 0 ? clearSkippedMark(existing, questionIndex) : existing.delegatedIndices
          }
        }
      };
    });
  };

  handleQuestionCustomValueChange = (tabId: string, messageId: string, questionIndex: number, customValue: string) => {
    this.updateTab(tabId, (tab) => {
      const existing = tab.questionnaires[messageId] || createEmptyQuestionnaireProgress();
      return {
        questionnaires: {
          ...tab.questionnaires,
          [messageId]: {
            ...existing,
            customValues: {
              ...existing.customValues,
              [questionIndex]: customValue
            },
            delegatedIndices:
              customValue.trim().length > 0 ? clearSkippedMark(existing, questionIndex) : existing.delegatedIndices
          }
        }
      };
    });
  };

  handleQuestionGoTo = (tabId: string, messageId: string, targetIndex: number) => {
    this.updateTab(tabId, (tab) => {
      const existing = tab.questionnaires[messageId] || createEmptyQuestionnaireProgress();
      return {
        questionnaires: {
          ...tab.questionnaires,
          [messageId]: {
            ...existing,
            currentQuestionIndex: targetIndex
          }
        }
      };
    });
  };

  handleQuestionBack = (tabId: string, messageId: string) => {
    this.updateTab(tabId, (tab) => {
      const existing = tab.questionnaires[messageId] || createEmptyQuestionnaireProgress();
      return {
        questionnaires: {
          ...tab.questionnaires,
          [messageId]: {
            ...existing,
            currentQuestionIndex: Math.max(0, existing.currentQuestionIndex - 1)
          }
        }
      };
    });
  };

  handleQuestionNext = (
    tabId: string,
    messageId: string,
    questionCount: number,
    currentQuestion?: ParsedQuestionBlock
  ) => {
    this.updateTab(tabId, (tab) => {
      const existing = tab.questionnaires[messageId] || createEmptyQuestionnaireProgress();
      if (questionCount <= 0) return {};

      const currentQuestionIndex = Math.max(0, Math.min(existing.currentQuestionIndex, questionCount - 1));
      // Advance on RESOLVED, not answered: a question handed to the analyst is
      // settled, and gating on answeredness alone stranded the user on a skipped
      // question they had stepped back to. This used to inline its own copy of the
      // answeredness rule — the duplication `questionnaire-answered.ts` exists to
      // stop — so it now asks the shared predicate.
      const isCurrentResolved: boolean =
        currentQuestion !== undefined
          ? isQuestionResolved(currentQuestion, currentQuestionIndex, existing)
          : isQuestionDelegated(currentQuestionIndex, existing);
      if (!isCurrentResolved || currentQuestionIndex >= questionCount - 1) return {};

      return {
        questionnaires: {
          ...tab.questionnaires,
          [messageId]: {
            ...existing,
            currentQuestionIndex: currentQuestionIndex + 1
          }
        }
      };
    });
  };

  handleQuestionSubmit = (tabId: string, messageId: string, questionnaire: ParsedQuestionnaire) => {
    const tab = this.state.tabs.find((t) => t.id === tabId);
    if (!tab || tab.isLoading || questionnaire.questions.length === 0) return;

    const progress = tab.questionnaires[messageId] || createEmptyQuestionnaireProgress();
    const formatted = formatQuestionnaireAnswers(questionnaire, progress);
    if (!formatted) return;

    this.updateTab(tabId, (t) => ({
      questionnaires: {
        ...t.questionnaires,
        [messageId]: {
          ...(t.questionnaires[messageId] || createEmptyQuestionnaireProgress()),
          submitted: true
        }
      }
    }));

    this.handleSend(formatted);
  };

  // ── Active-tab question callback wrappers (stable references for render) ──

  handleActiveTabQuestionOptionSelect = (
    messageId: string,
    questionIndex: number,
    value: string,
    questionCount: number,
    question?: ParsedQuestionBlock
  ) => {
    this.handleQuestionOptionSelect(this.state.activeTabId, messageId, questionIndex, value, questionCount, question);
  };

  handleActiveTabQuestionMultiToggle = (messageId: string, questionIndex: number, value: string) => {
    this.handleQuestionMultiToggle(this.state.activeTabId, messageId, questionIndex, value);
  };

  handleActiveTabQuestionCustomValueChange = (messageId: string, questionIndex: number, customValue: string) => {
    this.handleQuestionCustomValueChange(this.state.activeTabId, messageId, questionIndex, customValue);
  };

  handleActiveTabQuestionGoTo = (messageId: string, targetIndex: number) => {
    this.handleQuestionGoTo(this.state.activeTabId, messageId, targetIndex);
  };

  handleActiveTabQuestionBack = (messageId: string) => {
    this.handleQuestionBack(this.state.activeTabId, messageId);
  };

  handleActiveTabQuestionNext = (messageId: string, questionCount: number, currentQuestion?: ParsedQuestionBlock) => {
    this.handleQuestionNext(this.state.activeTabId, messageId, questionCount, currentQuestion);
  };

  handleActiveTabQuestionSubmit = (messageId: string, questionnaire: ParsedQuestionnaire) => {
    this.handleQuestionSubmit(this.state.activeTabId, messageId, questionnaire);
  };

  handleActiveTabQuestionDelegateOne = (messageId: string, questionIndex: number, questionCount: number) => {
    this.handleQuestionDelegateOne(this.state.activeTabId, messageId, questionIndex, questionCount);
  };

  handleRemoveSelectedElement = (kuikaId: string) => {
    this.updateActiveTab((tab) => ({
      selectedElementAttachments: tab.selectedElementAttachments.filter((att) => att.kuikaId !== kuikaId)
    }));
  };

  /** Stable wrapper for UI click handlers — prevents the MouseEvent from being passed as `message`. */
  handleSendFromUI = () => {
    this.handleSend();
  };

  // ── Send / Stop / Clear ──

  /**
   * KOA-18304 — the two answers to the server's Designer capability question.
   *
   * These literals are the wire: the server writes them as the `value` of the
   * two chips (`canned-replies.ts` `DESIGNER_CHOICE_*`), and a chip click
   * submits the value verbatim. Matched exactly, never parsed. If either side
   * drifts, the chip degrades into an ordinary text message — which for the
   * "stay" answer means the question is asked again about the same request,
   * forever. Both sides pin these in tests. The literals live in the shared
   * chat utils so a test can import them without mounting this component.
   */
  /** The request the capability question was asked about — the last thing the user
   *  actually typed. The question is the newest assistant turn, so the newest user
   *  message is the held request. Empty when there is nothing to resend. */
  private lastUserMessageText(): string {
    const activeTab = this.getActiveTab();
    for (let i = activeTab.messages.length - 1; i >= 0; i--) {
      const m = activeTab.messages[i];
      if (m.role === "user" && m.content.trim().length > 0) return m.content.trim();
    }
    return "";
  }

  /**
   * Chip clicks. Ordinary options are sent as their own text — that is what every
   * other options block in the product does. The two capability answers are not
   * text: they resume the HELD request, so they resend the user's original words
   * with no second bubble, and the Builder answer flips the tab first so the turn
   * runs with full capability.
   */
  handleOptionSelect = (text: string): void => {
    const isContinue: boolean = text === DESIGNER_CHOICE_CONTINUE;
    const isSwitch: boolean = text === DESIGNER_CHOICE_SWITCH;
    if (!isContinue && !isSwitch) {
      void this.handleSend(text);
      return;
    }

    const original: string = this.lastUserMessageText();
    if (original.length === 0) return;

    if (isContinue) {
      // The flag is what stops the question repeating about the same request.
      void this.handleSend(undefined, undefined, undefined, {
        driveMessageOverride: original,
        suppressUserBubble: true,
        designerUiOnlyConfirmed: true
      });
      return;
    }

    // Builder needs no server-side capability grant: the mode is an ordinary
    // request field, so flipping the tab and resending IS the switch. Sent from
    // the update callback so the request is built after the mode has landed —
    // building it first would send the old mode and ask again.
    const activeTab = this.getActiveTab();
    this.updateTab(
      activeTab.id,
      () => ({ chatMode: "builder" }),
      () => {
        void this.handleSend(undefined, undefined, undefined, {
          driveMessageOverride: original,
          suppressUserBubble: true
        });
      }
    );
  };

  /**
   * Send a turn.
   *
   * Resolves TRUE when the request actually went on the wire and FALSE on every
   * early return (nothing to send, no balance, no connection, queued behind an
   * in-flight turn). Callers that flipped UI state ahead of the send — the change
   * plan's Approve / Revise card — put it back on false; a card that hides its
   * own buttons after a send that never left is a card nobody can decide again.
   */
  handleSend = async (
    message?: string,
    selectedElementAttachments?: SelectedElementAttachment[],
    selectedElementSegments?: SelectedElementSegment[],
    opts?: HandleSendOptions
  ): Promise<boolean> => {
    const activeTab = this.getActiveTab();
    // KOA-17628 — spec approval drives the turn with the BA's `## Spec:` markdown
    // (opts.driveMessageOverride) as if the user typed it, with no visible bubble.
    const overrideText = opts?.driveMessageOverride?.trim();
    // KOA-18422 — a decision on a change plan carries the delta's ID, not words.
    // It must never borrow the composer either: whatever is sitting in the input
    // box is the user's NEXT message, and sending it here would both mislabel the
    // turn and silently eat their draft.
    const isChangeDecision = isChangeDecisionIntent(opts?.intent);
    let text: string;
    if (isChangeDecision) {
      text = (message ?? "").trim();
    } else if (overrideText && overrideText.length > 0) {
      text = overrideText;
    } else {
      text = (message || this.currentInputValue).trim();
    }
    const hasImages = activeTab.attachedImages.length > 0;
    const hasFiles = activeTab.attachedFiles.length > 0;
    const hasSegments = !!selectedElementSegments && selectedElementSegments.length > 0;

    // Pills the user inserted inline in the editor become structured attachments.
    // Pulled here so the LLM still gets `selected element: …` lines without
    // leaking them into the visible bubble content.
    const pillElements = this.inputAreaRef.current?.contentEditableRef.current?.getSerializedContent().elements ?? [];
    const mergedAttachments: SelectedElementAttachment[] = [];
    const seenAttachmentIds = new Set<string>();
    for (const att of [
      ...(selectedElementAttachments ?? []),
      ...activeTab.selectedElementAttachments,
      ...pillElements
    ]) {
      if (!att?.kuikaId || seenAttachmentIds.has(att.kuikaId)) continue;
      seenAttachmentIds.add(att.kuikaId);
      mergedAttachments.push(att);
    }

    const sendable = hasSendablePayload({
      text,
      hasImages,
      hasFiles,
      attachmentCount: mergedAttachments.length,
      hasSegments,
      ...(opts?.intent !== undefined ? { intent: opts.intent } : {})
    });
    if (!sendable) return false;

    // Block if balance is exhausted (only enforced on platform deployments —
    // non-platform responses set remainingBalance to null). ASK mode is FREE,
    // so it is exempt — it never charges and must stay usable at zero balance
    // (mirrors the server-side pre-flight gate exemption).
    if (this.isSendBlockedByBalance(activeTab)) {
      this.addAssistantMessageToTab(
        activeTab.id,
        "Your AI credits have run out. Top up to continue from Settings → AI Chatbot Credits.",
        true
      );
      return false;
    }

    // Queue the message if a request is already in-flight
    if (activeTab.isLoading) {
      const queued: QueuedMessage = {
        text,
        images: [...activeTab.attachedImages],
        files: [...activeTab.attachedFiles],
        selectedElementAttachments: mergedAttachments,
        mentions: [...activeTab.mentions]
      };
      this.updateActiveTab(
        (tab) => ({
          queuedMessages: [...tab.queuedMessages, queued],
          inputValue: "",
          attachedImages: [],
          attachedFiles: [],
          selectedElementAttachments: [],
          mentions: [],
          mentionCounter: 0
        }),
        () => {
          this.inputAreaRef.current?.clearContent();
          this.inputAreaRef.current?.resetHeight();
          this.scrollToBottom();
        }
      );
      return false;
    }

    const tabId = activeTab.id;
    const currentImages = activeTab.attachedImages;
    const currentFiles = activeTab.attachedFiles;
    const currentSelectedElementAttachments = mergedAttachments;
    const currentMentions = activeTab.mentions;
    let displayText = text;
    if (!displayText && hasImages) {
      displayText = "Sent image(s)";
    } else if (!displayText && hasFiles) {
      displayText = "Sent file(s)";
    }

    // Rewind baseline: start fetch now, attach when ready — do NOT await before
    // sendChatRequest. Tools run only after the server LLM path (seconds later);
    // a light stagedDraftOpIds call finishing in that window is enough for a
    // correct pre-tool checkpoint. Awaiting blocked chat on CoreApi for no gain.
    const baselinePromise: Promise<string[] | null> =
      this.state.rewindEnabled === true ? this.captureStagedOpIdsBaseline() : Promise.resolve(null);

    const userMsg: McpChatMessage = {
      id: generateMessageId(),
      role: "user",
      content: displayText,
      timestamp: Date.now(),
      images:
        currentImages.length > 0
          ? currentImages.map((img) => ({ base64: img.base64, mediaType: img.mediaType, previewUrl: img.previewUrl }))
          : undefined,
      files:
        currentFiles.length > 0
          ? currentFiles.map((f) => ({ fileName: f.fileName, mediaType: f.mediaType, fileSize: f.fileSize }))
          : undefined,
      selectedElementAttachments:
        currentSelectedElementAttachments.length > 0 ? currentSelectedElementAttachments : undefined,
      selectedElementSegments: hasSegments ? selectedElementSegments : undefined,
      mentions: currentMentions.length > 0 ? currentMentions : undefined
    };

    // Baseline is fetched in parallel with send (not awaited) — see below.

    try {
      const sd = getDesignerRuntime();
      const stackSize = sd?.screenProvider?.getUndoStackSize?.() ?? 0;
      this.preRequestUndoStackSize.set(tabId, stackSize);
    } catch {
      this.preRequestUndoStackSize.set(tabId, 0);
    }

    const shouldAutoName = activeTab.messages.length === 0 && !activeTab.isCustomName && text;
    // Mint the sessionId client-side when the tab doesn't have one yet so the
    // title-generation aux call can share the same session as this first chat
    // request. Otherwise title calls ship session_id=null and the server aux
    // fallback buckets every title LLM call from every user into a single
    // literal "aux-title" session row in telemetry. The server accepts
    // client-supplied ids (ws-bridge.ts: `payload.session_id || uuidv4()`).
    const assignedSessionId = activeTab.sessionId || crypto.randomUUID();
    this.currentInputValue = "";
    this.historyIndex = -1;
    this.historyDraft = "";
    this.resetToolActivityState(tabId);

    this.updateTab(
      tabId,
      (tab) => {
        const updates: Partial<ChatTab> = {
          // KOA-17628 — spec-approval sends drive the pipeline without echoing a
          // redundant user bubble (the spec card already shows the content); the
          // turn still enters the thinking state via isLoading below.
          ...(opts?.suppressUserBubble ? {} : { messages: [...tab.messages, userMsg] }),
          inputValue: "",
          isLoading: true,
          isTextStreaming: false,
          toolActivities: [],
          attachedImages: [],
          attachedFiles: [],
          selectedElementAttachments: [],
          mentions: [],
          mentionCounter: 0,
          effectiveCapabilityProfile: null,
          capabilityReason: null,
          capabilityPromotedGroups: null,
          capabilityDenialMessage: null
        };
        if (!tab.sessionId) {
          updates.sessionId = assignedSessionId;
        }
        if (shouldAutoName) {
          const words = text.split(/\s+/);
          let autoName = "";
          for (const word of words) {
            if (autoName.length + word.length + 1 > 30) {
              autoName += "...";
              break;
            }
            autoName += (autoName ? " " : "") + word;
          }
          updates.name = autoName || text.slice(0, 30);
        }
        return updates;
      },
      () => {
        this.forceScrollToBottom();
        this.inputAreaRef.current?.clearContent();
        this.inputAreaRef.current?.resetHeight();
        // Do NOT generateTabTitle here. Title is a separate aux LLM call that
        // used to fire before chat_request left the browser and overlapped the
        // first-turn critical path (observed: aux-title 40s concurrent with
        // chat start; contention / mis-attribution of "thinking" delay).
        // Title runs only after sendChatRequest succeeds (below).
        // Save immediately — bypass scheduleSave's debounce and streaming guard.
        // At this point the user message is in state but the streaming request
        // hasn't started yet (activeChatRequestIds is still empty for this tab).
        this.flushSave();
      }
    );

    let fullMessage = text;
    fullMessage = this.buildOutgoingMessage(fullMessage, currentSelectedElementAttachments, selectedElementSegments);

    // Expand @ mention references into detailed context
    if (currentMentions.length > 0) {
      const mentionContextParts = currentMentions.map((ref) => expandMentionContext(ref, true));
      fullMessage += "\n\n--- Referenced Entities ---\n" + mentionContextParts.join("\n\n");
    }

    const sessionId = assignedSessionId;
    const currentUser = getCurrentUser();
    const requestBody: ChatRequestBody = {
      session_id: sessionId,
      tab_id: McpToolBridge.getTabId(),
      // Chat-tab id (stable across refresh) so the server can gate plan
      // hydration to the owning tab — distinct from tab_id, the page id (KOA-17797).
      chat_tab_id: activeTab.id,
      user_id: currentUser?.id || undefined,
      user_name: currentUser?.username || undefined,
      message: fullMessage,
      project_id: getActiveProjectId(),
      project_name: getActiveProjectName(),
      project_type: getActiveProjectType(),
      domain_name: getCurrentDomain(),
      auth_token: localStorage.getItem("tokenString") || undefined,
      requested_mode: activeTab.chatMode
    };
    // Effort tier only means something on the OpenRouter (platform) provider —
    // ModelRegistry.resolve applies overrides only there. Sending it elsewhere is
    // a no-op server-side; gate it (same gate as the selector chip + credits pill)
    // so on-prem / other providers keep their configured model.
    if (this.state.serverProvider === AiChatProviderName.OpenRouter) {
      requestBody.model_tier = activeTab.effort;
    }
    const screenContext = gatherScreenContext();
    if (screenContext) requestBody.screen_context = screenContext;
    if (currentImages.length > 0) {
      requestBody.images = currentImages.map((img) => ({ base64: img.base64, mediaType: img.mediaType }));
    }
    if (currentFiles.length > 0) {
      requestBody.files = currentFiles.map((f) => ({
        base64: f.base64,
        fileName: f.fileName,
        mediaType: f.mediaType,
        fileSize: f.fileSize
      }));
    }
    if (activeTab.chatMode === "ask") {
      requestBody.ask_mode = true;
    } else if (activeTab.chatMode === "builder") {
      requestBody.builder_mode = true;
    }
    if (activeTab.planMode) {
      requestBody.plan_mode = true;
    }
    if (isPlatformMode()) {
      requestBody.platform_mode = true;
    }
    if (opts?.specApproved) {
      requestBody.spec_approved = true;
    }
    if (opts?.designerUiOnlyConfirmed) {
      requestBody.designer_ui_only_confirmed = true;
    }
    // Structured AI validation-fix task (KOA-17717): carry the intent + items and
    // run in BUILDER (full capability). The server's fix_validations branch is
    // intent-based and sits after the ASK/PLAN short-circuits, so clear ask/plan
    // (BUILDER is not short-circuited) — the branch runs regardless of mode.
    // KOA-18422 — a decision on a change plan. STRUCTURAL: the delta's id, never
    // the plan's markdown, so the server applies what was shown instead of reading
    // the request a second time. It bypasses triage and the planner entirely.
    if (opts?.intent === "apply_change_delta" || opts?.intent === "discard_change_delta") {
      requestBody.intent = opts.intent;
      requestBody.change_delta_id = opts.changeDeltaId;
      requestBody.plan_mode = false;
    }
    if (opts?.intent === "fix_validations") {
      requestBody.intent = opts.intent;
      requestBody.validation_fix_items = opts.validationFixItems;
      requestBody.requested_mode = "builder";
      requestBody.ask_mode = false;
      requestBody.builder_mode = true;
      requestBody.plan_mode = false;
    }
    // Project analysis: the same structured-marker treatment. The server's branch
    // is intent-based and sits before triage, but ASK and PLAN short-circuit
    // EARLIER than that — so both are cleared here. `requested_mode` has to go
    // too, not just `ask_mode`: the server resolves ASK from EITHER
    // (`requested_mode === "ask" || ask_mode`), so clicking the button in a tab
    // that happens to be in Ask mode would otherwise answer in prose and never
    // run the walk. Forced to designer rather than builder (what the
    // validation-fix task uses) because this branch calls no tools and needs no
    // elevated capability. No payload: the project comes from the request scope.
    if (opts?.intent === "analyze_project") {
      requestBody.intent = opts.intent;
      requestBody.requested_mode = "designer";
      requestBody.ask_mode = false;
      requestBody.builder_mode = false;
      requestBody.plan_mode = false;
    }
    // Include model overrides if any are set (kuika.com users on openrouter only —
    // the override path routes through OpenRouter and is meaningless on other providers).
    const { modelOverrides, serverProvider } = this.state;
    if (
      this.isKuikaDeveloper() &&
      serverProvider === AiChatProviderName.OpenRouter &&
      (modelOverrides.complex || modelOverrides.default || modelOverrides.cheap || modelOverrides.braid)
    ) {
      requestBody.model_overrides = modelOverrides;
    }

    // After page refresh the tab's stored sessionId is null but we may have
    // messages restored from localStorage. Send them as history so the
    // backend can seed the conversation and the AI retains context. Check
    // the ORIGINAL tab.sessionId (not the possibly-minted assignedSessionId
    // above) — the mint is what we're about to send to the server, but the
    // refresh-recovery decision hinges on whether this tab had a live server
    // session before.
    if (!activeTab.sessionId && activeTab.messages.length > 0) {
      const historyMsgs = activeTab.messages
        .filter((m) => (m.role === "user" || m.role === "assistant") && !m.isError)
        .map((m) => ({
          id: m.id,
          role: m.role,
          content:
            m.role === "user"
              ? this.buildOutgoingMessage(m.content, m.selectedElementAttachments, m.selectedElementSegments)
              : m.content,
          timestamp: m.timestamp,
          ...(m.completedToolActivities ? { completedToolActivities: m.completedToolActivities } : {}),
          ...(m.responseDurationMs ? { responseDurationMs: m.responseDurationMs } : {}),
          ...(m.mentions && m.mentions.length > 0 ? { mentions: m.mentions } : {}),
          ...(m.chatRequestId ? { chatRequestId: m.chatRequestId } : {}),
          ...(m.userRating != null ? { userRating: m.userRating } : {})
        }));
      if (historyMsgs.length > 0) {
        requestBody.history = historyMsgs;
      }
      // Include persisted plan so server can hydrate it on the new session —
      // but ONLY if it belongs to THIS tab. A plan stamped for another tab must
      // never be sent here, else it bleeds in as the planner's standing plan and
      // hijacks an unrelated turn (KOA-17797).
      if (activeTab.planJson && planBelongsToTab(activeTab.planJson, activeTab.id)) {
        requestBody.plan_json = activeTab.planJson;
      }
    }

    // Canvas zone activation removed — the tool bridge activates/deactivates
    // per-tool via CATEGORY_ZONE_MAP/FLAT_ZONE_MAP, so backend-only loops
    // no longer trigger the canvas border glow.

    const bridge = getMcpBridgeInstance();
    if (!bridge || !bridge.isConnected) {
      this.addAssistantMessageToTab(
        tabId,
        "Not connected to MCP server. Please check your connection and try again.",
        true
      );
      this.updateTab(
        tabId,
        () => ({ isLoading: false, isTextStreaming: false, toolActivities: [] }),
        this.scrollToBottom
      );
      return false;
    }

    const chatRequestId = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    this.activeChatRequestIds.set(tabId, chatRequestId);
    this.requestStartTimes.set(tabId, performance.now());
    this.beginTurnProgress(tabId);

    // KOA-18030 — an agent build must not become its own collab blocker. Every
    // screen load joins a session, so a multi-screen build opened a session per
    // screen ON THIS TAB and CoreApi then refused that same build's whole-screen
    // save ("is being edited in a live collaboration session" — the guard counts
    // participants, not distinct humans). Suspend participation for the turn; a
    // session belonging to ANOTHER person is untouched, so a genuine concurrent
    // editor still blocks the save (and the agent is told, not silently wiped).
    this.setCollabAgentBuildSuspension(true);

    // Capture pre-request screen state for structural diff
    const beforeSnap = captureSnapshot();
    if (beforeSnap) this.beforeSnapshots.set(tabId, beforeSnap);

    const finalize = () => {
      this.finalizeTurn(tabId, { outcome: "completed", chatRequestId });
    };

    let receivedResponse = false;
    let modelContentShown = false;
    let tokenRetryAttempted = false;

    bridge.onChatEvent(chatRequestId, (evt: ChatEventData) => {
      if (!this.isMounted_) return;
      const { event: eventType, data } = evt;

      if (eventType === "model_delta") {
        receivedResponse = true;
        // Specialist deltas (agent field set) are rendered as entries
        // inside the live tool transcript via the `specialist_activity`
        // event. Suppress the standalone bubble creation here so the
        // same content doesn't also stack as a separate "Wirer is
        // writing…" assistant message. Only keep the generalist /
        // non-specialist flow which has no agent field.
        if (data.agent) {
          this.updateTab(tabId, () => ({ isTextStreaming: true }));
        } else if (data.accumulated) {
          // `modelContentShown` gates the `done` fallback below, so it may only
          // be set where a bubble is ACTUALLY written. Setting it for every
          // model_delta disarmed that fallback on essentially every turn:
          // post-PEA nearly all text arrives as specialist deltas (agent set,
          // no bubble), and ASK-mode deltas carry only `delta` with no
          // `accumulated`. Both drew nothing yet suppressed `done.content` —
          // including the cancellation note — leaving the turn blank (KOA-17572).
          modelContentShown = true;
          // Keep isLoading true — clearing it here caused a gap where no
          // indicator showed between text completion and tool execution.
          this.updateTab(tabId, () => ({ isTextStreaming: true }));
          this.upsertStreamingAssistantMessage(
            tabId,
            data.accumulated,
            undefined,
            undefined,
            true,
            data.messageId,
            undefined,
            undefined
          );
        }
      } else if (eventType === "routing") {
        // The server has always emitted `routing` — 152 sites — naming the phase
        // it is entering, counters included: `Building "Orders" (2/5)...`,
        // `Backend 3/4 — Orders, OrderItems (7 action)`, `Retrying screen build
        // (attempt 2/11)...`. The panel had no branch for it, so every one was
        // serialized, sent, parsed and dropped while the header sat on
        // "Thinking…" for minutes. These fire immediately BEFORE the long work,
        // which is exactly when the UI used to go dark.
        //
        // `routing` carries two unrelated payload shapes under one name: this
        // free-text narration, and structured classification/decision records
        // (`{tier,model,reason}`, `{intentType,…}`) that have no `message` at
        // all. The type guard is the filter — never branch on the text itself.
        this.notePhase(tabId, data);
      } else if (eventType === "model") {
        receivedResponse = true;
        if (data.message) {
          // Only an actually-written bubble disarms the `done` fallback — see
          // the model_delta branch above.
          modelContentShown = true;
          // Clear routing progress and streaming flag when text is finalized
          this.updateTab(tabId, () => ({ isTextStreaming: false }));
          this.upsertStreamingAssistantMessage(
            tabId,
            data.message,
            undefined,
            undefined,
            false,
            data.messageId,
            data.explorationContent
          );
        }
        if (data.suggestions?.length) {
          this.pendingSuggestions.set(tabId, data.suggestions);
        }
      } else if (eventType === "effective_capability") {
        this.updateTab(tabId, () => ({
          effectiveCapabilityProfile: data.effectiveCapabilityProfile ?? null,
          capabilityReason: data.reason ?? null,
          capabilityPromotedGroups: data.promotedGroups ?? null,
          capabilityDenialMessage: data.denialMessage ?? null
        }));
        if (data.reason === "auto_promoted" && data.promotedGroups?.length) {
          const groups = (data.promotedGroups as string[]).join("/");
          // Flip the mode selector to "builder" so the bottom toggle and any
          // derived UI reflect the promoted state. Server-side promotion is
          // per-turn only; the user can flip back manually if they want the
          // next turn to run as Designer again.
          this.updateTab(tabId, () => ({ chatMode: "builder" }), this.scrollToBottom);
        }
      } else if (eventType === "tool_activity") {
        // Re-enable loading indicator during tool execution rounds
        // (it was turned off when model_delta started streaming text).
        // NO scrollToBottom here — the MCP_TOOL_ACTIVITY_EVENT dispatch
        // below (via handleToolActivity) handles scroll selectively,
        // only for genuinely NEW activities. Double-scrolling here was
        // part of the "constant scroll jumping" problem on tool calls.
        this.updateTab(tabId, () => ({ isLoading: true }));
        this.noteBackendToolTitle(tabId, data);
        this.dispatchStreamToolActivity(tabId, data);
      } else if (eventType === "specialist_activity") {
        // Specialist runs (Wirer/Binder/Designer/Backend-Architect/
        // Render-Fixer) flow through the same tool-activity pipeline so
        // they render as entries inside the live transcript instead of
        // spawning standalone assistant bubbles. Keep isLoading true
        // until the turn finishes (mirroring tool_activity above).
        this.updateTab(tabId, () => ({ isLoading: true }), this.scrollToBottom);
        this.dispatchStreamSpecialistActivity(tabId, data);
      } else if (eventType === "interrupt") {
        // Human-in-the-loop: agent wants confirmation or asks questions.
        receivedResponse = true;
        // Mark that content was shown so the `done` handler's fallback
        // content-write doesn't fire. Without this, `done` overwrites
        // the streaming buffer entry this handler just wrote —
        // replacing the interruptData (approval buttons) with plain
        // text and rendering a dead message.
        modelContentShown = true;

        // Capture sessionId early — the `done` event normally sets it,
        // but with the blocking gate the pipeline hasn't completed yet.
        // Without this, first-request tabs have `sessionId: null` and
        // the approve handler rejects with "No active session."
        if (data.sessionId) {
          this.updateTab(tabId, (tab) => (tab.sessionId ? {} : { sessionId: data.sessionId }));
        }

        // The agent loop is BLOCKED (awaiting the gate Promise), not
        // finished. Set isLoading=false so the approve/reject buttons
        // become clickable — the `InterruptActions` component disables
        // them when `isActiveTabLoading` is true. Without this, the
        // buttons render but are greyed out and the user can't act.
        // When the user clicks approve/reject, `sendInterruptDecision`
        // sets isLoading=true again for the continuation phase.
        this.updateTab(tabId, () => ({ isLoading: false, isTextStreaming: false }));

        // Agent loop is paused — reset canvas animation so it doesn't run
        // indefinitely while waiting for user input. It will re-activate
        // when the next tool call fires after the user responds.
        if (this.canvasZoneActiveTabs.has(tabId)) {
          this.canvasZoneActiveTabs.delete(tabId);
          window.dispatchEvent(new CustomEvent(MCP_ZONE_RESET, { detail: { zone: "canvas" } }));
        }

        const pending = data.pendingToolCall;
        if (pending) {
          let interruptData: InterruptData;
          if (data.type === "question") {
            interruptData = {
              type: "question",
              toolCallId: pending.id,
              toolName: pending.tool_name,
              parameters: pending.arguments || {},
              status: "pending",
              // Server-scoped: only the flow where handing a question back is
              // meaningful offers the delegate buttons. A consent card does not.
              allowDelegate: data?.allowDelegate === true
            } as QuestionInterruptData;
            this.upsertStreamingAssistantMessage(tabId, "Clarifying questions", undefined, interruptData);
          } else {
            const parts = getInterruptDescriptionParts(pending.tool_name, pending.arguments || {});
            interruptData = {
              type: "confirmation",
              toolCallId: pending.id,
              toolName: pending.tool_name,
              parameters: pending.arguments || {},
              description: getInterruptDescription(pending.tool_name, pending.arguments || {}),
              actionText: parts.action,
              targetName: parts.targetName,
              targetKuikaId: parts.targetKuikaId,
              detailItems: parts.detailItems,
              priorFailure: pending.priorFailure,
              status: "pending"
            } as ConfirmationInterruptData;
            this.upsertStreamingAssistantMessage(tabId, interruptData.description, undefined, interruptData);
          }
        } else if (data.message) {
          this.upsertStreamingAssistantMessage(tabId, data.message);
        }
      } else if (eventType === "discovery_report") {
        this.handleDiscoveryReportEvent(data);
      } else if (eventType === "screenshot") {
        if (data.screenshot) {
          const syntheticReqId = `${Date.now()}-_screenshot`;
          const detail: McpToolActivityEvent = {
            requestId: syntheticReqId,
            toolName: "_screenshot",
            input: data,
            status: "completed",
            isMutating: false,
            timestamp: Date.now()
          };
          window.dispatchEvent(new CustomEvent(MCP_TOOL_ACTIVITY_EVENT, { detail }));
        }
      } else if (eventType === "plan_progress") {
        const newMessages: McpChatMessage[] = [];

        if (data.planCompleted) {
          // End-of-execution summary — single "Plan completed" message
          const total = (data.totalSteps as number) || 0;
          newMessages.push({
            id: generateMessageId(),
            role: "tool_status" as const,
            content: `All ${total} steps completed`,
            timestamp: Date.now(),
            toolName: "Plan completed",
            toolStatus: "completed" as const
          });
        } else if (Array.isArray(data.completedSteps)) {
          // Mid-execution — individual step completion messages
          for (const step of data.completedSteps as PlanProgressStep[]) {
            newMessages.push({
              id: generateMessageId(),
              role: "tool_status" as const,
              content: step.description,
              timestamp: Date.now(),
              toolName: `Step ${step.index} completed`,
              toolStatus: "completed" as const
            });
          }
        }

        this.batchTabUpdate(tabId, (t) => ({
          // Stamp the owning tab so this plan can never bleed into another tab's
          // turn (KOA-17797). `tabId` is the request's origin tab, captured at send.
          ...(data.planJson ? { planJson: stampPlanOwner(data.planJson, tabId) } : {}),
          ...(newMessages.length > 0 ? { messages: [...t.messages, ...newMessages] } : {})
        }));
        // Only scroll for mid-execution step messages, not for the final
        // summary — the "done" event follows immediately and finalize() scrolls.
        if (newMessages.length > 0 && !data.planCompleted) {
          this.scrollToBottom();
        }
      } else if (eventType === "done") {
        receivedResponse = true;
        // Credits are no longer carried on the `done` event — the
        // BalanceManager's WS push (`balance_state`) delivers the
        // authoritative post-spend number. See server-side
        // chat-orchestrator.buildDoneData for context.
        if (data.sessionId) {
          this.updateTab(tabId, (tab) => (tab.sessionId ? {} : { sessionId: data.sessionId }));
        }
        // Capture plan JSON for cross-session persistence
        if (data.planJson) {
          this.updateTab(tabId, () => ({ planJson: stampPlanOwner(data.planJson, tabId) }));
        }
        // Capture suggestions from done event as fallback (model event is preferred)
        if (data.suggestions?.length && !this.pendingSuggestions.has(tabId)) {
          this.pendingSuggestions.set(tabId, data.suggestions);
        }
        // Only use done content as a fallback when no "model" event was
        // received during this request. Otherwise the done event would
        // overwrite the last displayed message with a redundant copy.
        if (data.content && !modelContentShown) {
          this.upsertStreamingAssistantMessage(tabId, data.content);
        }
        this.maybeShowQuotaWarning(tabId, data);
        finalize();
      } else if (eventType === "token_expired") {
        receivedResponse = true;
        this.refreshAuthToken().then((refreshed) => {
          if (refreshed && !tokenRetryAttempted) {
            // Auto-retry with fresh token — seamless to the user
            tokenRetryAttempted = true;
            // Clean up the current request's listener without full finalize
            // (avoids processNextQueuedMessage race)
            this.activeChatRequestIds.delete(tabId);
            bridge.offChatEvent(chatRequestId);
            this.streamingMessageIds.delete(tabId);
            requestBody.auth_token = localStorage.getItem("tokenString") || undefined;
            this.retryChatAfterTokenRefresh(tabId, requestBody, bridge);
          } else {
            // Silently finalize — the axios interceptor will redirect to login
            // if the refresh token is truly expired. No chat message needed.
            finalize();
          }
        });
      } else if (eventType === "error") {
        receivedResponse = true;
        this.addAssistantMessageToTab(tabId, data.error || "Something went wrong", true);
        finalize();
      }
    });

    const abortSendWithoutChat = (userVisibleError: string): void => {
      this.addAssistantMessageToTab(tabId, userVisibleError, true);
      this.updateTab(
        tabId,
        () => ({ isLoading: false, isTextStreaming: false, toolActivities: [] }),
        this.scrollToBottom
      );
      this.activeChatRequestIds.delete(tabId);
      bridge.offChatEvent(chatRequestId);
      this.streamingMessageIds.delete(tabId);
      this.requestStartTimes.delete(tabId);
      this.beforeSnapshots.delete(tabId);
      this.preRequestUndoStackSize.delete(tabId);
    };

    if (!bridge.isConnected) {
      abortSendWithoutChat("Not connected to MCP server. Please check your connection and try again.");
      return false;
    }

    const sent = bridge.sendChatRequest(chatRequestId, requestBody);
    if (!sent) {
      abortSendWithoutChat("Not connected to MCP server. Please check your connection and try again.");
      return false;
    }

    // Attach rewind checkpoint when the parallel fetch finishes — never gates send.
    void baselinePromise.then((baseline: string[] | null) => {
      if (!this.isMounted_ || baseline === null) {
        if (this.state.rewindEnabled === true && baseline === null) {
          console.warn(
            "[rewind] baseline unavailable for message — chat already sent; this turn may lack rewind scope"
          );
        }
        return;
      }
      this.updateTab(tabId, (tab) => ({
        messages: tab.messages.map((m) => (m.id === userMsg.id ? { ...m, stagedOpIdsBeforeSend: baseline } : m))
      }));
    });

    // Title after chat is on the wire — never race first-turn send with aux-title.
    // Tab already has a provisional name from the message words (shouldAutoName above).
    if (shouldAutoName) {
      this.generateTabTitle(tabId, text, assignedSessionId);
    }
    return true;
  };

  /**
   * Refresh the auth token using the stored refresh token.
   * Returns true if the token was refreshed successfully.
   */
  refreshAuthToken = async (): Promise<boolean> => {
    try {
      // Shares the single in-flight refresh with the axios interceptor and the
      // streaming helper — the backend rotates refresh tokens, so a private POST
      // here would retire a token another path is still using. See auth/token-refresh.
      await refreshAccessToken();
      return true;
    } catch {
      // Token refresh failed
      return false;
    }
  };

  /**
   * Re-send the chat request after a successful token refresh.
   * Reuses the existing sendMessage flow so all event handling
   * (model, tool_activity, interrupt, done, error) works normally.
   */
  private retryChatAfterTokenRefresh(tabId: string, requestBody: ChatRequestBody, bridge: McpToolBridge): void {
    // Remove the user message that was already added — sendMessage will add it again
    // Actually, the user message is already in the tab. We just need to re-send
    // the WS request. The backend will use the existing session.
    const retryRequestId = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    this.resetToolActivityState(tabId);
    this.activeChatRequestIds.set(tabId, retryRequestId);
    this.requestStartTimes.set(tabId, performance.now());
    this.beginTurnProgress(tabId);
    this.updateTab(
      tabId,
      () => ({
        isLoading: true,
        toolActivities: [],
        effectiveCapabilityProfile: null,
        capabilityReason: null,
        capabilityPromotedGroups: null,
        capabilityDenialMessage: null
      }),
      this.scrollToBottom
    );

    // Capture pre-request snapshot for retry path (if not already captured)
    if (!this.beforeSnapshots.has(tabId)) {
      const retrySnap = captureSnapshot();
      if (retrySnap) this.beforeSnapshots.set(tabId, retrySnap);
    }

    const finalize = () => {
      this.finalizeTurn(tabId, { outcome: "completed", chatRequestId: retryRequestId });
    };

    let modelContentShown = false;

    bridge.onChatEvent(retryRequestId, (evt: ChatEventData) => {
      if (!this.isMounted_) return;
      const { event: eventType, data } = evt;

      if (eventType === "model") {
        if (data.message) {
          // Same rule as the primary handler: only a written bubble disarms the
          // `done` fallback below.
          modelContentShown = true;
          this.upsertStreamingAssistantMessage(tabId, data.message, undefined, undefined, false, data.messageId);
        }
        if (data.suggestions?.length) {
          this.pendingSuggestions.set(tabId, data.suggestions);
        }
      } else if (eventType === "routing") {
        // Same feed as the primary handler — a turn resumed after a token
        // refresh must keep narrating, not fall silent for the rest of a build.
        this.notePhase(tabId, data);
      } else if (eventType === "tool_activity") {
        this.noteBackendToolTitle(tabId, data);
        this.dispatchStreamToolActivity(tabId, data);
      } else if (eventType === "interrupt") {
        modelContentShown = true;
        // Capture sessionId early (same reason as primary handler)
        if (data.sessionId) {
          this.updateTab(tabId, (tab) => (tab.sessionId ? {} : { sessionId: data.sessionId }));
        }
        // Unblock UI so approve/reject buttons are clickable
        this.updateTab(tabId, () => ({ isLoading: false, isTextStreaming: false }));
        const pending = data.pendingToolCall;
        if (pending) {
          let interruptData: InterruptData;
          if (data.type === "question") {
            interruptData = {
              type: "question",
              toolCallId: pending.id,
              toolName: pending.tool_name,
              parameters: pending.arguments || {},
              status: "pending",
              // Server-scoped: only the flow where handing a question back is
              // meaningful offers the delegate buttons. A consent card does not.
              allowDelegate: data?.allowDelegate === true
            } as QuestionInterruptData;
            this.upsertStreamingAssistantMessage(tabId, "Clarifying questions", undefined, interruptData);
          } else {
            const parts = getInterruptDescriptionParts(pending.tool_name, pending.arguments || {});
            interruptData = {
              type: "confirmation",
              toolCallId: pending.id,
              toolName: pending.tool_name,
              parameters: pending.arguments || {},
              description: getInterruptDescription(pending.tool_name, pending.arguments || {}),
              actionText: parts.action,
              targetName: parts.targetName,
              targetKuikaId: parts.targetKuikaId,
              detailItems: parts.detailItems,
              priorFailure: pending.priorFailure,
              status: "pending"
            } as ConfirmationInterruptData;
            this.upsertStreamingAssistantMessage(tabId, interruptData.description, undefined, interruptData);
          }
        }
        // Don't finalize — the agent loop is still alive (blocked on gate).
        // The approve/reject handler will resume it and eventually a `done`
        // event will fire from the completed pipeline.
      } else if (eventType === "discovery_report") {
        this.handleDiscoveryReportEvent(data);
      } else if (eventType === "plan_progress") {
        const newMessages: McpChatMessage[] = [];
        if (data.planCompleted) {
          const total = (data.totalSteps as number) || 0;
          newMessages.push({
            id: generateMessageId(),
            role: "tool_status" as const,
            content: `All ${total} steps completed`,
            timestamp: Date.now(),
            toolName: "Plan completed",
            toolStatus: "completed" as const
          });
        } else if (Array.isArray(data.completedSteps)) {
          for (const step of data.completedSteps as PlanProgressStep[]) {
            newMessages.push({
              id: generateMessageId(),
              role: "tool_status" as const,
              content: step.description,
              timestamp: Date.now(),
              toolName: `Step ${step.index} completed`,
              toolStatus: "completed" as const
            });
          }
        }
        this.batchTabUpdate(tabId, (t) => ({
          // Stamp the owning tab so this plan can never bleed into another tab's
          // turn (KOA-17797). `tabId` is the request's origin tab, captured at send.
          ...(data.planJson ? { planJson: stampPlanOwner(data.planJson, tabId) } : {}),
          ...(newMessages.length > 0 ? { messages: [...t.messages, ...newMessages] } : {})
        }));
        if (newMessages.length > 0 && !data.planCompleted) {
          this.scrollToBottom();
        }
      } else if (eventType === "done") {
        if (data.sessionId) {
          this.updateTab(tabId, (tab) => (tab.sessionId ? {} : { sessionId: data.sessionId }));
        }
        if (data.planJson) {
          this.updateTab(tabId, () => ({ planJson: stampPlanOwner(data.planJson, tabId) }));
        }
        if (data.suggestions?.length && !this.pendingSuggestions.has(tabId)) {
          this.pendingSuggestions.set(tabId, data.suggestions);
        }
        if (data.content && !modelContentShown) {
          this.upsertStreamingAssistantMessage(tabId, data.content);
        }
        this.maybeShowQuotaWarning(tabId, data);
        finalize();
      } else if (eventType === "error") {
        this.addAssistantMessageToTab(tabId, data.error || "Something went wrong", true);
        finalize();
      } else if (eventType === "token_expired") {
        // Second token_expired on retry — silently finalize
        finalize();
      }
    });

    const sent: boolean = bridge.sendChatRequest(retryRequestId, requestBody);
    if (!sent) {
      this.addAssistantMessageToTab(
        tabId,
        "Not connected to MCP server. Please check your connection and try again.",
        true
      );
      this.updateTab(
        tabId,
        () => ({ isLoading: false, isTextStreaming: false, toolActivities: [] }),
        this.scrollToBottom
      );
      this.activeChatRequestIds.delete(tabId);
      bridge.offChatEvent(retryRequestId);
      this.streamingMessageIds.delete(tabId);
      this.requestStartTimes.delete(tabId);
    }
  }

  /**
   * Close a turn: fold the live tool transcript into the turn's assistant
   * message, stop the spinner, and tear down the request's listeners.
   *
   * EVERY ending routes through here — the `done` event, the token-refresh
   * retry's `done`, the Stop button, and the reconnect that finds the turn
   * gone. Before KOA-17572 only the `done` paths folded: `handleStop` wiped
   * `toolActivities` outright (after destroying the archive) and the
   * disconnect notice did the same, so a turn that ran 58 tool calls and was
   * then stopped left no record of any of them.
   *
   * The activity fold is computed SYNCHRONOUSLY, before the setState, so a
   * caller may reset the archive immediately afterwards without racing the
   * updater.
   */
  private finalizeTurn = (tabId: string, opts: FinalizeTurnOptions): void => {
    if (this.streamingRafId != null) {
      cancelAnimationFrame(this.streamingRafId);
      this.streamingRafId = null;
    }
    if (this.streamingBuffer.size > 0) {
      if (opts.sync) this.flushStreamingBufferSync();
      else this.flushStreamingBuffer();
    }

    this.activeChatRequestIds.delete(tabId);
    if (opts.chatRequestId) {
      getMcpBridgeInstance()?.offChatEvent(opts.chatRequestId);
    }
    this.streamingMessageIds.delete(tabId);
    // Turn over — rejoin the screen the canvas ended on so live collaboration
    // resumes exactly where the user is now looking.
    this.setCollabAgentBuildSuspension(false);

    // Always reset canvas animation — WsBridge tool calls dispatch MCP_ZONE_ACTIVATE
    // directly (bypassing handleToolActivity), so canvasZoneActiveTabs may not reflect
    // the true activation state. Unconditional reset is cheap and prevents stuck animations.
    this.canvasZoneActiveTabs.delete(tabId);
    window.dispatchEvent(new CustomEvent(MCP_ZONE_RESET, { detail: { zone: "canvas" } }));

    const startTime: number | undefined = this.requestStartTimes.get(tabId);
    this.requestStartTimes.delete(tabId);
    const responseDurationMs: number | undefined =
      startTime != null ? Math.round(performance.now() - startTime) : undefined;

    // Detect whether any designer-level mutation happened during the turn; the
    // success-flash animation is gated on it. Only a turn that actually
    // completed can flash — a cancelled or dropped turn did not succeed.
    const preSize: number = this.preRequestUndoStackSize.get(tabId) ?? 0;
    this.preRequestUndoStackSize.delete(tabId);
    let hadDesignerChanges: boolean = false;
    if (opts.outcome === "completed") {
      try {
        const sd = getDesignerRuntime();
        const postSize: number = sd?.screenProvider?.getUndoStackSize?.() ?? 0;
        hadDesignerChanges = postSize > preSize;
      } catch {
        // ignore
      }
    }

    const beforeSnap = this.beforeSnapshots.get(tabId);
    this.beforeSnapshots.delete(tabId);
    const afterSnap = beforeSnap ? captureSnapshot() : null;

    const suggestions = this.pendingSuggestions.get(tabId);
    this.pendingSuggestions.delete(tabId);

    // Read the archive + live list NOW, not inside the state updater, so a
    // caller that resets tool-activity state right after this call (handleStop
    // does — the turn is hard-aborted and nothing more will arrive) cannot
    // empty the archive before the fold runs.
    const liveTab: ChatTab | undefined = this.state.tabs.find((tab: ChatTab): boolean => tab.id === tabId);
    const finalToolActivities: ToolActivity[] = this.getFinalToolActivities(tabId, liveTab?.toolActivities ?? []);
    const changesSummary = finalToolActivities.length > 0 ? buildChangesSummary(finalToolActivities) : undefined;
    const completedToolActivities = buildCompletedToolActivitiesFromActivities(finalToolActivities);
    const structuralDiff =
      finalToolActivities.length > 0
        ? buildStructuralDiff(beforeSnap ?? null, afterSnap, finalToolActivities)
        : undefined;

    let finalizedAssistantMessageId: string | undefined;

    const foldTab = (tab: ChatTab): ChatTab => {
      // Defensive: clear isStreaming on any lingering specialist assistant
      // bubbles (legacy messages from before specialists were folded into the
      // tool transcript). The pipeline only emits one final model event for the
      // summary, so earlier specialist messages can be stuck on isStreaming.
      const clearedMessages: McpChatMessage[] = tab.messages.map((msg: McpChatMessage): McpChatMessage =>
        msg.role === "assistant" && msg.isStreaming && msg.streamingAgent ? { ...msg, isStreaming: false } : msg
      );
      const assistantMessage: UpsertAssistantMessageResult = this.upsertOrAppendAssistantMessage(clearedMessages, {
        isStreaming: false,
        changesSummary,
        completedToolActivities,
        responseDurationMs,
        structuralDiff,
        suggestions,
        ...(opts.chatRequestId ? { chatRequestId: opts.chatRequestId } : {})
      });
      finalizedAssistantMessageId = assistantMessage.messageId;

      const messages: McpChatMessage[] = opts.notice
        ? [
            ...assistantMessage.messages,
            {
              id: generateMessageId(),
              role: "assistant",
              content: opts.notice.content,
              timestamp: Date.now(),
              ...(opts.notice.isError ? { isError: true } : {})
            }
          ]
        : assistantMessage.messages;

      return {
        ...tab,
        messages,
        isLoading: false,
        isTextStreaming: false,
        toolActivities: [],
        ...(opts.dropQueue ? { queuedMessages: [] } : {})
      };
    };

    // Teardown paths (unmount / beforeunload) run after `isMounted_` is false,
    // where React will not process a setState — mirror the in-place mutation
    // `componentWillUnmount` already uses for the input value so the pending
    // save still carries the folded tab.
    if (opts.sync) {
      const index: number = this.state.tabs.findIndex((tab: ChatTab): boolean => tab.id === tabId);
      if (index >= 0) this.state.tabs[index] = foldTab(this.state.tabs[index]);
      if (finalizedAssistantMessageId) this.finalizedAssistantMessageIds.set(tabId, finalizedAssistantMessageId);
      return;
    }

    this.setState(
      (prev) => ({ tabs: prev.tabs.map((tab: ChatTab): ChatTab => (tab.id === tabId ? foldTab(tab) : tab)) }),
      () => {
        const container = this.messagesContainerRef.current;
        const lastMsg = this.state.tabs.find((t: ChatTab): boolean => t.id === tabId)?.messages;
        const hasError = lastMsg?.some((m: McpChatMessage): boolean => m.role === "assistant" && !!m.isError);
        if (container && !hasError && hadDesignerChanges) {
          container.classList.add("mcp-chat__messages--success-flash");
          setTimeout(() => container.classList.remove("mcp-chat__messages--success-flash"), 1300);
        }
        if (finalizedAssistantMessageId) {
          this.finalizedAssistantMessageIds.set(tabId, finalizedAssistantMessageId);
        }
        this.scrollToBottom();
        if (opts.onFinalized) opts.onFinalized();
        if (!opts.dropQueue) this.processNextQueuedMessage(tabId);
        this.scheduleSave();
      }
    );
  };

  private upsertOrAppendAssistantMessage(
    messages: McpChatMessage[],
    patch: AssistantMessagePatch
  ): UpsertAssistantMessageResult {
    return upsertAssistantMessage(messages, patch, generateMessageId);
  }

  /** Last rendered quota-warning key per tab (`kind|window`) — a warn-mode user
   *  shouldn't see the identical banner appended after every single turn. */
  private lastQuotaWarningKeys: Map<string, string> = new Map();

  /**
   * Render the per-user quota warning carried on the `done` event (KOA-18066).
   * `approaching` = ≥80% of a window's limit; `exceeded_warn` = over the limit
   * but the workspace policy allows chats to continue.
   */
  private maybeShowQuotaWarning = (tabId: string, data: Record<string, unknown>): void => {
    const warning = data?.quotaWarning as QuotaWarningPayload | undefined;
    if (!warning || typeof warning !== "object" || !warning.kind) return;

    const key: string = `${warning.kind}|${warning.window ?? ""}`;
    if (this.lastQuotaWarningKeys.get(tabId) === key) return;
    this.lastQuotaWarningKeys.set(tabId, key);

    const windowLabel: string = warning.window === "monthly" ? "monthly" : "weekly";
    const limit: string = typeof warning.limitCredits === "number" ? warning.limitCredits.toLocaleString("en-US") : "?";
    const consumed: string =
      typeof warning.consumedCredits === "number" ? warning.consumedCredits.toLocaleString("en-US") : "?";
    const message: string =
      warning.kind === "exceeded_warn"
        ? `⚠️ You've gone past your ${windowLabel} AI usage limit (${consumed} / ${limit} credits). Chats can continue under your workspace's current policy — ask your workspace owner if you need a higher limit.`
        : `⚠️ Heads up: you've used ${consumed} of your ${limit}-credit ${windowLabel} AI limit. When it's reached, your workspace policy decides whether chats pause until the window resets.`;
    this.addAssistantMessageToTab(tabId, message);
  };

  addAssistantMessageToTab = (tabId: string, content: string, isError?: boolean) => {
    const msg: McpChatMessage = {
      id: generateMessageId(),
      role: "assistant",
      content,
      timestamp: Date.now(),
      isError
    };
    this.updateTab(tabId, (tab) => ({ messages: [...tab.messages, msg] }), this.scrollToBottom);
  };

  /**
   * Insert-or-update a streaming assistant message.
   * Buffers content and flushes once per animation frame instead of per token.
   * When `serverMessageId` is provided (from the server's model event), it is
   * used as the stable message identity — the frontend no longer guesses which
   * message to update.
   */
  upsertStreamingAssistantMessage = (
    tabId: string,
    content: string,
    _completedToolActivities?: CompletedToolActivity[],
    interruptData?: InterruptData,
    isStreaming?: boolean,
    serverMessageId?: string,
    explorationContent?: string,
    streamingAgent?: string
  ) => {
    // Write to buffer (overwrites previous unflushed content for same tab).
    // The previous shape stored one streaming state per tab; with the
    // specialist pipeline multiple agents emit deltas under DIFFERENT
    // messageIds during the same request, so we also need to key buffered
    // state by serverMessageId. The flush pass reads both — see
    // flushStreamingBuffer for the per-tab + per-messageId routing.
    this.streamingBuffer.set(tabId, {
      content,
      interruptData,
      isStreaming,
      serverMessageId,
      explorationContent,
      streamingAgent
    });

    // Schedule a flush if one isn't already pending
    if (this.streamingRafId == null) {
      this.streamingRafId = requestAnimationFrame(this.flushStreamingBuffer);
    }
  };

  /**
   * Synchronous version of flushStreamingBuffer for use during beforeunload.
   * Directly mutates this.state.tabs because React won't process setState
   * during page unload. This is intentionally impure — it only runs on teardown.
   */
  private flushStreamingBufferSync(): void {
    if (this.streamingBuffer.size === 0) return;

    if (this.streamingRafId != null) {
      cancelAnimationFrame(this.streamingRafId);
      this.streamingRafId = null;
    }

    const buffered = new Map(this.streamingBuffer);
    this.streamingBuffer.clear();

    const tabs = this.state.tabs;
    for (const [tabId, { content, interruptData, serverMessageId, explorationContent, streamingAgent }] of buffered) {
      const nextContent = collapseExactDuplicateAssistantResponse(content);
      let effectiveId = serverMessageId || this.streamingMessageIds.get(tabId);
      const tab = tabs.find((t) => t.id === tabId);
      if (!tab) continue;

      let existingMsg = effectiveId ? tab.messages.find((m) => m.id === effectiveId) : undefined;
      // Keep prior questionnaire answers visible: new question rounds and the
      // final Spec each open a new bubble instead of overwriting the previous.
      if (
        shouldForkStreamingMessageForQuestionInterrupt(existingMsg, interruptData) ||
        shouldForkStreamingMessageForNonInterruptContent(existingMsg, interruptData)
      ) {
        this.streamingMessageIds.delete(tabId);
        effectiveId = undefined;
        existingMsg = undefined;
      }

      if (existingMsg && effectiveId) {
        const merged = mergeStreamingAssistantMessage(existingMsg, {
          content: nextContent,
          interruptData,
          isStreaming: false,
          explorationContent,
          streamingAgent
        });
        Object.assign(existingMsg, merged);
        if (!interruptData) {
          delete existingMsg.interruptData;
        }
        // Never reset progress on an in-place update of a non-forked message
        // unless this is the first attach of a pending question (no prior answers).
        const freshProgress = questionnaireProgressForPendingQuestion(interruptData);
        if (freshProgress && !tab.questionnaires[effectiveId]) {
          tab.questionnaires = { ...tab.questionnaires, [effectiveId]: freshProgress };
        }
      } else {
        const id = serverMessageId || generateMessageId();
        this.streamingMessageIds.set(tabId, id);
        tab.messages.push({
          id,
          role: "assistant",
          content: nextContent,
          timestamp: Date.now(),
          isStreaming: false,
          ...(interruptData ? { interruptData } : {}),
          ...(explorationContent ? { explorationContent } : {}),
          ...(streamingAgent ? { streamingAgent } : {})
        });
        const freshProgress = questionnaireProgressForPendingQuestion(interruptData);
        if (freshProgress) {
          tab.questionnaires = { ...tab.questionnaires, [id]: freshProgress };
        }
      }
    }
  }

  /** Flush buffered streaming content to React state (called once per rAF). */
  private flushStreamingBuffer = () => {
    this.streamingRafId = null;
    if (!this.isMounted_) return;
    if (this.streamingBuffer.size === 0) return;

    // Snapshot and clear the buffer before setState
    const buffered = new Map(this.streamingBuffer);
    this.streamingBuffer.clear();

    this.setState(
      (prev) => {
        let tabs = prev.tabs;
        for (const [
          tabId,
          { content, interruptData, isStreaming, serverMessageId, explorationContent, streamingAgent }
        ] of buffered) {
          const nextContent = collapseExactDuplicateAssistantResponse(content);
          // Use server-assigned messageId when available; fall back to the
          // legacy streaming slot for callers that don't provide one (e.g. interrupts).
          let effectiveId = serverMessageId || this.streamingMessageIds.get(tabId);

          // Check if a message with this ID already exists in the tab
          const tab = tabs.find((t) => t.id === tabId);
          const existingMsg =
            effectiveId && tab ? tab.messages.find((m: McpChatMessage) => m.id === effectiveId) : undefined;

          // Keep prior questionnaire answers visible across BA rounds and Spec.
          if (
            shouldForkStreamingMessageForQuestionInterrupt(existingMsg, interruptData) ||
            shouldForkStreamingMessageForNonInterruptContent(existingMsg, interruptData)
          ) {
            this.streamingMessageIds.delete(tabId);
            effectiveId = undefined;
          }

          const existsInTab =
            effectiveId !== undefined && !!tab?.messages.some((m: McpChatMessage) => m.id === effectiveId);
          const existingForMerge =
            effectiveId && existsInTab && tab
              ? tab.messages.find((m: McpChatMessage) => m.id === effectiveId)
              : undefined;

          if (effectiveId && existsInTab && existingForMerge) {
            // Update existing streaming message
            const merged = mergeStreamingAssistantMessage(existingForMerge, {
              content: nextContent,
              interruptData,
              isStreaming,
              explorationContent,
              streamingAgent
            });
            const freshProgress = questionnaireProgressForPendingQuestion(interruptData);
            tabs = tabs.map((t) => {
              if (t.id !== tabId) return t;
              const alreadyHasProgress = Boolean(t.questionnaires[effectiveId]);
              return {
                ...t,
                messages: t.messages.map((m) => (m.id === effectiveId ? merged : m)),
                // Only seed empty progress when this message has none yet —
                // never wipe answers on an in-place redelivery.
                ...(freshProgress && !alreadyHasProgress
                  ? { questionnaires: { ...t.questionnaires, [effectiveId]: freshProgress } }
                  : {})
              };
            });
          } else {
            // First chunk — create new message using server ID or generate one
            const id = serverMessageId || generateMessageId();
            this.streamingMessageIds.set(tabId, id);
            const msg: McpChatMessage = {
              id,
              role: "assistant",
              content: nextContent,
              timestamp: Date.now(),
              isStreaming: !!isStreaming,
              ...(interruptData ? { interruptData } : {}),
              ...(explorationContent ? { explorationContent } : {}),
              ...(streamingAgent ? { streamingAgent } : {})
            };
            const freshProgress = questionnaireProgressForPendingQuestion(interruptData);
            tabs = tabs.map((t) =>
              t.id === tabId
                ? withCappedMessages({
                    ...t,
                    messages: [...t.messages, msg],
                    ...(freshProgress ? { questionnaires: { ...t.questionnaires, [id]: freshProgress } } : {})
                  })
                : t
            );
          }
        }
        return { tabs };
      },
      () => {
        this.scrollToBottom();
        // If more data arrived while we were flushing, schedule another frame
        if (this.streamingBuffer.size > 0) {
          this.streamingRafId = requestAnimationFrame(this.flushStreamingBuffer);
        }
      }
    );
  };

  /**
   * True when a send would be rejected by the exhausted-balance gate. ASK mode
   * is FREE and exempt; a null/undefined balance means a non-platform
   * deployment where the gate does not apply. Single source of truth for both
   * `handleSend` and `handleRetry` so retry never trims history for a send the
   * gate will silently reject.
   */
  private isSendBlockedByBalance(tab: ChatTab): boolean {
    return (
      tab.chatMode !== "ask" &&
      this.state.remainingBalance !== null &&
      this.state.remainingBalance !== undefined &&
      this.state.remainingBalance <= 0
    );
  }

  handleRetry = (errorMessageId: string) => {
    const activeTab = this.getActiveTab();
    if (activeTab.isLoading) return;

    const errorIdx = activeTab.messages.findIndex((m) => m.id === errorMessageId);
    if (errorIdx < 0) return;

    let userMsgIdx = -1;
    for (let i = errorIdx - 1; i >= 0; i--) {
      if (activeTab.messages[i].role === "user") {
        userMsgIdx = i;
        break;
      }
    }
    if (userMsgIdx < 0) return;

    const userMessage = activeTab.messages[userMsgIdx].content;

    // If the exhausted-balance gate would reject the re-send, do NOT trim the
    // conversation. handleRetry trims optimistically assuming handleSend will
    // re-add the user turn + a fresh reply; when the gate blocks the send that
    // re-add never happens, so an unguarded trim permanently deletes the last
    // real exchange (and every retry click eats one turn further back). Surface
    // the run-out warning via handleSend without touching history instead.
    if (this.isSendBlockedByBalance(activeTab)) {
      this.handleSend(
        userMessage,
        activeTab.messages[userMsgIdx].selectedElementAttachments,
        activeTab.messages[userMsgIdx].selectedElementSegments
      );
      return;
    }

    const trimmedMessages = activeTab.messages.slice(0, userMsgIdx);
    this.updateActiveTab(
      () => ({ messages: trimmedMessages }),
      () => {
        this.handleSend(
          userMessage,
          activeTab.messages[userMsgIdx].selectedElementAttachments,
          activeTab.messages[userMsgIdx].selectedElementSegments
        );
      }
    );
  };

  /**
   * Returns the id of the most recent user message that has a baseline
   * snapshot attached — i.e. the message the last-AI-message "Undo" button
   * should rewind to. Cached on messages reference for O(1) re-renders.
   */
  getMostRecentUndoableMessageId = (): string | null => {
    const tab = this.getActiveTab();
    const messages = tab.messages;
    const rewindEnabled = this.state.rewindEnabled === true;

    if (this._cachedUndoableInput === messages && this._cachedUndoableRewindEnabled === rewindEnabled) {
      return this._cachedUndoableId;
    }

    let result: string | null = null;
    if (rewindEnabled) {
      for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg.role === "user" && msg.stagedOpIdsBeforeSend !== undefined) {
          result = msg.id;
          break;
        }
      }
    }

    this._cachedUndoableInput = messages;
    this._cachedUndoableRewindEnabled = rewindEnabled;
    this._cachedUndoableId = result;
    return result;
  };

  /**
   * Open the revert-history modal for the active tab. Fetches current staged
   * entities once, then runs `buildCheckpointList` against the tab's message
   * history so every card reflects the live diff. Deliberately snapshots the
   * tab id so a later tab switch doesn't leak a different conversation's
   * checkpoints into the open modal.
   */
  handleHistoryOpen = async (): Promise<void> => {
    if (this.state.rewindEnabled !== true) return;
    const tab = this.getActiveTab();
    if (!tab) return;

    const projectId = getActiveProjectId();
    if (!projectId) return;

    this.setState({
      historyModal: { tabId: tab.id, checkpoints: [], isLoading: true, errorText: null }
    });

    try {
      // Run the staged-entities + archived-tabs fetches in parallel — they
      // don't depend on each other and both are needed before we can render
      // the unified checkpoint list.
      const [entitiesRes, archivedTabs] = await Promise.all([
        BranchingService.GetStagedDraftEntities(projectId),
        loadArchivedChatTabs(projectId)
      ]);
      if (!this.isMounted_) return;
      const currentTab = this.state.tabs.find((t) => t.id === tab.id);
      if (!currentTab) {
        // Tab was closed while we were fetching — drop the modal silently.
        this.setState({ historyModal: null });
        return;
      }
      // Aggregate checkpoints across ALL tabs — live + archived — so the
      // user gets a unified timeline going back across closed sessions.
      const liveTabInputs = this.state.tabs.map((t) => ({
        tabId: t.id,
        tabName: t.name,
        messages: t.messages
      }));
      const archivedTabInputs = archivedTabs.map((t) => ({
        tabId: t.id,
        tabName: t.name,
        messages: t.messages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
          stagedOpIdsBeforeSend: m.stagedOpIdsBeforeSend,
          ddlOps: m.ddlOps
        })),
        isArchived: true
      }));
      const tabInputs = [...liveTabInputs, ...archivedTabInputs];
      const checkpoints = buildCheckpointList(tabInputs, entitiesRes.data || [], this.resolveComponentLabel);
      this.setState((prev) => ({
        historyModal:
          prev.historyModal && prev.historyModal.tabId === tab.id
            ? { ...prev.historyModal, checkpoints, isLoading: false }
            : prev.historyModal
      }));
    } catch (err) {
      console.warn("[rewind] history fetch failed", err);
      if (!this.isMounted_) return;
      this.setState((prev) => ({
        historyModal:
          prev.historyModal && prev.historyModal.tabId === tab.id
            ? { ...prev.historyModal, isLoading: false, errorText: "Failed to load history. Try again." }
            : prev.historyModal
      }));
    }
  };

  handleHistoryClose = (): void => {
    this.setState({ historyModal: null });
  };

  /**
   * User picked a checkpoint in the history modal. Three cases:
   *  - Same active tab → just dispatch rewind.
   *  - Different LIVE tab → switch tabs first, then dispatch.
   *  - Archived tab → no tab to switch to. Use the checkpoint snapshot
   *    from the modal (which has the baseline-opIds preserved) to drive
   *    rewind directly, without touching `state.tabs`.
   */
  handleHistorySelectCheckpoint = (userMsgId: string, tabId: string): void => {
    const checkpoint = this.state.historyModal?.checkpoints.find(
      (cp) => cp.userMsgId === userMsgId && cp.tabId === tabId
    );
    const isArchived = checkpoint?.isArchived === true;

    this.setState({ historyModal: null }, () => {
      if (isArchived && checkpoint) {
        this.handleArchivedRewindRequest(checkpoint);
        return;
      }
      const needsSwitch = tabId !== this.state.activeTabId;
      if (needsSwitch) {
        this.handleSwitchTab(tabId);
        // Let React commit the tab switch before the rewind dialog reads
        // the now-active tab's messages/baseline.
        setTimeout(() => this.handleRewindRequest(userMsgId), 0);
      } else {
        this.handleRewindRequest(userMsgId);
      }
    });
  };

  /**
   * Rewind to a checkpoint that lives in an archived (closed/cleared)
   * chat. Skips the tab-lookup dance in `handleRewindRequest` — we already
   * have the computed summary and baseline-backed opIds from the history
   * modal. Confirming discards the ops; the archived conversation itself
   * stays untouched (the tab is already gone from the UI).
   */
  private handleArchivedRewindRequest = (checkpoint: CheckpointDescriptor): void => {
    if (this.state.rewindEnabled !== true) return;
    this.setState({
      rewindDialog: {
        // The archived tab is the "owning" tab for confirm-time bookkeeping.
        // handleRewindConfirm's tab-lookup will fall through to its null
        // branch for archived tabs and short-circuit to the discard call.
        tabId: checkpoint.tabId,
        userMsgId: checkpoint.userMsgId,
        summary: checkpoint.summary,
        errorText: null,
        isInFlight: false,
        isLoadingSummary: false
      }
    });
  };

  /**
   * Compute the baseline for a rewind target and open the confirmation
   * dialog. Rolled-back scope is the diff between the project's current
   * staged-op set and the baseline captured when the user sent the target
   * message. All subsequent messages in the tab are also in scope (they
   * share the same draft and will be removed from the conversation too).
   */
  handleRewindRequest = async (userMsgId: string): Promise<void> => {
    const tab = this.getActiveTab();
    if (tab.isLoading) return;
    if (this.state.rewindEnabled !== true) return;

    const targetIdx = tab.messages.findIndex((m) => m.id === userMsgId && m.role === "user");
    if (targetIdx < 0) return;
    const targetMsg = tab.messages[targetIdx];
    if (targetMsg.stagedOpIdsBeforeSend === undefined) return;

    // Aggregate DDL ops from the target message and every subsequent message
    // (later messages may have added more DDL after this checkpoint).
    const ddlOps: DdlOp[] = [];
    for (let i = targetIdx; i < tab.messages.length; i++) {
      const m = tab.messages[i];
      if (m.ddlOps && m.ddlOps.length > 0) ddlOps.push(...m.ddlOps);
    }

    const messagesToDeleteCount = tab.messages.length - targetIdx;

    // Open dialog with a placeholder summary while we fetch current entities.
    this.setState({
      rewindDialog: {
        tabId: tab.id,
        userMsgId,
        summary: {
          opIdsToDiscard: [],
          buckets: [],
          ddlOps,
          messagesToDeleteCount,
          isEmpty: false
        },
        errorText: null,
        isInFlight: false,
        isLoadingSummary: true
      }
    });

    const projectId = getActiveProjectId();
    if (!projectId) {
      this.setState({
        rewindDialog: { ...this.state.rewindDialog!, errorText: "Project not loaded.", isLoadingSummary: false }
      });
      return;
    }

    try {
      const res = await BranchingService.GetStagedDraftEntities(projectId);
      if (!this.isMounted_) return;
      const entities = res.data || [];
      const summary = computeRewindSummary(
        targetMsg.stagedOpIdsBeforeSend,
        entities,
        ddlOps,
        messagesToDeleteCount,
        this.resolveComponentLabel
      );
      const currentOpCount = entities.reduce((n, e) => n + (e.patch?.length || 0), 0);
      const baselineSet = new Set(targetMsg.stagedOpIdsBeforeSend ?? []);
      const newOpsByEntity = entities
        .map((e) => ({
          entityType: e.entityType,
          entityId: e.entityId,
          newOps: (e.patch || [])
            .filter((o) => o.opId && !baselineSet.has(o.opId))
            .map((o) => ({ op: o.op, path: o.path, opId: o.opId }))
        }))
        .filter((e) => e.newOps.length > 0);
      // Guard against races: if the user opened a different message's dialog
      // while this fetch was in flight, don't overwrite their newer state.
      this.setState((prev) => ({
        rewindDialog:
          prev.rewindDialog && prev.rewindDialog.userMsgId === userMsgId
            ? { ...prev.rewindDialog, summary, isLoadingSummary: false }
            : prev.rewindDialog
      }));
    } catch (err) {
      console.warn("[rewind] summary fetch failed", err);
      if (!this.isMounted_) return;
      this.setState((prev) => ({
        rewindDialog:
          prev.rewindDialog && prev.rewindDialog.userMsgId === userMsgId
            ? { ...prev.rewindDialog, errorText: "Failed to load changes. Try again.", isLoadingSummary: false }
            : prev.rewindDialog
      }));
    }
  };

  handleRewindCancel = (): void => {
    this.setState({ rewindDialog: null });
  };

  handleRewindConfirm = async (): Promise<void> => {
    const dialog = this.state.rewindDialog;
    if (!dialog || dialog.isInFlight) return;

    // Resolve the dialog's origin tab. For LIVE tabs we trim the message
    // list and push the original content back into the input. For ARCHIVED
    // tabs (launched from the history modal) there is no live conversation
    // to trim — the tab no longer exists in `state.tabs`, so we just run
    // the discard against the project and close the dialog.
    const tabId = dialog.tabId;
    const tab = this.state.tabs.find((t) => t.id === tabId);
    const isArchivedRewind = !tab;

    let targetIdx = -1;
    let targetContent = "";
    if (!isArchivedRewind) {
      targetIdx = tab.messages.findIndex((m) => m.id === dialog.userMsgId && m.role === "user");
      if (targetIdx < 0) {
        this.setState({ rewindDialog: null });
        return;
      }
      targetContent = tab.messages[targetIdx].content;
    }

    const projectId = getActiveProjectId();
    if (!projectId) {
      this.setState({ rewindDialog: { ...dialog, errorText: "Project not loaded." } });
      return;
    }

    // Stop any in-flight AI request on the ORIGIN tab (live-only). Archived
    // tabs can't be "loading" — they have no active session/websocket.
    if (tab && tab.isLoading) {
      const chatRequestId = this.activeChatRequestIds.get(tabId);
      const bridge = getMcpBridgeInstance();
      if (chatRequestId && bridge) {
        bridge.sendChatCancel(chatRequestId, true);
        bridge.offChatEvent(chatRequestId);
        this.activeChatRequestIds.delete(tabId);
      }
      this.requestStartTimes.delete(tabId);
      this.streamingMessageIds.delete(tabId);
      this.beforeSnapshots.delete(tabId);
    }

    this.setState({ rewindDialog: { ...dialog, isInFlight: true, errorText: null } });

    let discardSucceeded = false;
    try {
      if (dialog.summary.opIdsToDiscard.length > 0) {
        const discardRes = await BranchingService.DiscardDraftStagedOps(dialog.summary.opIdsToDiscard, projectId);
      } else {
        console.warn("[rewind] nothing to discard — opIdsToDiscard is empty", {
          baselineOpCount: 0,
          summary: dialog.summary
        });
      }
      discardSucceeded = true;
    } catch (err: unknown) {
      console.warn("[rewind] discard threw", err);
      if (!this.isMounted_) return;
      const msg = err instanceof Error ? err.message : "Revert failed.";
      this.setState((prev) => ({
        rewindDialog: prev.rewindDialog ? { ...prev.rewindDialog, isInFlight: false, errorText: msg } : null
      }));
      return;
    }

    // Reload is best-effort: backend state is already reversed at this point.
    // If reload fails the canvas stays stale until next refresh — we still
    // must trim the conversation to reflect reality, otherwise the user sees
    // ghost messages referencing ops that no longer exist.
    await this.reloadProjectAfterRewind();

    if (!this.isMounted_) return;

    if (discardSucceeded && !isArchivedRewind) {
      const isActiveTab = this.state.activeTabId === tabId;
      this.updateTab(
        tabId,
        (t) => ({
          messages: t.messages.slice(0, targetIdx),
          inputValue: targetContent,
          isLoading: false,
          isTextStreaming: false,
          toolActivities: [],
          attachedImages: [],
          attachedFiles: [],
          selectedElementAttachments: [],
          mentions: [],
          mentionCounter: 0
        }),
        () => {
          // Only push the message content back into the input area when the
          // origin tab is currently visible — otherwise the input belongs to
          // a different tab and would be clobbered.
          if (isActiveTab) {
            this.currentInputValue = targetContent;
            this.inputAreaRef.current?.setContent?.(targetContent);
          }
          this.flushSave();
        }
      );
    }
    // Archived rewind: ops are discarded + project reloaded; there's no
    // live tab to trim and the archive copy is intentionally left intact
    // so the user can inspect the old conversation's context later.

    this.setState({ rewindDialog: null });
  };

  /**
   * Refresh the designer's in-memory project/screen state after backend ops
   * have been reversed. The MCP tool path (ui_project.reload_project) can
   * silently drop its dispatch when an MCP defer-render flag is stuck from
   * a prior batch or when the screen id URL now points to a soft-deleted
   * row. We bypass the tool wrapper and:
   *   1. Clear the MCP defer-render flag so setScreenDesign propagates.
   *   2. Clear ScreenService's projectId cache (the screen row may have
   *      been soft-deleted, and the cache would otherwise serve a stale
   *      projectId lookup).
   *   3. Call projectProvider.openProject(true) directly to force a
   *      fresh GET against the backend and a CHANGE_SD dispatch.
   *   4. As a safety net when reload can't recover (e.g. current screen
   *      no longer exists on disk), fall through to a full page reload.
   */
  /**
   * Suspend / resume this tab's collab participation around an agent turn.
   * Best-effort on purpose: collaboration is a convenience, and a build must
   * never fail because presence bookkeeping did.
   */
  private setCollabAgentBuildSuspension = (suspended: boolean): void => {
    try {
      const sd = getDesignerRuntime();
      const collab = sd?.collabProvider;
      if (!collab) return;
      if (suspended) {
        collab.suspendCollabForAgentBuild?.();
        return;
      }
      const screenId: string | undefined = sd?.screenProvider?.getScreenDesign?.()?.id;
      void collab.resumeCollabAfterAgentBuild?.(screenId ?? "");
    } catch {
      // Non-fatal — worst case collab stays suspended until the next screen load.
    }
  };

  private reloadProjectAfterRewind = async (): Promise<void> => {
    // Ensure cached staged state for this tab is wiped — it reflects pre-rewind
    // data and would leak back into the next LLM turn.
    try {
      const kuika = getKuikaWindow().kuika;
      if (kuika && kuika._mcpDeferRender) kuika._mcpDeferRender = false;
    } catch {
      // Non-fatal — worst case we fall through to a full reload below.
    }
    try {
      ScreenService.clearProjectIdCache();
    } catch {
      // Non-fatal.
    }

    const sd = getDesignerRuntime();
    const openProject: ((force: boolean) => Promise<void> | void) | undefined = sd?.projectProvider?.openProject?.bind(
      sd.projectProvider
    );
    if (!openProject) {
      // Fallback: no designer instance — reload the whole page.
      try {
        window.location.reload();
      } catch {
        // ignored
      }
      return;
    }

    try {
      // openProject() uses processWithSpinner internally which does NOT await
      // its inner delegate — the Promise it returns resolves immediately.
      // We give the delegate a reasonable window to finish (fetch + dispatch)
      // before returning so the caller's conversation-trim runs against an
      // already-updated canvas.
      openProject(true);
      await this.waitForProjectReload();
    } catch {
      // Reload failed — surface as a warning but don't block the chat trim.
    }
  };

  /**
   * Poll `projectProvider.state.isProcessingWithSpinner` until it flips back
   * to false (delegate finished) or a max timeout elapses. Needed because
   * `openProject()` fires a setState and doesn't propagate its inner
   * `await delegate()` to the outer caller's Promise chain.
   */
  private waitForProjectReload = async (maxWaitMs: number = 5000): Promise<void> => {
    const deadline = performance.now() + maxWaitMs;
    const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));
    // Give the initial setState a tick to apply so we don't observe pre-flip state.
    await sleep(50);
    while (performance.now() < deadline) {
      const sd = getDesignerRuntime();
      const busy = sd?.projectProvider?.state?.isProcessingWithSpinner === true;
      if (!busy) return;
      await sleep(100);
    }
  };

  handleStop = () => {
    const activeTab = this.getActiveTab();
    if (!activeTab.isLoading) return;

    // Synchronously flush streaming buffer to preserve partial content
    if (this.streamingRafId != null) {
      cancelAnimationFrame(this.streamingRafId);
      this.streamingRafId = null;
    }
    if (this.streamingBuffer.size > 0) {
      this.flushStreamingBuffer();
    }

    const chatRequestId = this.activeChatRequestIds.get(activeTab.id);
    if (chatRequestId) {
      const bridge = getMcpBridgeInstance();
      if (bridge) {
        // Force hard abort — the UI immediately hides the stop button and sets
        // isLoading: false, so the user cannot press a second time to escalate.
        // Without force, only a soft interrupt flag is set and the server-side
        // agent loop continues until the current LLM/tool round finishes.
        bridge.sendChatCancel(chatRequestId, true);
      }
    }

    // Close the turn through the SAME path a normal finish uses, so the work
    // that already succeeded keeps its tool cards and specialist groups and the
    // partial answer stops being marked as still-streaming. This used to be a
    // hand-rolled partial finalize that wiped `toolActivities` and, worse,
    // reset the archive first — a stopped build lost every card it had earned
    // (KOA-17572). `finalizeTurn` reads the activities synchronously, so the
    // archive reset below cannot race it.
    this.finalizeTurn(activeTab.id, {
      outcome: "cancelled",
      chatRequestId,
      notice: { content: "Request cancelled by user." },
      dropQueue: true,
      onFinalized: () => {
        this.forceScrollToBottom();
        this.inputAreaRef.current?.focus();
      }
    });
    // Hard abort — nothing more will arrive for this turn, so the archive that
    // exists to survive `done` timing races has no further purpose.
    this.resetToolActivityState(activeTab.id);
  };

  /** Dequeue and send the next queued message for the given tab, or focus the input if the queue is empty. */
  processNextQueuedMessage = (tabId: string) => {
    const tab = this.state.tabs.find((t) => t.id === tabId);
    if (tab && tab.queuedMessages.length > 0) {
      const [next, ...rest] = tab.queuedMessages;
      // Restore queued attachments to the tab so handleSend picks them up
      this.updateTab(
        tabId,
        () => ({
          queuedMessages: rest,
          attachedImages: next.images,
          attachedFiles: next.files,
          selectedElementAttachments: next.selectedElementAttachments,
          mentions: next.mentions
        }),
        () => {
          this.handleSend(next.text);
        }
      );
    } else {
      this.inputAreaRef.current?.focus();
    }
  };

  handleCancelQueuedMessage = (index: number) => {
    this.updateActiveTab(
      (tab) => ({
        queuedMessages: tab.queuedMessages.filter((_, i) => i !== index)
      }),
      () => {
        this.inputAreaRef.current?.focus();
      }
    );
  };

  handleCancelAllQueuedMessages = () => {
    this.updateActiveTab(
      () => ({ queuedMessages: [] }),
      () => {
        this.inputAreaRef.current?.focus();
      }
    );
  };

  clearSession = (sessionId: string | null) => {
    if (!sessionId) return;
    const bridge = getMcpBridgeInstance();
    if (bridge && bridge.isConnected) {
      const clearRequestId = `clear_${Date.now()}`;
      bridge.sendChatClear(clearRequestId, sessionId);
    }
  };

  handleClearChat = () => {
    const activeTab = this.getActiveTab();

    // Hard-abort any in-flight chat for this tab BEFORE clearing the session
    // — otherwise the server-side agent keeps running, its tool dispatches
    // still mutate the canvas, and the next prompt the user sends gets
    // queued onto the dying session and replays the build a second time.
    // Mirrors handleStop's bridge cancel without the user-visible "cancelled"
    // chat message (clear has its own UX).
    const activeReqId = this.activeChatRequestIds.get(activeTab.id);
    if (activeReqId) {
      if (this.streamingRafId != null) {
        cancelAnimationFrame(this.streamingRafId);
        this.streamingRafId = null;
      }
      if (this.streamingBuffer.size > 0) {
        this.flushStreamingBuffer();
      }
      const bridge = getMcpBridgeInstance();
      if (bridge) {
        bridge.sendChatCancel(activeReqId, true);
        bridge.offChatEvent(activeReqId);
      }
      this.activeChatRequestIds.delete(activeTab.id);
      this.requestStartTimes.delete(activeTab.id);
      this.streamingMessageIds.delete(activeTab.id);
      this.beforeSnapshots.delete(activeTab.id);
      this.canvasZoneActiveTabs.delete(activeTab.id);
      window.dispatchEvent(new CustomEvent(MCP_ZONE_RESET, { detail: { zone: "canvas" } }));
    }

    this.deactivateInspector(false, false);
    this.clearSession(activeTab.sessionId);
    this.currentInputValue = "";
    this.resetToolActivityState(activeTab.id);

    // Mint a fresh tab id so the next saveState can INSERT without
    // colliding on the (project_id, id) PK held by the archived row the
    // server will produce. Both paths archive the OLD id: an immediate
    // POST for responsiveness, plus `pendingArchiveTabIds` so the next
    // save does it atomically even if the POST races.
    const oldTabId = activeTab.id;
    const freshTabId = generateTabId();
    this.pendingArchiveTabIds.add(oldTabId);
    archiveTab(this.loadedProjectId, oldTabId);

    this.setState(
      (prev) => ({
        activeTabId: prev.activeTabId === oldTabId ? freshTabId : prev.activeTabId,
        tabs: prev.tabs.map((t) =>
          t.id !== oldTabId
            ? t
            : {
                ...t,
                id: freshTabId,
                name: "Chat 1",
                isCustomName: false,
                messages: [],
                sessionId: null,
                inputValue: "",
                toolActivities: [],
                expandedCategory: null,
                attachedImages: [],
                attachedFiles: [],
                questionnaires: {},
                visibleMessageCount: MESSAGES_PAGE_SIZE,
                selectedElementAttachments: [],
                mentions: [],
                mentionCounter: 0,
                queuedMessages: []
              }
        )
      }),
      () => {
        this.inputAreaRef.current?.clearContent();
        this.inputAreaRef.current?.resetHeight();
        this.inputAreaRef.current?.focus();
        this.scheduleSave();
      }
    );
  };

  // ── Slash Commands ──

  addSystemMessage = (content: string) => {
    const msg: McpChatMessage = {
      id: generateMessageId(),
      role: "assistant",
      content,
      timestamp: Date.now()
    };
    this.updateActiveTab((tab) => ({ messages: [...tab.messages, msg] }), this.scrollToBottom);
  };

  clearInput = () => {
    this.currentInputValue = "";
    this.historyIndex = -1;
    this.historyDraft = "";
    this.updateActiveTab(
      () => ({ inputValue: "", mentions: [], mentionCounter: 0 }),
      () => {
        this.inputAreaRef.current?.clearContent();
        this.inputAreaRef.current?.resetHeight();
        this.inputAreaRef.current?.focus();
      }
    );
  };

  handleChatModeChange = (mode: import("../shared/mcp-chat-types").ChatMode): void => {
    const activeTab = this.getActiveTab();
    this.updateTab(activeTab.id, () => (mode === "ask" ? { chatMode: mode, planMode: false } : { chatMode: mode }));
  };

  /**
   * "How do I do this?" on a "Couldn't do" item: switch to Ask and send the question.
   *
   * The mode switch and the send MUST be one atomic step. `handleSend` reads `getActiveTab()`
   * synchronously and serializes `chatMode` into the request body, while `handleChatModeChange`
   * is a plain `setState` with no callback — calling the two back to back sends the question in
   * whatever mode the tab was already in. Driving the send from `updateTab`'s completion callback
   * is the same shape `handlePlanApprove` and `handleSpecApprove` use.
   *
   * Called from the message renderer through `getMcpChatPanelInstance()` rather than a prop,
   * because the message is rendered from two sites (this panel and the docked wrapper) and a prop
   * threaded through only one of them breaks docked mode silently.
   */
  askFollowUp = (question: string): boolean => {
    const trimmed: string = question.trim();
    if (!trimmed) return false;
    const activeTab = this.getActiveTab();
    // A turn is already in flight; queueing an Ask behind it would flip the mode under it.
    // Reported back so the caller can leave the button clickable instead of dead-ending it.
    if (activeTab.isLoading) return false;
    this.updateTab(
      activeTab.id,
      () => ({ chatMode: "ask" as const, planMode: false }),
      () => {
        void this.handleSend(trimmed);
      }
    );
    return true;
  };

  handleModelTierChange = (effort: EffortTier): void => {
    const activeTab = this.getActiveTab();
    this.updateTab(activeTab.id, () => ({ effort }));
    setStoredEffort(effort);
  };

  handlePlanModeToggle = () => {
    this.updateActiveTab((t) => ({ planMode: !t.planMode }));
  };

  /** Returns user message contents (most recent last) for history navigation. */
  private getUserMessages = (): string[] => {
    const tab = this.getActiveTab();
    return tab.messages.filter((m) => m.role === "user" && m.content.trim()).map((m) => m.content);
  };

  /** ArrowUp handler — returns true if event was consumed. */
  handleHistoryUp = (): boolean => {
    const messages = this.getUserMessages();
    if (messages.length === 0) return false;

    if (this.historyIndex === -1) {
      // Not browsing yet — only enter history if input is empty
      if (this.currentInputValue.trim()) return false;
      this.historyDraft = this.currentInputValue;
      this.historyIndex = messages.length - 1;
    } else if (this.historyIndex > 0) {
      this.historyIndex--;
    } else {
      return true; // already at oldest, consume to prevent cursor movement
    }

    const text = messages[this.historyIndex];
    this.currentInputValue = text;
    this.inputAreaRef.current?.setContent(text);
    return true;
  };

  /** ArrowDown handler — returns true if event was consumed. */
  handleHistoryDown = (): boolean => {
    if (this.historyIndex === -1) return false; // not browsing

    const messages = this.getUserMessages();
    if (this.historyIndex < messages.length - 1) {
      this.historyIndex++;
      const text = messages[this.historyIndex];
      this.currentInputValue = text;
      this.inputAreaRef.current?.setContent(text);
    } else {
      // Past newest → restore draft and exit history
      this.currentInputValue = this.historyDraft;
      this.inputAreaRef.current?.setContent(this.historyDraft);
      this.historyIndex = -1;
      this.historyDraft = "";
    }
    return true;
  };

  handlePlanApprove = (messageId: string) => {
    const tab = this.getActiveTab();
    if (tab.isLoading) return;

    // KOA-18373 — drive the turn with the APPROVED PLAN'S OWN MARKDOWN, the way
    // `handleSpecApprove` drives it with the spec's. The previous trigger was a
    // fixed sentence that failed twice over: it did not resend the plan (it
    // referred to "the plan above" and relied on the plan surviving in
    // conversation history, which compaction does not guarantee), and its
    // second clause explicitly LICENSED adapting away from the very thing the
    // user had just approved. An approval that authorises deviating from what
    // was approved cannot mean anything. The retired wording is quoted in
    // `__tests__/plan-approval-drive.test.ts`, which pins that it stays gone.
    //
    // This does NOT make the approval enforceable — a plan carries no version
    // footer, no content hash and no change map, so nothing downstream can
    // check the run against it (unlike the change-spec path, where
    // `approveChangeMapForSession` binds the approval to an exact render).
    // Making plan mode terminate in a hash-bound commitment document is the
    // structural fix and is deliberately NOT attempted here.
    const planMarkdown = tab.messages.find((m) => m.id === messageId)?.content ?? "";
    const driveMessage = `${PLAN_APPROVED_DIRECTIVE}\n\n${planMarkdown}`;

    // Mark the plan approved and leave plan mode (agent mode is preserved), then
    // send. No visible user bubble — the plan card above carries the approval.
    this.updateActiveTab(
      (t) => ({
        planMode: false,
        messages: t.messages.map((m) => (m.id === messageId ? { ...m, planStatus: "approved" as const } : m))
      }),
      () => {
        this.handleSend("", undefined, undefined, {
          driveMessageOverride: driveMessage,
          suppressUserBubble: true
        });
      }
    );
  };

  handlePlanReject = (messageId: string) => {
    this.updateActiveTab((t) => ({
      messages: t.messages.map((m) => (m.id === messageId ? { ...m, planStatus: "rejected" as const } : m))
    }));
    this.addSystemMessage("Plan cancelled.");
  };

  // Business Analyst spec approval (KOA-17628). "Build" sends the fixed trigger
  // that the orchestrator matches to flip the gate to "approved" and run the
  // pipeline with the spec injected. "Revise" just hides the buttons — the
  // user then types changes, which the BA agent folds into an updated spec.
  /**
   * KOA-18422 — approve a change plan.
   *
   * Sends the DELTA ID, not the plan's markdown: the server loads the recorded
   * delta and executes exactly what the card was rendered for. The BA spec card
   * beside this one re-drives the turn with its own text — right for a spec that
   * should be re-read as a build request, wrong for a change that was already
   * understood and only needs a yes.
   */
  handleChangeApprove = (messageId: string, deltaId: string) => {
    this.decideChangePlan(messageId, deltaId, "approved");
  };

  /** Revise: the plan is filed as rejected and the user says what they meant instead. */
  handleChangeReject = (messageId: string, deltaId: string) => {
    this.decideChangePlan(messageId, deltaId, "rejected");
  };

  /**
   * Decide a change plan: flip the card, send the decision, and put the card
   * BACK if the send never left the browser.
   *
   * The badge is what hides the buttons, so an optimistic flip over a send that
   * silently returned strands the card — decided in the UI, untouched on the
   * server, and impossible to decide again. Both buttons run through here so
   * neither can grow its own version of that rule.
   */
  private decideChangePlan(messageId: string, deltaId: string, status: "approved" | "rejected"): void {
    const tab = this.getActiveTab();
    if (tab.isLoading || !deltaId) return;
    const tabId = tab.id;
    const intent = status === "approved" ? "apply_change_delta" : "discard_change_delta";
    this.setChangeStatus(tabId, messageId, status);
    this.handleSend("", undefined, undefined, {
      suppressUserBubble: true,
      intent,
      changeDeltaId: deltaId
    }).then((sent: boolean) => {
      if (!sent) this.setChangeStatus(tabId, messageId, undefined);
    });
  }

  /** Set (or clear) the decision badge on one change-plan message. */
  private setChangeStatus(tabId: string, messageId: string, status: "approved" | "rejected" | undefined): void {
    this.updateTab(tabId, (t) => ({
      messages: t.messages.map((m) => {
        if (m.id !== messageId) return m;
        if (status === undefined) {
          const { changeStatus: _cleared, ...withoutStatus } = m;
          return withoutStatus;
        }
        return { ...m, changeStatus: status };
      })
    }));
  }

  handleSpecApprove = (messageId: string) => {
    const tab = this.getActiveTab();
    if (tab.isLoading) return;

    // KOA-17628 — drive the pipeline with the BA's `## Spec:` markdown itself (the
    // approved spec message's content), as if the user typed it, plus the
    // `spec_approved` flag. No visible user bubble — the spec card above flips to
    // the "approved — building" badge and the turn goes straight to thinking.
    const specMarkdown = tab.messages.find((m) => m.id === messageId)?.content ?? "";

    this.updateActiveTab(
      (t) => ({
        // KOA-18373 — leave plan mode on approval. A plan-mode turn now ends in
        // this very spec (see `plan-mode-route.ts`), so a toggle left ON would
        // send the user's NEXT message back to the Business Analyst to
        // re-author a spec they already approved. The server refuses to
        // re-author on the approval turn itself (`planModeTurnIsInsideBaChain`);
        // this is the turn AFTER that, which only the client can prevent.
        planMode: false,
        messages: t.messages.map((m) => (m.id === messageId ? { ...m, specStatus: "approved" as const } : m))
      }),
      () => {
        this.handleSend("", undefined, undefined, {
          driveMessageOverride: specMarkdown,
          suppressUserBubble: true,
          specApproved: true
        });
      }
    );
  };

  handleSpecReject = (messageId: string) => {
    this.updateActiveTab((t) => ({
      messages: t.messages.map((m) => (m.id === messageId ? { ...m, specStatus: "rejected" as const } : m))
    }));
    this.addSystemMessage("Tell me what to change and I'll revise the specification.");
  };

  /** Shared event handler for interrupt responses (approve, approve_all, answer). */
  /** Revert an interrupt message status back to "pending" so buttons reappear. */
  private revertInterruptStatus(tabId: string, messageId: string): void {
    this.updateTab(tabId, (t) => ({
      messages: t.messages.map((m) =>
        m.id === messageId && m.interruptData
          ? { ...m, interruptData: { ...m.interruptData, status: "pending" as const } }
          : m
      ),
      isLoading: false
    }));
  }

  private setupInterruptResponseHandler(
    tabId: string,
    requestId: string,
    bridge: McpToolBridge,
    sourceMessageId: string
  ): void {
    bridge.onChatEvent(requestId, (evt: ChatEventData) => {
      if (!this.isMounted_) return;
      const { event: eventType, data } = evt;

      if (eventType === "model" && data.message) {
        this.upsertStreamingAssistantMessage(tabId, data.message);
      } else if (eventType === "routing") {
        // Continuation after an interrupt answer — same reason as above.
        this.notePhase(tabId, data);
      } else if (eventType === "tool_activity") {
        this.noteBackendToolTitle(tabId, data);
        this.dispatchStreamToolActivity(tabId, data);
      } else if (eventType === "interrupt") {
        this.streamingMessageIds.delete(tabId);

        // Agent loop paused — reset canvas animation while waiting for user input
        if (this.canvasZoneActiveTabs.has(tabId)) {
          this.canvasZoneActiveTabs.delete(tabId);
          window.dispatchEvent(new CustomEvent(MCP_ZONE_RESET, { detail: { zone: "canvas" } }));
        }

        const pending = data.pendingToolCall;
        if (pending) {
          let newInterrupt: InterruptData;
          if (data?.type === "question") {
            newInterrupt = {
              type: "question",
              toolCallId: pending.id,
              toolName: pending.tool_name,
              parameters: pending.arguments || {},
              status: "pending",
              // Server-scoped: only the flow where handing a question back is
              // meaningful offers the delegate buttons. A consent card does not.
              allowDelegate: data?.allowDelegate === true
            } as QuestionInterruptData;
          } else {
            const newParts = getInterruptDescriptionParts(pending.tool_name, pending.arguments || {});
            newInterrupt = {
              type: "confirmation",
              toolCallId: pending.id,
              toolName: pending.tool_name,
              parameters: pending.arguments || {},
              description: getInterruptDescription(pending.tool_name, pending.arguments || {}),
              actionText: newParts.action,
              targetName: newParts.targetName,
              targetKuikaId: newParts.targetKuikaId,
              detailItems: newParts.detailItems,
              status: "pending"
            } as ConfirmationInterruptData;
          }
          const displayMsg = newInterrupt.type === "confirmation" ? newInterrupt.description : "Clarifying questions";
          this.upsertStreamingAssistantMessage(tabId, displayMsg, undefined, newInterrupt);
        }
      } else if (eventType === "done") {
        // Post-interrupt continuation usually finalizes via the original
        // chatRequestId listener (server emitEvent still uses that id). This
        // path is a safety net when done is routed on the interrupt requestId
        // — keep wall-clock duration from the original handleSend start.
        this.streamingMessageIds.delete(tabId);
        const startTime = this.requestStartTimes.get(tabId);
        this.requestStartTimes.delete(tabId);
        const responseDurationMs: number | undefined =
          startTime != null ? Math.round(performance.now() - startTime) : undefined;

        // Reset canvas animation — interrupt response may have executed tools
        this.canvasZoneActiveTabs.delete(tabId);
        window.dispatchEvent(new CustomEvent(MCP_ZONE_RESET, { detail: { zone: "canvas" } }));

        let finalizedAssistantMessageId: string | undefined;
        this.setState(
          (prev) => ({
            tabs: prev.tabs.map((tab) => {
              if (tab.id !== tabId) return tab;
              const finalToolActivities = this.getFinalToolActivities(tab.id, tab.toolActivities);
              const completedToolActivities = buildCompletedToolActivitiesFromActivities(finalToolActivities);
              const assistantMessage = this.upsertOrAppendAssistantMessage(tab.messages, {
                isStreaming: false,
                completedToolActivities,
                responseDurationMs
              });
              finalizedAssistantMessageId = assistantMessage.messageId;
              return {
                ...tab,
                messages: assistantMessage.messages,
                isLoading: false,
                isTextStreaming: false,
                toolActivities: []
              };
            })
          }),
          () => {
            if (finalizedAssistantMessageId) {
              this.finalizedAssistantMessageIds.set(tabId, finalizedAssistantMessageId);
            }
            this.scrollToBottom();
            this.scheduleSave();
          }
        );
        bridge.offChatEvent(requestId);
      } else if (eventType === "error") {
        this.streamingMessageIds.delete(tabId);
        // Do not delete requestStartTimes — original chat turn may still complete.
        bridge.offChatEvent(requestId);

        // Reset canvas animation on error too
        this.canvasZoneActiveTabs.delete(tabId);
        window.dispatchEvent(new CustomEvent(MCP_ZONE_RESET, { detail: { zone: "canvas" } }));

        // Show error and revert interrupt status so the user can retry
        const errorMsg = data?.error || "Interrupt response failed";
        this.addAssistantMessageToTab(tabId, errorMsg, true);
        this.revertInterruptStatus(tabId, sourceMessageId);
      }
    });
  }

  /**
   * Resolve the tool-call id attached to a message's `interruptData`,
   * so decision payloads can route to the right `pendingInterrupts[id]`
   * entry on the backend. The backend tolerates the id being omitted
   * when exactly one pending exists (today's serial case), but echoing
   * it is required once parallel specialist execution lands — and
   * harmless today.
   */
  private lookupInterruptToolCallId(messageId: string): string | undefined {
    const tab = this.getActiveTab();
    const msg = tab.messages.find((m) => m.id === messageId);
    return msg?.interruptData?.toolCallId;
  }

  private sendInterruptDecision(messageId: string, decision: "approve" | "approve_all", newStatus: "approved"): void {
    const tab = this.getActiveTab();
    const bridge = getMcpBridgeInstance();

    // Validate preconditions before changing state
    if (!tab.sessionId || !bridge || !bridge.isConnected) {
      // Don't change state — just show an error message
      const reason = !tab.sessionId ? "No active session" : "Not connected to server";
      this.addAssistantMessageToTab(tab.id, `Cannot process approval: ${reason}. Please try again.`, true);
      return;
    }

    const toolCallId = this.lookupInterruptToolCallId(messageId);

    // Resume the same turn — do NOT reset requestStartTimes (would make the
    // thinking timer and responseDurationMs count only post-approve time) and
    // do NOT wipe toolActivities / archive (would drop pre-interrupt tools from
    // the final transcript). Original start was set on handleSend.
    this.updateActiveTab(
      (t) => ({
        messages: t.messages.map((m) =>
          m.id === messageId && m.interruptData ? { ...m, interruptData: { ...m.interruptData, status: newStatus } } : m
        ),
        isLoading: true
      }),
      // User-initiated turn (approving an interrupt) — force re-stick so the
      // reply is followed even if they'd scrolled up to inspect a prior tool.
      this.forceScrollToBottom
    );

    const requestId = `${Date.now()}-interrupt`;
    this.setupInterruptResponseHandler(tab.id, requestId, bridge, messageId);
    bridge.sendInterruptResponse(requestId, tab.sessionId, decision, undefined, toolCallId);
  }

  handleInterruptApprove = (messageId: string) => {
    this.sendInterruptDecision(messageId, "approve", "approved");
  };

  handleInterruptApproveAll = (messageId: string) => {
    this.sendInterruptDecision(messageId, "approve_all", "approved");
  };

  handleInterruptReject = (messageId: string) => {
    const tab = this.getActiveTab();
    const bridge = getMcpBridgeInstance();

    if (!tab.sessionId || !bridge || !bridge.isConnected) {
      const reason = !tab.sessionId ? "No active session" : "Not connected to server";
      this.addAssistantMessageToTab(tab.id, `Cannot process rejection: ${reason}. Please try again.`, true);
      return;
    }

    // Reject continues the same server turn (skip the destructive op). Keep
    // original requestStartTimes and tool activities; only flip loading so the
    // thinking indicator resumes until the original chat_request emits done.
    this.updateActiveTab(
      (t) => ({
        messages: t.messages.map((m) =>
          m.id === messageId && m.interruptData
            ? { ...m, interruptData: { ...m.interruptData, status: "rejected" as const } }
            : m
        ),
        isLoading: true
      }),
      this.forceScrollToBottom
    );

    const requestId = `${Date.now()}-interrupt`;
    const toolCallId = this.lookupInterruptToolCallId(messageId);
    bridge.onChatEvent(requestId, (evt: ChatEventData) => {
      if (!this.isMounted_) return;
      const { event: eventType, data } = evt;
      if (eventType === "done" || eventType === "model") {
        bridge.offChatEvent(requestId);
      } else if (eventType === "error") {
        bridge.offChatEvent(requestId);
        const errorMsg = data?.error || "Reject failed";
        this.addAssistantMessageToTab(tab.id, errorMsg, true);
        this.updateActiveTab((t) => ({ isLoading: false }));
        // Revert rejection so user can retry
        this.revertInterruptStatus(tab.id, messageId);
      }
    });
    bridge.sendInterruptResponse(requestId, tab.sessionId, "reject", undefined, toolCallId);
  };

  handleQuestionInterruptSubmit = (messageId: string, questionnaire: ParsedQuestionnaire) => {
    const tab = this.getActiveTab();
    const progress = tab.questionnaires[messageId] || createEmptyQuestionnaireProgress();
    // A card with delegated questions has gaps by design, so the strict formatter
    // would refuse it. Both formatters share `cleanQuestionPrompt`, so the answer
    // lines are byte-identical either way.
    const hasDelegated: boolean = (progress.delegatedIndices ?? []).length > 0;
    const formatted: string | null = hasDelegated
      ? formatAnsweredQuestionLines(questionnaire, progress)
      : formatQuestionnaireAnswers(questionnaire, progress);
    if (formatted === null) return;

    this.dispatchInterruptAnswer(messageId, formatted);
  };

  /**
   * "You decide this one" — record the hand-back and move on.
   *
   * Nothing is sent yet: the user keeps answering the remaining questions and
   * submits normally. The skipped question simply has no line in the submitted
   * text, and the server tells the agent the blanks were deliberate.
   */
  handleQuestionDelegateOne = (tabId: string, messageId: string, questionIndex: number, questionCount: number) => {
    this.updateTab(tabId, (t: ChatTab) => {
      const prev: QuestionnaireProgress = t.questionnaires[messageId] || createEmptyQuestionnaireProgress();
      const delegated: number[] = prev.delegatedIndices ?? [];
      return {
        questionnaires: {
          ...t.questionnaires,
          [messageId]: {
            ...prev,
            // Handing a question back replaces any answer it had — the two are
            // mutually exclusive, and a stale value would still be submitted.
            selectedValues: { ...prev.selectedValues, [questionIndex]: "" },
            customValues: { ...prev.customValues, [questionIndex]: "" },
            multiSelectedValues: { ...(prev.multiSelectedValues || {}), [questionIndex]: [] },
            delegatedIndices: delegated.includes(questionIndex) ? delegated : [...delegated, questionIndex],
            currentQuestionIndex: Math.min(questionIndex + 1, Math.max(0, questionCount - 1))
          }
        }
      };
    });
  };

  /**
   * "You decide the rest" — submit what is answered and stop the questions.
   *
   * Every still-unanswered question is marked delegated so the frozen card reads
   * honestly, then the answers go out with `decide_for_me`, which additionally
   * tells the server no further question this turn should reach the user.
   */
  handleQuestionDelegateRest = (messageId: string, questionnaire: ParsedQuestionnaire) => {
    const tab: ChatTab = this.getActiveTab();
    const progress: QuestionnaireProgress = tab.questionnaires[messageId] || createEmptyQuestionnaireProgress();
    const formatted: string = formatAnsweredQuestionLines(questionnaire, progress);
    const delegated: number[] = [...(progress.delegatedIndices ?? [])];
    for (let i = 0; i < questionnaire.questions.length; i++) {
      if (!isQuestionAnswered(questionnaire.questions[i], i, progress) && !delegated.includes(i)) {
        delegated.push(i);
      }
    }
    this.updateTab(tab.id, (t: ChatTab) => ({
      questionnaires: {
        ...t.questionnaires,
        [messageId]: {
          ...(t.questionnaires[messageId] || createEmptyQuestionnaireProgress()),
          delegatedIndices: delegated
        }
      }
    }));
    this.dispatchInterruptAnswer(messageId, formatted, "decide_for_me");
  };

  /**
   * Send an already-formatted answer back to a suspended
   * `ui_common_ask_user_question` interrupt: mark the interrupt approved, flip
   * the tab to loading, wire the response handler, and dispatch over the bridge.
   * Shared by "Submit answers", the instant-submit path (an option flagged
   * `submitImmediately`, e.g. the yes/no consent card), and "You decide the rest".
   */
  dispatchInterruptAnswer = (messageId: string, formatted: string, decision: InterruptDecision = "answer") => {
    const tab = this.getActiveTab();
    const bridge = getMcpBridgeInstance();

    if (!tab.sessionId || !bridge || !bridge.isConnected) {
      const reason = !tab.sessionId ? "No active session" : "Not connected to server";
      this.addAssistantMessageToTab(tab.id, `Cannot submit answers: ${reason}. Please try again.`, true);
      return;
    }

    // Same turn as the original chat_request — keep start time + tool transcript.
    // Mark interrupt approved + progress submitted so the bubble freezes as
    // "answered" and stays visible when the next BA round opens a new bubble.
    this.updateActiveTab(
      (t) => ({
        messages: t.messages.map((m) =>
          m.id === messageId && m.interruptData
            ? { ...m, interruptData: { ...m.interruptData, status: "approved" as const } }
            : m
        ),
        questionnaires: {
          ...t.questionnaires,
          [messageId]: {
            ...(t.questionnaires[messageId] || createEmptyQuestionnaireProgress()),
            submitted: true
          }
        },
        isLoading: true
      }),
      // User-initiated turn (submitting questionnaire answers) — force re-stick
      // so the reply is followed even if they'd scrolled up.
      this.forceScrollToBottom
    );

    const requestId = `${Date.now()}-question`;
    const toolCallId = this.lookupInterruptToolCallId(messageId);
    this.setupInterruptResponseHandler(tab.id, requestId, bridge, messageId);
    bridge.sendInterruptResponse(requestId, tab.sessionId, decision, formatted, toolCallId);
  };

  handleImprovePrompt = () => {
    const originalPrompt = this.currentInputValue.trim();
    if (!originalPrompt) return;

    const requestId = `improve_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const bridge = getMcpBridgeInstance();
    if (!bridge) return;

    this.currentInputValue = "";
    this.updateActiveTab(
      () => ({
        isImprovingPrompt: true,
        improveRequestId: requestId,
        improveOriginalPrompt: originalPrompt,
        inputValue: ""
      }),
      () => {
        this.inputAreaRef.current?.clearContent();

        bridge.onChatEvent(requestId, (event) => {
          if (!this.isMounted_) return;
          if (event.event === "prompt_improve_chunk") {
            const chunk = event.data?.chunk || "";
            this.updateActiveTab(
              (tab) => ({ inputValue: tab.inputValue + chunk }),
              () => {
                this.inputAreaRef.current?.setContent(this.getActiveTab().inputValue);
              }
            );
          } else if (event.event === "prompt_improve_done") {
            const finalText = event.data?.text || this.getActiveTab().inputValue;
            bridge.offChatEvent(requestId);
            this.currentInputValue = finalText;
            this.updateActiveTab(
              () => ({
                isImprovingPrompt: false,
                improveRequestId: null,
                improveOriginalPrompt: null,
                inputValue: finalText
              }),
              () => {
                this.inputAreaRef.current?.setContent(finalText);
                this.inputAreaRef.current?.focus();
              }
            );
          } else if (event.event === "prompt_improve_error") {
            bridge.offChatEvent(requestId);
            this.currentInputValue = originalPrompt;
            this.updateActiveTab(
              () => ({
                isImprovingPrompt: false,
                improveRequestId: null,
                improveOriginalPrompt: null,
                inputValue: originalPrompt
              }),
              () => {
                this.inputAreaRef.current?.setContent(originalPrompt);
                this.inputAreaRef.current?.focus();
              }
            );
          }
        });

        bridge.sendPromptImprove(
          requestId,
          originalPrompt,
          this.getActiveTab().chatMode,
          buildAuxCallContext(this.getActiveTab().sessionId)
        );
      }
    );
  };

  handleCancelImprovePrompt = () => {
    const activeTab = this.getActiveTab();
    const requestId = activeTab.improveRequestId;
    if (!requestId) return;

    const bridge = getMcpBridgeInstance();
    if (bridge) {
      bridge.sendPromptImproveCancel(requestId);
      bridge.offChatEvent(requestId);
    }

    const restoreText = activeTab.improveOriginalPrompt ?? "";
    this.currentInputValue = restoreText;
    this.updateActiveTab(
      () => ({
        isImprovingPrompt: false,
        improveRequestId: null,
        improveOriginalPrompt: null,
        inputValue: restoreText
      }),
      () => {
        this.inputAreaRef.current?.setContent(restoreText);
        this.inputAreaRef.current?.focus();
      }
    );
  };

  handleSendToLlm = (message: string) => {
    this.updateActiveTab(
      () => ({ inputValue: "" }),
      () => {
        this.inputAreaRef.current?.resetHeight();
        this.handleSend(message);
      }
    );
  };

  handleClosePanel = () => {
    this.deactivateInspector(true, false);
    this.props.setIsMcpChatPanelVisible(false);
  };

  handleSlashCommand = (command: SlashCommandDefinition, args: string) => {
    const activeTab = this.getActiveTab();

    switch (command.name) {
      // ── Chat commands ──
      case "clear":
        if (!activeTab.isLoading && activeTab.messages.length > 0) this.handleClearChat();
        else this.clearInput();
        break;

      case "new":
        this.clearInput();
        this.handleAddTab();
        break;

      case "close":
        this.clearInput();
        if (this.state.tabs.length > 1) this.handleCloseTab(this.state.activeTabId);
        else this.addSystemMessage("Cannot close the last tab.");
        break;

      case "rename": {
        const name = args.trim();
        if (!name) {
          this.clearInput();
          this.addSystemMessage("Usage: `/rename <name>`");
          break;
        }
        this.handleRenameTab(this.state.activeTabId, name);
        this.clearInput();
        this.addSystemMessage(`Tab renamed to **${name}**`);
        break;
      }

      case "export":
        this.clearInput();
        this.handleExportConversation();
        break;

      case "designer": {
        this.clearInput();
        if (activeTab.chatMode === "designer") {
          this.addSystemMessage("Already in **designer** mode.");
        } else {
          this.handleChatModeChange("designer");
          this.addSystemMessage(
            "Switched to **designer** mode — AI will design screens without modifying backend or logic."
          );
        }
        break;
      }

      case "builder": {
        this.clearInput();
        if (activeTab.chatMode === "builder") {
          this.addSystemMessage("Already in **builder** mode.");
        } else {
          this.handleChatModeChange("builder");
          this.addSystemMessage(
            "Switched to **builder** mode — AI has full capabilities including UI design, backend, data, and logic."
          );
        }
        break;
      }

      case "plan": {
        this.clearInput();
        const newPlanMode = !activeTab.planMode;
        this.updateActiveTab(() => ({ planMode: newPlanMode }));
        this.addSystemMessage(
          newPlanMode
            ? "Plan mode **enabled** — AI will explore and create a plan before making changes."
            : "Plan mode **disabled** — AI will execute directly."
        );
        break;
      }

      case "ask": {
        this.clearInput();
        if (activeTab.chatMode === "ask") {
          this.addSystemMessage("Already in **ask** mode.");
        } else {
          this.handleChatModeChange("ask");
          this.addSystemMessage("Switched to **ask** mode — AI will answer questions without making changes.");
        }
        break;
      }

      // ── Navigation commands ──
      case "screen": {
        const query = args.trim();
        if (!query) {
          this.clearInput();
          this.handleListScreens();
          break;
        }
        this.clearInput();
        this.handleNavigateToScreen(query);
        break;
      }

      // ── Design commands ──
      case "save":
        this.clearInput();
        this.handleSaveScreen();
        break;

      case "preview":
        this.clearInput();
        this.handleRunPreview();
        break;

      case "undo":
        this.clearInput();
        this.handleUndoLastAiChange();
        break;

      case "validate":
        this.clearInput();
        this.handleValidateScreen();
        break;

      // ── AI commands ──
      case "review":
        this.handleSendToLlm(
          "Review the current screen design. Check for styling inconsistencies, accessibility issues, and suggest improvements."
        );
        break;

      // Not `handleSendToLlm` like its neighbours: the walk is reached by a
      // structured marker, not by a sentence a model has to interpret. Same
      // entry point as the toolbar button, so both cost the same and confirm
      // the same way.
      case "analyze":
        this.clearInput();
        this.sendProjectAnalysisRequest();
        break;

      case "docs": {
        const query = args.trim();
        if (!query) {
          this.clearInput();
          this.addSystemMessage("Usage: `/docs <search query>`");
          break;
        }
        this.handleSendToLlm(`Search Kuika documentation for: ${query}`);
        break;
      }

      // ── Info commands ──
      case "help":
        this.clearInput();
        this.handleShowHelp();
        break;

      case "shortcuts":
        this.clearInput();
        this.handleShowShortcuts();
        break;

      case "context":
        this.clearInput();
        this.handleShowContext();
        break;

      case "search":
        this.clearInput();
        if (!this.state.search.isOpen) this.handleSearchToggle();
        break;

      default:
        // Unknown command — fall through to LLM
        this.handleSendToLlm(`/${command.name} ${args}`.trim());
        break;
    }
  };

  // ── Slash command handler implementations ──

  handleExportConversation = () => {
    const tab = this.getActiveTab();
    if (tab.messages.length === 0) {
      this.addSystemMessage("No messages to export.");
      return;
    }

    const lines = [`# ${tab.name}`, `Exported: ${new Date().toLocaleString("en-US")}`, ""];
    for (const msg of tab.messages) {
      let role = "System";
      if (msg.role === "user") {
        role = "You";
      } else if (msg.role === "assistant") {
        role = "AI";
      }
      const time = new Date(msg.timestamp).toLocaleTimeString("en-US");
      lines.push(`**${role}** (${time}):`);
      lines.push(msg.role === "assistant" ? collapseExactDuplicateAssistantResponse(msg.content) : msg.content);
      lines.push("");
    }

    downloadMarkdownFile(`${tab.name.replace(/[^a-zA-Z0-9-_ ]/g, "")}.md`, lines.join("\n"));
    this.addSystemMessage("Conversation exported as markdown.");
  };

  /**
   * `discovery_report` — a finished project walk hands the user the report as a
   * file. The markdown is the same text the answer printed, so this saves a
   * copy rather than rendering anything new; the report itself stays in the
   * transcript either way.
   *
   * Silently ignores a malformed payload: a missing file must never cost the
   * user the analysis they already paid for and can still read on screen.
   */
  handleDiscoveryReportEvent = (data: { fileName?: unknown; markdown?: unknown }) => {
    const markdown = typeof data.markdown === "string" ? data.markdown : "";
    if (markdown.trim().length === 0) return;
    const requested = typeof data.fileName === "string" ? data.fileName : "";
    const fileName = sanitizeDownloadFileName(requested, "discovery-report.md");
    downloadMarkdownFile(fileName, markdown);
    this.addSystemMessage(`Keşif raporu dosya olarak indirildi: ${fileName}`);
  };

  handleNavigateToScreen = async (query: string) => {
    try {
      const sd = getDesignerRuntime();
      const screenList = sd?.screenProvider?.state?.screenList;
      if (!screenList || screenList.length === 0) {
        this.addSystemMessage("No screens available.");
        return;
      }

      const lowerQuery = query.toLowerCase();
      // Shared with the `kuika://screen/…` links in assistant messages, so the slash
      // command and a clicked page name resolve identically.
      const { resolveScreenRef } = await import("../../../../../shared/utility/kuika-chat-links");
      const match = resolveScreenRef(sd, query);

      if (!match) {
        const suggestions = screenList
          .filter((screen) => screen.name?.toLowerCase().includes(lowerQuery))
          .slice(0, 5)
          .map((screen) => `\`${screen.name}\``)
          .join("  \n");
        this.addSystemMessage(
          suggestions ? `Screen "${query}" not found. Did you mean:  \n${suggestions}` : `Screen "${query}" not found.`
        );
        return;
      }

      const { openDesignerScreen } = await import("../../../../../shared/utility/designer-screen-actions");
      const result = await openDesignerScreen(sd, match.id?.toString());
      if (result && "error" in result) {
        this.addSystemMessage(`Failed to navigate: ${result.error}`);
      } else {
        this.addSystemMessage(`Navigated to screen **${match.name}**`);
      }
    } catch {
      this.addSystemMessage("Failed to navigate to screen.");
    }
  };

  handleListScreens = () => {
    try {
      const sd = getDesignerRuntime();
      const screenList = sd?.screenProvider?.state?.screenList;
      if (!screenList || screenList.length === 0) {
        this.addSystemMessage("No screens available.");
        return;
      }
      const list = screenList.map((screen) => `\`${screen.name}\``).join("  \n");
      this.addSystemMessage(`**Available Screens**  \n${list}  \n\nUse \`/screen <name>\` to navigate.`);
    } catch {
      this.addSystemMessage("Failed to list screens.");
    }
  };

  handleSaveScreen = async () => {
    try {
      const { saveDesignerScreen } = await import("../../../../../shared/utility/designer-project-actions");
      const sd = getDesignerRuntime();
      if (!sd) {
        this.addSystemMessage("Designer not available.");
        return;
      }
      const result = await saveDesignerScreen(sd);
      this.addSystemMessage(result.message);
    } catch {
      this.addSystemMessage("Failed to save screen.");
    }
  };

  handleRunPreview = async () => {
    try {
      const { runDesignerPreview } = await import("../../../../../shared/utility/designer-project-actions");
      const sd = getDesignerRuntime();
      if (!sd) {
        this.addSystemMessage("Designer not available.");
        return;
      }
      const result = await runDesignerPreview(sd);
      if ("error" in result) {
        this.addSystemMessage(result.error);
      } else {
        this.addSystemMessage("Preview opened in a new tab.");
      }
    } catch {
      this.addSystemMessage("Failed to run preview.");
    }
  };

  handleValidateScreen = async () => {
    try {
      const result: unknown = await McpToolExecutor.execute("ui_validation", {
        operation: "validate_screen",
        scope: "full"
      });
      if (
        result &&
        typeof result === "object" &&
        "error" in result &&
        typeof (result as { error: unknown }).error === "string"
      ) {
        this.addSystemMessage(`Validation failed: ${(result as { error: string }).error}`);
        return;
      }
      const report = result as ValidationToolResult;
      const lines: string[] = [];
      lines.push(`**Screen Validation** — Score: ${report.score}/100 across ${report.componentCount} components`);
      lines.push(report.summary);
      if (report.issues && report.issues.length > 0) {
        lines.push("");
        for (const issue of report.issues) {
          const tag = issue.severity === "error" ? "ERROR" : issue.severity === "warning" ? "WARN" : "INFO";
          lines.push(`- [${tag}] **${issue.category}** — ${issue.issue}`);
          if (issue.components && issue.components.length > 0) {
            lines.push(`  Components: ${issue.components.join(", ")}`);
          }
          if (issue.suggestion) {
            lines.push(`  Suggestion: ${issue.suggestion}`);
          }
        }
      }
      this.addSystemMessage(lines.join("\n"));
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      this.addSystemMessage(`Failed to validate screen: ${msg}`);
    }
  };

  handleUndoLastAiChange = () => {
    const msgId = this.getMostRecentUndoableMessageId();
    if (!msgId) {
      this.addSystemMessage("No revertable AI changes found.");
      return;
    }
    this.handleRewindRequest(msgId);
  };

  handleShowContext = () => {
    const ctx = gatherScreenContext();
    if (!ctx) {
      this.addSystemMessage("No screen context available.");
      return;
    }

    const lines: string[] = ["**Current Context**"];
    if (ctx.screenName) lines.push(`**Screen:** ${ctx.screenName}`);
    if (ctx.currentView) lines.push(`**View:** ${ctx.currentView}`);
    if (ctx.selectedComponent) {
      const sc = ctx.selectedComponent;
      const id = sc.kuikaId;
      if (id == null || String(id) === "-1") {
        lines.push("**Selected:** Screen (root)");
      } else {
        lines.push(`**Selected:** ${sc.friendlyName || sc.name} (kuikaId: ${id})`);
      }
    } else {
      lines.push("**Selected:** No item selected");
    }
    const datasources = Array.isArray(ctx.datasources) ? (ctx.datasources as ContextDatasource[]) : [];
    if (datasources.length > 0) {
      lines.push(`**Datasources:** ${datasources.map((datasource) => datasource.name).join(", ")}`);
    }
    if (ctx.treeSummary) {
      const count = this.countTreeNodes(ctx.treeSummary);
      lines.push(`**Components:** ${count} total`);
    }

    this.addSystemMessage(lines.join("  \n"));
  };

  countTreeNodes = (nodes: TreeNodeLike[]): number => {
    let count = 0;
    for (const node of nodes) {
      count++;
      if (node.children) count += this.countTreeNodes(node.children);
    }
    return count;
  };

  handleShowHelp = () => {
    const activeTab = this.getActiveTab();
    const grouped: Record<string, SlashCommandDefinition[]> = {};
    for (const cmd of SLASH_COMMANDS) {
      if (!grouped[cmd.category]) grouped[cmd.category] = [];
      grouped[cmd.category].push(cmd);
    }

    const sections: string[] = [];
    for (const category of Object.keys(grouped)) {
      const meta = COMMAND_CATEGORY_META[category as keyof typeof COMMAND_CATEGORY_META];
      const cmds = grouped[category]
        .map((cmd) => {
          const argHint = cmd.mode === "argument" ? ` \`<${cmd.argPlaceholder?.replace("...", "") || "arg"}>\`` : "";
          return `\`/${cmd.name}\`${argHint} — ${cmd.description}`;
        })
        .join("  \n");
      sections.push(`**${meta.label}**  \n${cmds}`);
    }

    this.addSystemMessage(
      `**Available Commands**\n\n${sections.join("\n\n")}\n\nType \`/\` to open the command palette. Use \`@\` to mention components, datasources, or screens.`
    );
  };

  handleShowShortcuts = () => {
    const shortcuts = [
      `**Enter** — Send message`,
      `**Shift+Enter** — New line`,
      `**/** — Open command palette`,
      `**@** — Mention a component, datasource, screen, or action`,
      `**Arrow keys** — Navigate palette or mentions`,
      `**ArrowUp/Down** — Browse sent message history (when input is empty)`,
      `**Tab/Enter** — Select from palette or mentions`,
      `**Cmd/Ctrl+Shift+F** — Search messages`,
      `**Esc** — Close panel`
    ];
    this.addSystemMessage(`**Keyboard Shortcuts**\n\n${shortcuts.join("  \n")}`);
  };

  // ── Search ──

  private static EMPTY_SEARCH: SearchState = {
    isOpen: false,
    query: "",
    matches: [],
    currentMatchIndex: -1,
    crossTab: false
  };

  handleSearchToggle = () => {
    this.setState((prev) => ({
      search: prev.search.isOpen ? McpChatPanel.EMPTY_SEARCH : { ...McpChatPanel.EMPTY_SEARCH, isOpen: true }
    }));
  };

  handleModelSelectorToggle = () => {
    this.setState((prev) => ({ isModelSelectorOpen: !prev.isModelSelectorOpen, isBalancePanelOpen: false }));
  };

  handleBalancePanelToggle = () => {
    this.setState((prev) => ({ isBalancePanelOpen: !prev.isBalancePanelOpen, isModelSelectorOpen: false }));
  };

  handleModelOverridesChange = (overrides: ModelOverrides) => {
    this.setState({ modelOverrides: overrides });
  };

  isKuikaDeveloper = (): boolean => {
    const username = getCurrentUser()?.username || "";
    return typeof username === "string" && username.includes("@kuika.com");
  };

  handleSearchQueryChange = (query: string) => {
    const matches = searchMessages(query, this.state.tabs, this.state.activeTabId, this.state.search.crossTab);
    this.setState(
      {
        search: {
          ...this.state.search,
          query,
          matches,
          currentMatchIndex: matches.length > 0 ? 0 : -1
        }
      },
      () => {
        if (matches.length > 0) this.scrollToSearchMatch(matches[0].messageId, matches[0].tabId);
      }
    );
  };

  handleSearchNextMatch = () => {
    const { matches, currentMatchIndex } = this.state.search;
    if (matches.length === 0) return;
    const nextIndex = (currentMatchIndex + 1) % matches.length;
    const match = matches[nextIndex];
    this.setState({ search: { ...this.state.search, currentMatchIndex: nextIndex } }, () =>
      this.scrollToSearchMatch(match.messageId, match.tabId)
    );
  };

  handleSearchPrevMatch = () => {
    const { matches, currentMatchIndex } = this.state.search;
    if (matches.length === 0) return;
    const prevIndex = (currentMatchIndex - 1 + matches.length) % matches.length;
    const match = matches[prevIndex];
    this.setState({ search: { ...this.state.search, currentMatchIndex: prevIndex } }, () =>
      this.scrollToSearchMatch(match.messageId, match.tabId)
    );
  };

  handleSearchToggleCrossTab = () => {
    const crossTab = !this.state.search.crossTab;
    const matches = searchMessages(this.state.search.query, this.state.tabs, this.state.activeTabId, crossTab);
    this.setState({
      search: {
        ...this.state.search,
        crossTab,
        matches,
        currentMatchIndex: matches.length > 0 ? 0 : -1
      }
    });
  };

  handleSearchSelectMatch = (matchIndex: number) => {
    const match = this.state.search.matches[matchIndex];
    if (!match) return;
    this.setState({ search: { ...this.state.search, currentMatchIndex: matchIndex } }, () =>
      this.scrollToSearchMatch(match.messageId, match.tabId)
    );
  };

  private scrollToSearchMatch(messageId: string, tabId: string) {
    const switchTab = tabId !== this.state.activeTabId;
    const doScroll = () => {
      // Ensure message is visible (expand virtual scroll if needed)
      const tab = this.state.tabs.find((t) => t.id === (switchTab ? tabId : this.state.activeTabId));
      if (!tab) return;
      const msgIndex = tab.messages.findIndex((m) => m.id === messageId);
      if (msgIndex === -1) return;

      const posFromEnd = tab.messages.length - msgIndex;
      if (posFromEnd > tab.visibleMessageCount) {
        this.updateTab(
          tab.id,
          () => ({ visibleMessageCount: posFromEnd + 5 }),
          () => {
            requestAnimationFrame(() => this.scrollMessageIntoView(messageId));
          }
        );
      } else {
        requestAnimationFrame(() => this.scrollMessageIntoView(messageId));
      }
    };

    if (switchTab) {
      this.handleSwitchTab(tabId);
      // Allow tab switch render, then scroll
      setTimeout(doScroll, 100);
    } else {
      doScroll();
    }
  }

  private scrollMessageIntoView(messageId: string) {
    const container = this.messagesContainerRef.current;
    if (!container) return;
    const el = container.querySelector(`[data-message-id="${messageId}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  handleGlobalKeyDown = (e: KeyboardEvent) => {
    if (!this.props.isMcpChatPanelVisible) return;

    // Cmd/Ctrl+Shift+F — toggle search
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === "f" || e.key === "F")) {
      e.preventDefault();
      e.stopPropagation();
      this.handleSearchToggle();
      return;
    }

    if (e.key === "Escape") {
      // Close search first before closing panel
      if (this.state.search.isOpen) {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ search: McpChatPanel.EMPTY_SEARCH });
        return;
      }
      if (this.tabBarRef.current?.isEditing()) return;
      if (this.inputAreaRef.current?.isPaletteOpen()) return;
      if (this.inputAreaRef.current?.isPillDropdownOpen()) return;
      e.preventDefault();
      e.stopPropagation();
      this.props.setIsMcpChatPanelVisible(false);
      return;
    }
  };

  // ── Focus trap ──

  handleFocusTrap = (e: KeyboardEvent) => {
    if (e.key !== "Tab" || !this.props.isMcpChatPanelVisible) return;
    const panel = this.panelRef.current;
    if (!panel) return;

    const focusable = panel.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first || !panel.contains(document.activeElement)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last || !panel.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  // ── Anchor persistence ──

  loadPersistedAnchor(): ChatAnchor {
    try {
      const raw = localStorage.getItem(PANEL_ANCHOR_STORAGE_KEY);
      // Only restore floating anchors; docked anchor (right-panel)
      // requires parent coordination that isn't available on initial load.
      if (raw === "bottom-left" || raw === "bottom-right" || raw === "top" || raw === "bottom") {
        return raw;
      }
    } catch {
      // ignore
    }
    return "bottom-right";
  }

  savePersistedAnchor(anchor: ChatAnchor): void {
    try {
      localStorage.setItem(PANEL_ANCHOR_STORAGE_KEY, anchor);
    } catch {
      // ignore
    }
  }

  // ── Anchor zone computation ──

  getAnchorZones(): Record<ChatAnchor, { x: number; y: number; width: number; height: number }> {
    if (this._cachedAnchorZones && !this._anchorZonesDirty) {
      return this._cachedAnchorZones;
    }
    const leftMenuWidth = 48;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const panelW = this.state.panelSize?.width ?? 400;
    const panelH = this.state.panelSize?.height ?? 560;
    const footerH = 56;
    const toolbarH = 48;
    const margin = 24;

    // Read actual DOM widths for accuracy (handles collapsed state correctly)
    const leftPanelEl = document.querySelector(".kuika-left-panel") as HTMLElement | null;
    const rightPanelEl = document.querySelector(".kuika_rightPanel") as HTMLElement | null;
    const leftPanelW = leftPanelEl ? leftPanelEl.getBoundingClientRect().width : 0;
    const rightPanelW = rightPanelEl ? rightPanelEl.getBoundingClientRect().width : readRightPanelWidth();

    // The left edge where content starts (after menu + panel)
    const leftEdge = leftMenuWidth + leftPanelW;

    // Horizontal (landscape) panel size for top/bottom anchors
    const hSize = this.state.horizontalPanelSize ?? this.computeDefaultHorizontalSize();
    const hW = hSize.width;
    const hH = hSize.height;
    // Center horizontally in the canvas area (between left edge and right panel)
    const canvasCenterX = leftEdge + (vw - leftEdge - rightPanelW) / 2;
    const hX = canvasCenterX - hW / 2;

    const zones: Partial<Record<ChatAnchor, { x: number; y: number; width: number; height: number }>> = {
      "bottom-right": {
        x: vw - panelW - margin,
        y: vh - panelH - footerH,
        width: panelW,
        height: panelH
      },
      "bottom-left": {
        x: leftMenuWidth + margin,
        y: vh - panelH - footerH,
        width: panelW,
        height: panelH
      },
      top: {
        x: hX,
        y: 4,
        width: hW,
        height: hH
      },
      bottom: {
        x: hX,
        y: vh - hH - 4,
        width: hW,
        height: hH
      }
    };

    // Only include right-panel anchor when docking is allowed (design state with visible right panel)
    if (this.props.canDockToRightPanel !== false) {
      zones["right-panel"] = {
        x: vw - rightPanelW,
        y: toolbarH,
        width: rightPanelW,
        height: vh - toolbarH - footerH
      };
    }

    this._cachedAnchorZones = zones as Record<ChatAnchor, { x: number; y: number; width: number; height: number }>;
    this._anchorZonesDirty = false;
    return this._cachedAnchorZones;
  }

  computeNearestAnchor(referenceX: number, referenceY: number): ChatAnchor | null {
    const zones = this.getAnchorZones();
    let nearest: ChatAnchor | null = null;
    let minDist = Infinity;

    for (const [key, zone] of Object.entries(zones) as [
      ChatAnchor,
      { x: number; y: number; width: number; height: number }
    ][]) {
      const zoneCx = zone.x + zone.width / 2;
      const zoneCy = zone.y + zone.height / 2;
      const dist = Math.sqrt((referenceX - zoneCx) ** 2 + (referenceY - zoneCy) ** 2);
      if (dist < minDist) {
        minDist = dist;
        nearest = key;
      }
    }

    return minDist <= ANCHOR_SNAP_DISTANCE * 3 ? nearest : null;
  }

  // ── Drag & drop ──

  handleDragStart = (e: React.MouseEvent) => {
    // Don't drag if panel is docked
    if (this.state.anchor === "right-panel") return;

    const panel = this.panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();

    this.setState({
      isDragging: true,
      dragOffset: { x: e.clientX - rect.left, y: e.clientY - rect.top },
      dragPosition: { x: rect.left, y: rect.top }
    });

    this.setIframePointerEvents(false);
    document.addEventListener("mousemove", this.handleDragMove);
    document.addEventListener("mouseup", this.handleDragEnd);
    document.body.style.userSelect = "none";
  };

  handleDragMove = (e: MouseEvent) => {
    if (!this.state.isDragging) return;
    const x = e.clientX - this.state.dragOffset.x;
    const y = e.clientY - this.state.dragOffset.y;
    const nearest = this.computeNearestAnchor(e.clientX, e.clientY);

    this.setState({ dragPosition: { x, y }, nearestAnchor: nearest });
  };

  /** Stores the target position (and optional size for orientation changes) during a snap animation. */
  private snapTarget: SnapTarget | null = null;

  handleDragEnd = (e: MouseEvent) => {
    document.removeEventListener("mousemove", this.handleDragMove);
    document.removeEventListener("mouseup", this.handleDragEnd);
    document.body.style.userSelect = "";
    this.setIframePointerEvents(true);

    if (!this.state.isDragging) return;

    const { anchor: currentAnchor } = this.state;
    const targetAnchor = this.computeNearestAnchor(e.clientX, e.clientY) || this.state.nearestAnchor || currentAnchor;

    if (targetAnchor === "right-panel") {
      // Dock to panel — no floating snap needed
      this.setState({
        isDragging: false,
        dragPosition: null,
        nearestAnchor: null,
        isSnapping: false,
        anchor: targetAnchor
      });
      this.savePersistedAnchor(targetAnchor);
      this.props.onDockToPanel?.(targetAnchor);
    } else {
      // Compute snap target from the anchor zone
      const zones = this.getAnchorZones();
      const zone = zones[targetAnchor];
      const snap: SnapTarget = {
        left: zone.x,
        top: zone.y
      };

      // When orientation changes (vertical↔horizontal), animate size too
      const wasHorizontal = this.isHorizontalAnchor(currentAnchor);
      const willBeHorizontal = this.isHorizontalAnchor(targetAnchor);
      if (wasHorizontal !== willBeHorizontal) {
        snap.width = zone.width;
        snap.height = zone.height;
      }

      this.snapTarget = snap;

      // Keep dragPosition so the panel stays where the user dropped it,
      // then enable isSnapping so the CSS transition animates to snapTarget.
      this.setState({ isDragging: false, nearestAnchor: null });

      // Use rAF to ensure the browser has painted the current position
      // before we flip to the snap target, so the transition actually animates.
      requestAnimationFrame(() => {
        this.setState({ isSnapping: true });
        // After the transition completes, finalize
        setTimeout(() => {
          this.snapTarget = null;
          this.setState({
            anchor: targetAnchor,
            isSnapping: false,
            dragPosition: null
          });
          this.savePersistedAnchor(targetAnchor);
        }, 370);
      });
    }
  };

  /** Set anchor programmatically (used when undocking from panel). */
  private handleWindowResize = () => {
    this._anchorZonesDirty = true;

    // Clamp panel size to the new viewport so the panel never overflows after zoom changes
    const next: Pick<McpChatPanelState, "panelSize" | "horizontalPanelSize"> = {
      panelSize: this.state.panelSize,
      horizontalPanelSize: this.state.horizontalPanelSize
    };
    let changed = false;

    const ps = this.state.panelSize;
    if (ps) {
      const maxW = getPanelMaxWidth();
      const maxH = getPanelMaxHeight();
      const clampedW = Math.max(PANEL_MIN_WIDTH, Math.min(ps.width, maxW));
      const clampedH = Math.max(PANEL_MIN_HEIGHT, Math.min(ps.height, maxH));
      if (clampedW !== ps.width || clampedH !== ps.height) {
        const clamped = { width: clampedW, height: clampedH };
        next.panelSize = clamped;
        changed = true;
        this.savePersistedSize(clamped);
      }
    }

    const hps = this.state.horizontalPanelSize;
    if (hps) {
      const maxW = getPanelHorizontalMaxWidth();
      const maxH = getPanelHorizontalMaxHeight();
      const clampedW = Math.max(PANEL_HORIZONTAL_MIN_WIDTH, Math.min(hps.width, maxW));
      const clampedH = Math.max(PANEL_HORIZONTAL_MIN_HEIGHT, Math.min(hps.height, maxH));
      if (clampedW !== hps.width || clampedH !== hps.height) {
        const clamped = { width: clampedW, height: clampedH };
        next.horizontalPanelSize = clamped;
        changed = true;
        this.savePersistedHorizontalSize(clamped);
      }
    }

    if (changed) {
      this.setState(next);
    } else {
      // Always re-render so anchor zones and FAB position are recalculated
      this.forceUpdate();
    }
  };

  setAnchor = (anchor: ChatAnchor) => {
    this._anchorZonesDirty = true;
    this.setState({ anchor });
    this.savePersistedAnchor(anchor);
  };

  /** Undock from a panel and immediately enter drag mode at the given cursor position. */
  startDragFromDock = (clientX: number, clientY: number) => {
    // Default floating panel dimensions
    const w = this.state.panelSize?.width ?? 400;
    const h = this.state.panelSize?.height ?? 560;
    // Centre the panel on the cursor
    const x = clientX - w / 2;
    const y = clientY - 20; // offset so cursor is near the header

    this.setState(
      {
        anchor: "bottom-right",
        isDragging: true,
        dragPosition: { x, y },
        dragOffset: { x: clientX - x, y: clientY - y },
        nearestAnchor: null,
        isSnapping: false
      },
      () => {
        this.savePersistedAnchor("bottom-right");
        this.setIframePointerEvents(false);
        document.addEventListener("mousemove", this.handleDragMove);
        document.addEventListener("mouseup", this.handleDragEnd);
        document.body.style.userSelect = "none";
      }
    );
  };

  // ── Resize handle ──

  /** Disable pointer-events on the canvas iframe so mousemove/mouseup are not swallowed. */
  private setIframePointerEvents(enabled: boolean) {
    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement | null;
    if (iframe) {
      iframe.style.pointerEvents = enabled ? "" : "none";
    }
  }

  handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const panel = this.panelRef.current;
    if (!panel) return;

    const rect = panel.getBoundingClientRect();
    this.resizeStartX = e.clientX;
    this.resizeStartY = e.clientY;
    this.resizeStartW = rect.width;
    this.resizeStartH = rect.height;
    this.resizeStartTop = rect.top;

    this.setState({ isResizing: true });
    this.setIframePointerEvents(false);
    document.body.style.userSelect = "none";
    document.body.style.cursor = this.getResizeCursor();
    document.addEventListener("mousemove", this.handleResizeMouseMove);
    document.addEventListener("mouseup", this.handleResizeMouseUp);
  };

  handleResizeMouseMove = (e: MouseEvent) => {
    const panel = this.panelRef.current;
    if (!panel) return;

    const { anchor } = this.state;
    if (anchor === "top" || anchor === "bottom") {
      const dy = anchor === "top" ? e.clientY - this.resizeStartY : this.resizeStartY - e.clientY;
      const newHeight = Math.max(
        PANEL_HORIZONTAL_MIN_HEIGHT,
        Math.min(this.resizeStartH + dy, getPanelHorizontalMaxHeight())
      );

      // Keep the horizontal anchors locked to their existing width.
      panel.style.width = `${this.resizeStartW}px`;
      panel.style.height = `${newHeight}px`;

      // "bottom" anchor: panel is positioned with top, so adjust it to keep the bottom edge pinned.
      if (anchor === "bottom") {
        const heightDelta = newHeight - this.resizeStartH;
        panel.style.top = `${this.resizeStartTop - heightDelta}px`;
      }
    } else {
      // Bottom-right grows from the top-left corner, bottom-left from the top-right corner.
      const dx = anchor === "bottom-left" ? e.clientX - this.resizeStartX : this.resizeStartX - e.clientX;
      const dy = this.resizeStartY - e.clientY;

      const newWidth = Math.max(PANEL_MIN_WIDTH, Math.min(this.resizeStartW + dx, getPanelMaxWidth()));
      // Clamp max height to viewport so the panel can't extend above the screen
      const viewportMaxHeight = window.innerHeight - 64;
      const maxHeight = Math.min(getPanelMaxHeight(), viewportMaxHeight);
      const newHeight = Math.max(PANEL_MIN_HEIGHT, Math.min(this.resizeStartH + dy, maxHeight));

      panel.style.width = `${newWidth}px`;
      panel.style.height = `${newHeight}px`;
    }
  };

  handleResizeMouseUp = () => {
    document.removeEventListener("mousemove", this.handleResizeMouseMove);
    document.removeEventListener("mouseup", this.handleResizeMouseUp);
    this.setIframePointerEvents(true);
    this._anchorZonesDirty = true;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";

    const panel = this.panelRef.current;
    if (panel) {
      const height = panel.offsetHeight;
      if (this.isHorizontalAnchor()) {
        const size = { width: this.getEffectivePanelSize().width, height };
        this.setState({ horizontalPanelSize: size, isResizing: false });
        this.savePersistedHorizontalSize(size);
      } else {
        const size = { width: panel.offsetWidth, height };
        this.setState({ panelSize: size, isResizing: false });
        this.savePersistedSize(size);
      }
    } else {
      this.setState({ isResizing: false });
    }
  };

  // ── Panel-level drag & drop ──

  private hasDroppableFiles(dt: DataTransfer): boolean {
    for (let i = 0; i < dt.items.length; i++) {
      if (dt.items[i].kind === "file") return true;
    }
    return false;
  }

  handlePanelDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    this.panelDragEnterCounter++;
    if (e.dataTransfer && this.hasDroppableFiles(e.dataTransfer)) {
      this.setState({ isFileDragOver: true });
    }
  };

  handlePanelDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  handlePanelDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    this.panelDragEnterCounter--;
    if (this.panelDragEnterCounter <= 0) {
      this.panelDragEnterCounter = 0;
      this.setState({ isFileDragOver: false });
    }
  };

  handlePanelDrop = (e: React.DragEvent) => {
    e.preventDefault();
    this.panelDragEnterCounter = 0;
    this.setState({ isFileDragOver: false });

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const imageFiles: File[] = [];
    const docFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      if (files[i].type.startsWith("image/")) {
        imageFiles.push(files[i]);
      } else {
        const ext = files[i].name.split(".").pop()?.toLowerCase() || "";
        if (ALLOWED_FILE_EXTENSIONS.includes(ext)) {
          docFiles.push(files[i]);
        }
      }
    }

    // Process images
    if (imageFiles.length > 0) {
      const remaining = MAX_ATTACHED_IMAGES - this.getActiveTab().attachedImages.length;
      const toProcess = imageFiles.slice(0, remaining);
      const pending: import("../shared/mcp-chat-types").AttachedImage[] = [];
      let loaded = 0;

      toProcess.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (!this.isMounted_) return;
          const dataUrl = reader.result as string;
          const base64 = dataUrl.split(",")[1];
          const mediaType = file.type || "image/png";

          pending.push({
            id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            file,
            base64,
            mediaType,
            previewUrl: dataUrl
          });

          loaded++;
          if (loaded === toProcess.length) {
            this.handleAttachImages(pending);
          }
        };
        reader.readAsDataURL(file);
      });
    }

    // Process document files
    if (docFiles.length > 0) {
      const remaining = MAX_ATTACHED_FILES - this.getActiveTab().attachedFiles.length;
      const toProcess = docFiles.slice(0, remaining);
      const pendingDocs: import("../shared/mcp-chat-types").AttachedFile[] = [];
      let loaded = 0;

      toProcess.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (!this.isMounted_) return;
          const dataUrl = reader.result as string;
          const base64 = dataUrl.split(",")[1];
          const ext = file.name.split(".").pop()?.toLowerCase() || "";

          pendingDocs.push({
            id: `file_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            file,
            base64,
            fileName: file.name,
            mediaType: file.type || "application/octet-stream",
            fileSize: file.size,
            fileExtension: ext
          });

          loaded++;
          if (loaded === toProcess.length) {
            this.handleAttachFiles(pendingDocs);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // ── Banners ──

  handleSaveFailed = () => {
    if (this.isMounted_) this.setState({ showSaveFailureBanner: true });
  };

  handleSaveRecovered = () => {
    if (this.isMounted_) this.setState({ showSaveFailureBanner: false });
  };

  handleDismissSaveFailureBanner = () => {
    this.setState({ showSaveFailureBanner: false });
  };

  handleDismissDisconnectedBanner = () => {
    this.setState({ showDisconnectedBanner: false });
  };

  handleRetryConnection = () => {
    // Refresh bridge connection state
    this.setupBridgeConnectionListener();
  };

  render() {
    const { isMcpChatPanelVisible, setIsMcpChatPanelVisible } = this.props;
    const {
      isWsConnected,
      panelSize,
      isResizing,
      anchor,
      isDragging,
      dragPosition,
      isSnapping,
      isFileDragOver,
      isInspectorActive,
      isCollapsedForInspector
    } = this.state;
    const isCompact = (panelSize?.width ?? 400) < PANEL_COMPACT_THRESHOLD;
    const activeTab = this.getActiveTab();
    const { messages, isLoading, inputValue } = activeTab;
    const hasMessages = messages.length > 0;
    const { visibleMessages, hiddenCount } = this.getVisibleMessages();
    const mostRecentUndoableId = this.getMostRecentUndoableMessageId();
    // The last non-error assistant message gets the persistent "Undo" pill —
    // a shortcut to rewind back to the most recent rewindable user message.
    // Only computed when a rewind target exists, so the pill never hangs on
    // orphan AI messages (e.g. after restore or when rewind is disabled).
    // Additionally, require the candidate to have at least one mutation in
    // its structuralDiff — completion summaries from credit-exhausted or
    // cancelled runs carry no diff, so anchoring Revert on them would
    // surface a button that reverts to a state indistinguishable from
    // the current one (nothing to undo).
    let latestAssistantIdForUndo: string | null = null;
    if (mostRecentUndoableId) {
      for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];
        if (m.role === "assistant" && !m.isError) {
          if ((m.structuralDiff?.totalChanges ?? 0) > 0) {
            latestAssistantIdForUndo = m.id;
          }
          break;
        }
      }
    }
    // The latest non-error assistant message keeps its OK/Not OK/Copy footer
    // pinned visible (others reveal on hover). Unconditional — unlike the
    // rewind-gated latestAssistantIdForUndo above.
    let lastAssistantId: string | null = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "assistant" && !m.isError) {
        lastAssistantId = m.id;
        break;
      }
    }
    const balancePanelProps = buildMcpChatBalancePanelProps(this);

    // When docked, the floating panel is hidden; content renders inside the panel via McpChatDocked
    const isDocked = anchor === "right-panel";
    const anchorZones = isDragging ? this.getAnchorZones() : null;
    const resizeHandleClassName = [
      "mcp-chat__resize-handle",
      this.isHorizontalAnchor() && "mcp-chat__resize-handle--vertical-only"
    ]
      .filter(Boolean)
      .join(" ");
    const resizeHandleTitle = this.isHorizontalAnchor() ? "Drag to change height" : "Drag to resize";

    const panelStyle: React.CSSProperties | undefined = (() => {
      const effectiveSize = this.getEffectivePanelSize();
      const base: React.CSSProperties = { width: effectiveSize.width, height: effectiveSize.height };

      // For horizontal anchors at rest, compute inline position from anchor zones
      if (!isDragging && !isSnapping && !dragPosition && this.isHorizontalAnchor()) {
        const zones = this.getAnchorZones();
        const zone = zones[anchor];
        return {
          ...base,
          position: "fixed" as const,
          left: zone.x,
          top: zone.y,
          right: "auto",
          bottom: "auto"
        };
      }

      if (isDragging && dragPosition) {
        return {
          ...base,
          position: "fixed" as const,
          left: dragPosition.x,
          top: dragPosition.y,
          right: "auto",
          bottom: "auto"
        };
      }
      if (isSnapping && this.snapTarget) {
        // Animate from the last drag position to the snap target
        const snapStyle: React.CSSProperties = {
          ...base,
          position: "fixed" as const,
          left: this.snapTarget.left,
          top: this.snapTarget.top,
          right: "auto",
          bottom: "auto"
        };
        // Include target size when orientation changes (vertical↔horizontal)
        if (this.snapTarget.width != null) snapStyle.width = this.snapTarget.width;
        if (this.snapTarget.height != null) snapStyle.height = this.snapTarget.height;
        return snapStyle;
      }
      if (dragPosition) {
        // Between isDragging=false and isSnapping=true (one rAF frame): hold the drag position
        return {
          ...base,
          position: "fixed" as const,
          left: dragPosition.x,
          top: dragPosition.y,
          right: "auto",
          bottom: "auto"
        };
      }
      return base || undefined;
    })();

    // During inspection the panel stays OPEN as a narrow strip (see
    // getEffectivePanelSize) instead of hiding — so the user still sees the chat
    // while picking. It goes translucent when the right panel is closed so the
    // canvas underneath is visible/clickable through it.
    const inspectorStrip = isCollapsedForInspector && !isDocked;
    const inspectorTransparent = inspectorStrip && !this.isRightPanelOpen();
    const panelClassName = [
      "mcp-chat",
      isMcpChatPanelVisible && !isDocked && "mcp-chat--open",
      inspectorStrip && "mcp-chat--inspector-strip",
      inspectorTransparent && "mcp-chat--inspector-transparent",
      isCompact && "mcp-chat--compact",
      isResizing && "mcp-chat--resizing",
      isDragging && "mcp-chat--dragging",
      isSnapping && "mcp-chat--snapping"
    ]
      .filter(Boolean)
      .join(" ");

    const wrapperClassName = ["mcp-chat-wrapper", `mcp-chat-wrapper--${anchor}`, isDocked && "mcp-chat-wrapper--docked"]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={wrapperClassName}>
        <McpChatAnchorZones zones={anchorZones} nearestAnchor={this.state.nearestAnchor} />

        {/* Chat Panel (hidden when docked) */}
        {!isDocked && (
          <div
            ref={this.panelRef}
            className={panelClassName}
            style={panelStyle}
            role="dialog"
            aria-label="AI Chat"
            data-testid="mcpChatPanel"
            onDragEnter={this.handlePanelDragEnter}
            onDragOver={this.handlePanelDragOver}
            onDragLeave={this.handlePanelDragLeave}
            onDrop={this.handlePanelDrop}
          >
            {isFileDragOver && (
              <div className="mcp-chat__panel-drop-overlay">
                <div className="mcp-chat__panel-drop-overlay-content">
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                  <span>Drop files to attach</span>
                  <span className="mcp-chat__panel-drop-overlay-hint">
                    Images and documents (PDF, DOCX, XLSX, CSV, TXT, JSON, XML)
                  </span>
                </div>
              </div>
            )}
            {/* Anchor-aware resize handle */}
            <div
              className={resizeHandleClassName}
              onMouseDown={this.handleResizeMouseDown}
              title={resizeHandleTitle}
              aria-hidden="true"
            />
            <McpChatHeader
              isWsConnected={isWsConnected}
              hasMessages={hasMessages}
              isLoading={isLoading}
              chatMode={activeTab.chatMode}
              planMode={activeTab.planMode}
              onClearChat={this.handleClearChat}
              onClose={this.handleClosePanel}
              onDragStart={this.handleDragStart}
              isDragging={isDragging}
              onSearchToggle={this.handleSearchToggle}
              isSearchOpen={this.state.search.isOpen}
              showModelSelector={this.isKuikaDeveloper() && this.state.serverProvider === AiChatProviderName.OpenRouter}
              isModelSelectorOpen={this.state.isModelSelectorOpen}
              onModelSelectorToggle={this.handleModelSelectorToggle}
              hasModelOverrides={
                !!(
                  this.state.modelOverrides.complex ||
                  this.state.modelOverrides.default ||
                  this.state.modelOverrides.cheap ||
                  this.state.modelOverrides.braid
                )
              }
              onHistoryOpen={this.state.rewindEnabled === true ? this.handleHistoryOpen : undefined}
              isHistoryOpen={!!this.state.historyModal}
              onFullScreenToggle={this.handleFullScreenToggle}
              isFullScreen={this.state.isFullScreen}
            />
            {this.state.isModelSelectorOpen && this.state.serverProvider === AiChatProviderName.OpenRouter && (
              <McpChatModelSelector
                isOpen={this.state.isModelSelectorOpen}
                onToggle={this.handleModelSelectorToggle}
                onOverridesChange={this.handleModelOverridesChange}
              />
            )}
            <McpChatTabBar
              ref={this.tabBarRef}
              tabs={this.state.tabs}
              activeTabId={this.state.activeTabId}
              onSwitchTab={this.handleSwitchTab}
              onCloseTab={this.handleCloseTab}
              onAddTab={this.handleAddTab}
              onRenameTab={this.handleRenameTab}
            />

            {this.state.search.isOpen && (
              <McpChatSearchBar
                searchState={this.state.search}
                onQueryChange={this.handleSearchQueryChange}
                onNextMatch={this.handleSearchNextMatch}
                onPrevMatch={this.handleSearchPrevMatch}
                onToggleCrossTab={this.handleSearchToggleCrossTab}
                onClose={this.handleSearchToggle}
              />
            )}

            {this.state.search.isOpen && this.state.search.crossTab && this.state.search.matches.length > 0 && (
              <McpChatSearchResults
                matches={this.state.search.matches}
                tabs={this.state.tabs}
                currentMatchIndex={this.state.search.currentMatchIndex}
                onSelectMatch={this.handleSearchSelectMatch}
              />
            )}

            <McpChatDisconnectedBanner
              isWsConnected={isWsConnected}
              showDisconnectedBanner={this.state.showDisconnectedBanner}
              onRetry={this.handleRetryConnection}
              onDismiss={this.handleDismissDisconnectedBanner}
            />

            {/* Messages */}
            <div
              className={
                !hasMessages && !isLoading ? "mcp-chat__messages mcp-chat__messages--empty" : "mcp-chat__messages"
              }
              ref={this.messagesContainerRef}
              role="log"
              aria-live="polite"
              onScroll={this.handleMessagesScroll}
              onWheel={this.handleMessagesWheel}
            >
              <div className="mcp-chat__messages-content" ref={this.messagesContentRef}>
                {!hasMessages && !isLoading && (
                  <McpChatEmptyState
                    capabilities={this.state.capabilities}
                    expandedCategory={activeTab.expandedCategory}
                    onCategoryClick={this.handleCategoryClick}
                    onSuggestionClick={this.handleSuggestionClick}
                    isPlatformMode={isPlatformMode()}
                    chatMode={activeTab.chatMode}
                    planMode={activeTab.planMode}
                  />
                )}
                {hiddenCount > 0 && this.renderLoadMoreButton(hiddenCount)}
                {visibleMessages.map((msg, idx) => (
                  <McpChatErrorBoundary key={msg.id}>
                    <div data-message-id={msg.id}>
                      <McpChatMessageComponent
                        message={msg}
                        staggerIndex={idx}
                        isActiveTabLoading={isLoading}
                        isLastAssistant={lastAssistantId === msg.id}
                        onRate={this.handleRateResponse}
                        isMostRecentUndoable={mostRecentUndoableId === msg.id}
                        rewindEnabled={this.state.rewindEnabled === true}
                        undoButtonTargetUserMsgId={latestAssistantIdForUndo === msg.id ? mostRecentUndoableId : null}
                        questionnaireProgress={activeTab.questionnaires[msg.id]}
                        tabPlanMode={activeTab.planMode}
                        planJson={activeTab.planJson}
                        searchQuery={this.state.search.isOpen ? this.state.search.query : undefined}
                        isActiveSearchMatch={
                          this.state.search.isOpen &&
                          this.state.search.currentMatchIndex >= 0 &&
                          this.state.search.matches[this.state.search.currentMatchIndex]?.messageId === msg.id
                        }
                        onRetry={this.handleRetry}
                        onUndo={this.handleRewindRequest}
                        onPlanApprove={this.handlePlanApprove}
                        onPlanReject={this.handlePlanReject}
                        onSpecApprove={this.handleSpecApprove}
                        onSpecReject={this.handleSpecReject}
                        onChangeApprove={this.handleChangeApprove}
                        onChangeReject={this.handleChangeReject}
                        onInterruptApprove={this.handleInterruptApprove}
                        onInterruptApproveAll={this.handleInterruptApproveAll}
                        onInterruptReject={this.handleInterruptReject}
                        onQuestionInterruptSubmit={this.handleQuestionInterruptSubmit}
                        onQuestionDelegateOne={this.handleActiveTabQuestionDelegateOne}
                        onQuestionDelegateRest={this.handleQuestionDelegateRest}
                        onOptionSelect={this.handleOptionSelect}
                        onQuestionOptionSelect={this.handleActiveTabQuestionOptionSelect}
                        onQuestionMultiToggle={this.handleActiveTabQuestionMultiToggle}
                        onQuestionCustomValueChange={this.handleActiveTabQuestionCustomValueChange}
                        onQuestionGoTo={this.handleActiveTabQuestionGoTo}
                        onQuestionBack={this.handleActiveTabQuestionBack}
                        onQuestionNext={this.handleActiveTabQuestionNext}
                        onQuestionSubmit={this.handleActiveTabQuestionSubmit}
                      />
                    </div>
                  </McpChatErrorBoundary>
                ))}
                {isLoading && (
                  <McpChatThinkingIndicator
                    toolActivities={activeTab.toolActivities}
                    startTime={this.requestStartTimes.get(activeTab.id)}
                    phaseTitle={activeTab.phaseTitle}
                    toolTitle={activeTab.toolTitle}
                  />
                )}
                <div ref={this.messagesEndRef} />
              </div>
            </div>

            {this.state.showScrollToBottom && (
              <button
                className="mcp-chat__scroll-to-bottom"
                onClick={this.forceScrollToBottom}
                aria-label="Scroll to bottom"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 3v10m0 0l-4-4m4 4l4-4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}

            {activeTab.queuedMessages.length > 0 && (
              <div className="mcp-chat__queued-banner">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span className="mcp-chat__queued-text">
                  {activeTab.queuedMessages.length === 1
                    ? "1 message queued"
                    : `${activeTab.queuedMessages.length} messages queued`}
                </span>
                <button
                  className="mcp-chat__queued-cancel"
                  onClick={this.handleCancelAllQueuedMessages}
                  aria-label="Cancel all queued messages"
                >
                  Cancel all
                </button>
              </div>
            )}

            <McpChatSuggestions
              messages={activeTab.messages}
              isLoading={isLoading}
              inputValue={inputValue}
              onSuggestionClick={this.handleSuggestionClick}
            />

            <div className="mcp-chat__input-wrapper">
              {balancePanelProps ? <McpChatBalancePanel {...balancePanelProps} /> : null}
              <McpChatInputArea ref={this.inputAreaRef} {...buildMcpChatInputAreaProps(this, activeTab)} />
            </div>

            <p className="mcp-chat__disclaimer">
              {KMainFunctions.isMainAI() ? "Main AI" : "Kuika AI"} can make mistakes. Review its changes before
              publishing.
            </p>
          </div>
        )}
        {this.state.rewindDialog && (
          <McpChatRewindDialog
            summary={this.state.rewindDialog.summary}
            isLoadingSummary={this.state.rewindDialog.isLoadingSummary}
            isInFlight={this.state.rewindDialog.isInFlight}
            errorText={this.state.rewindDialog.errorText}
            onConfirm={this.handleRewindConfirm}
            onCancel={this.handleRewindCancel}
          />
        )}
        {this.state.isBuyCreditsOpen && (
          <McpChatBuyCreditsModal onClose={this.handleBuyCreditsClose} onEmbedFailed={this.redirectToTopUp} />
        )}
        {this.state.historyModal && (
          <McpChatHistoryModal
            checkpoints={this.state.historyModal.checkpoints}
            activeTabId={this.state.activeTabId}
            isLoading={this.state.historyModal.isLoading}
            errorText={this.state.historyModal.errorText}
            onSelectCheckpoint={this.handleHistorySelectCheckpoint}
            onClose={this.handleHistoryClose}
          />
        )}
      </div>
    );
  }
}

export default McpChatPanel;
